import React, { useState, useEffect, useRef } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend } from 'date-fns';
import html2canvas from 'html2canvas';
import { ptBR } from 'date-fns/locale';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Calendar,
  Users,
  UserPlus,
  Plus,
  Trash2,
  Save,
  Loader2,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  AlertTriangle,
  Check,
  X,
  Search,
  Clock,
  MapPin,
  Utensils,
  FileText,
  Briefcase,
  AlertCircle,
  ClipboardList,
  Edit,
  Building,
  Truck,
  Home,
  Eye,
  EyeOff,
  Bus,
  Package,
  Printer,
  FileDown,
  Maximize2,
  Minimize2,
  Columns,
  Settings2,
  CheckSquare,
  XSquare,
  CreditCard,
  Filter,
  CheckCircle2,
  Circle,
  Receipt,
  FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';
import { humanApi, dailyControlApi, pendenciasApi, reportsApi, osApi, fornecedorRefeicaoApi, refeicoesDiaApi, tiposAtividadeApi, veiculosApi, vencimentosRefeicaoApi, vencimentosTerceirosApi, vtApi, feriadosApi } from '../lib/api';
import api from '../lib/api';

const DailyControlPage = () => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [showFolgaColetivaModal, setShowFolgaColetivaModal] = useState(false);
  const [folgaColetivaTipo, setFolgaColetivaTipo] = useState('folga');
  const [lancandoFolgaColetiva, setLancandoFolgaColetiva] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Ref para captura de imagem do resumo
  const resumoInicialRef = useRef(null);
  const resumoFinalRef = useRef(null);
  const [downloadingImage, setDownloadingImage] = useState(false);
  
  // Exportação PDF Mensal
  const [exportingMonthPdf, setExportingMonthPdf] = useState(false);
  
  // Verificar se usuário é admin
  const isAdmin = ['admin', 'super_admin', 'diretoria'].includes(user?.role);
  
  // Feriados do ano
  const [feriados, setFeriados] = useState([]);
  
  // Dados de pessoas (da API /humans)
  const [colaboradores, setColaboradores] = useState([]);
  const [terceiros, setTerceiros] = useState([]);
  
  // Tipos de atividade (configurável por empresa)
  const [tiposAtividade, setTiposAtividade] = useState([]);
  
  // Veículos da empresa (configurável)
  const [veiculosEmpresa, setVeiculosEmpresa] = useState([]);
  
  // OSs do dia (para vincular nos apontamentos)
  const [ossDoDia, setOssDoDia] = useState([]);
  const [todasOss, setTodasOss] = useState([]); // Todas as OSs para o dropdown
  
  // Escala do dia - Apontamentos
  const [dayRecords, setDayRecords] = useState([]);
  const [wastes, setWastes] = useState([]);
  const [meals, setMeals] = useState({ 
    planned: 0, served: 0, wasted: 0, notes: '', unit_value: 0, total_value: 0, wasted_value: 0,
    paid_meals_qty: 0, paid_meals_value: 0, paid_meals_method: 'cartao', paid_meals_total: 0
  });
  
  // Refeições por Fornecedor (novo sistema)
  const [fornecedoresRefeicao, setFornecedoresRefeicao] = useState([]);
  const [refeicoesDia, setRefeicoesDia] = useState({
    fornecedores: [], // [{fornecedor_id, fornecedor_nome, funcionarios: [ids], valor_unitario, observacoes}]
    reembolsos: [],   // [{funcionario_id, funcionario_nome, valor, forma_pagamento, observacoes}]
    observacoes_gerais: '',
    totais: { total_refeicoes: 0, total_valor_fornecedores: 0, total_reembolsos: 0, total_geral: 0 }
  });
  const [savingRefeicoes, setSavingRefeicoes] = useState(false);
  const [showAddReembolso, setShowAddReembolso] = useState(false);
  
  // Reembolsos Exceções (transporte, materiais, etc.)
  const [reembolsosExcecoes, setReembolsosExcecoes] = useState([]);
  const [showAddReembolsoExcecao, setShowAddReembolsoExcecao] = useState(false);
  const [savingReembolsosExcecoes, setSavingReembolsosExcecoes] = useState(false);
  
  // Pendências do dia
  const [pendenciasDia, setPendenciasDia] = useState({ ponto: [], equipamento: [], total_ponto: 0, total_equipamento: 0 });
  
  // Notas de Veículos/Contele
  const [vehicleNotes, setVehicleNotes] = useState('');
  const [savingVehicleNotes, setSavingVehicleNotes] = useState(false);
  const [vehicleExpenses, setVehicleExpenses] = useState({ fuel: 0, maintenance: 0, other: 0, description: '' });
  const [vehicleData, setVehicleData] = useState({});  // { veiculo: { km, rota, obs } }
  
  // Vale Transporte
  const [valeTransporte, setValeTransporte] = useState({ total_funcionarios: 0, total_vt: 0, valor_unitario: 4.40, rotas: '', observacoes: '' });
  const [savingVT, setSavingVT] = useState(false);
  
  // Controle de Saldo VT
  const [saldoVT, setSaldoVT] = useState({ funcionarios: [], resumo: {}, dias_uteis: 0, dias_restantes: 0, feriados: [] });
  const [loadingSaldoVT, setLoadingSaldoVT] = useState(false);
  const [vtFuncionarioDetalhe, setVtFuncionarioDetalhe] = useState(null);
  const [showVtDetalhe, setShowVtDetalhe] = useState(false);
  const [vtMesSelecionado, setVtMesSelecionado] = useState({ ano: new Date().getFullYear(), mes: new Date().getMonth() + 1 });
  
  // Vencimentos de Refeições
  const [vencimentos, setVencimentos] = useState({ lancamentos: [], resumo_fornecedores: [], total_geral: 0, total_pendente: 0, total_pago: 0 });
  const [loadingVencimentos, setLoadingVencimentos] = useState(false);
  const [vencimentosFiltro, setVencimentosFiltro] = useState({ dataInicio: '', dataFim: '', fornecedorId: '', status: 'todos' });
  const [vencimentosSelecionados, setVencimentosSelecionados] = useState([]);
  const [processandoVencimentos, setProcessandoVencimentos] = useState(false);
  
  // Vencimentos de Terceiros
  const [vencimentosTerceiros, setVencimentosTerceiros] = useState({ lancamentos: [], resumo_terceiros: [], total_geral: 0, total_pendente: 0, total_pago: 0 });
  const [loadingVencTerceiros, setLoadingVencTerceiros] = useState(false);
  const [vencTerceirosSelecionados, setVencTerceirosSelecionados] = useState([]);
  const [vencimentosAbaAtiva, setVencimentosAbaAtiva] = useState('terceiros'); // 'terceiros' ou 'refeicoes'
  
  // Dialogs
  const [showAddWorker, setShowAddWorker] = useState(false);
  const [showAddWaste, setShowAddWaste] = useState(false);
  const [showEditRecord, setShowEditRecord] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterColaborador, setFilterColaborador] = useState(''); // Filtro por funcionário que persiste entre dias
  const [showExtraEntries, setShowExtraEntries] = useState(false);
  const [editingExtraRecord, setEditingExtraRecord] = useState(null);
  const [showTermoDialog, setShowTermoDialog] = useState(false);
  const [termoRecord, setTermoRecord] = useState(null);
  
  // Controle de visualização da tabela
  const [viewMode, setViewMode] = useState('normal'); // 'normal', 'compact', 'expanded'
  const [visibleColumns, setVisibleColumns] = useState({
    nome: true,
    entrada: true,
    saida: true,
    pOficial: true,
    batidas: true,
    local: true,
    transporte: true,
    servico: true,
    os: true,
    obs: true,
    acoes: true
  });
  
  // Form states - Adicionar Terceiro
  const [selectedHuman, setSelectedHuman] = useState(null);
  const [workerService, setWorkerService] = useState('');
  const [workerValue, setWorkerValue] = useState('');
  const [workerValueType, setWorkerValueType] = useState('definido');
  const [workerOS, setWorkerOS] = useState('');
  const [workerServiceType, setWorkerServiceType] = useState('evento');
  const [workerVencimento, setWorkerVencimento] = useState('');
  
  // Form states - Desperdício
  const [wasteDescription, setWasteDescription] = useState('');
  const [wasteValue, setWasteValue] = useState('');
  const [wasteType, setWasteType] = useState('outros');
  const [wasteResponsible, setWasteResponsible] = useState('');

  // Check user permissions
  const isAdminOrDiretoria = ['admin', 'super_admin', 'diretoria'].includes(user?.role);

  useEffect(() => {
    loadHumans();
    loadFornecedoresRefeicao();
    loadTiposAtividade();
    loadVeiculosEmpresa();
    loadFeriados();
  }, []);

  useEffect(() => {
    loadDayData();
    // Recarregar feriados se mudar de ano
    const anoAtual = new Date(selectedDate).getFullYear();
    loadFeriados();
  }, [selectedDate]);

  const loadHumans = async () => {
    try {
      const res = await humanApi.list({ status: 'ativo', include_strategic: true });
      const all = res.data || [];
      
      setColaboradores(all.filter(h => h.type === 'colaborador'));
      setTerceiros(all.filter(h => h.type === 'terceiro'));
    } catch (error) {
      console.error('Error loading humans:', error);
      toast.error('Erro ao carregar pessoas');
    }
  };

  const loadTiposAtividade = async () => {
    try {
      const res = await tiposAtividadeApi.list(true); // apenas ativos
      setTiposAtividade(res.data || []);
    } catch (error) {
      console.error('Error loading tipos atividade:', error);
    }
  };

  const loadFornecedoresRefeicao = async () => {
    try {
      const res = await fornecedorRefeicaoApi.list(true); // apenas ativos
      setFornecedoresRefeicao(res.data || []);
    } catch (error) {
      console.error('Error loading fornecedores refeicao:', error);
    }
  };

  const loadVeiculosEmpresa = async () => {
    try {
      const res = await veiculosApi.list(true); // apenas ativos
      setVeiculosEmpresa(res.data || []);
    } catch (error) {
      console.error('Error loading veiculos empresa:', error);
    }
  };

  const loadFeriados = async () => {
    try {
      const ano = new Date(selectedDate).getFullYear();
      const res = await feriadosApi.list(ano);
      setFeriados(res.data || []);
    } catch (error) {
      console.error('Error loading feriados:', error);
      setFeriados([]);
    }
  };

  // Lançar folga para todos os colaboradores CLT na data selecionada
  const handleFolgaColetiva = async () => {
    setLancandoFolgaColetiva(true);
    try {
      const res = await api.post('/day-records/folga-coletiva', {
        date: selectedDate,
        tipo: folgaColetivaTipo
      });
      const { total, pulados, bloqueados } = res.data;
      toast.success(`Folga lançada para ${total} colaborador(es)${pulados?.length ? ` — ${pulados.length} pulado(s) por já ter apontamento: ${pulados.join(', ')}` : ''}`);
      if (bloqueados?.length) {
        toast.error(`FOLGA/BANCO bloqueada por saldo negativo: ${bloqueados.join(', ')}. Lance FALTA ou 'folga abonada'.`, { duration: 10000 });
      }
      setShowFolgaColetivaModal(false);
      loadDayData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao lançar folga coletiva');
    } finally {
      setLancandoFolgaColetiva(false);
    }
  };

  // ===== Fases por pessoa na equipe da OS (Montagem/Evento/Encerramento) =====
  const fasesDaDataNaOS = (os, dateStr) => {
    const d = (v) => (v ? String(v).split('T')[0] : null);
    const fases = [];
    if (d(os.data_montagem) === dateStr) fases.push('montagem');
    const ini = d(os.data_evento) || d(os.data_inicio) || d(os.data);
    const fim = d(os.data_evento_fim) || d(os.data_fim) || d(os.data_final) || ini;
    if (ini && dateStr >= ini && dateStr <= fim) fases.push('evento');
    if (d(os.data_desmontagem) === dateStr) fases.push('encerramento');
    return fases;
  };
  const membroEsperadoNoDia = (os, membro, dateStr) => {
    const fasesData = fasesDaDataNaOS(os, dateStr);
    if (fasesData.length === 0) return true;
    const fasesMembro = (membro.fases && membro.fases.length > 0) ? membro.fases : ['montagem', 'evento', 'encerramento'];
    return fasesData.some(f => fasesMembro.includes(f));
  };

  const loadDayData = async (opts = {}) => {
    if (!opts.silent) setLoading(true);
    try {
      const [recordsRes, wastesRes, mealsRes, pendenciasRes, vehicleNotesRes, vtRes, osRes, refeicoesFornRes, reembolsosExcecoesRes] = await Promise.all([
        dailyControlApi.listRecords(selectedDate).catch(() => ({ data: [] })),
        dailyControlApi.listWastes(selectedDate).catch(() => ({ data: [] })),
        dailyControlApi.getMeals(selectedDate).catch(() => ({ data: { 
          planned: 0, served: 0, wasted: 0, notes: '', unit_value: 0, total_value: 0, wasted_value: 0,
          paid_meals_qty: 0, paid_meals_value: 0, paid_meals_method: 'cartao', paid_meals_total: 0
        } })),
        pendenciasApi.getDia(selectedDate).catch(() => ({ data: { ponto: [], equipamento: [], total_ponto: 0, total_equipamento: 0 } })),
        dailyControlApi.getVehicleNotes(selectedDate).catch(() => ({ data: { notes: '', expenses: {}, vehicles: {} } })),
        dailyControlApi.getTransportVoucher(selectedDate).catch(() => ({ data: { total_funcionarios: 0, total_vt: 0, valor_unitario: 4.40, rotas: '', observacoes: '' } })),
        osApi.list({ semana: selectedDate }).catch(() => ({ data: [] })),
        refeicoesDiaApi.get(selectedDate).catch(() => ({ data: { fornecedores: [], reembolsos: [], observacoes_gerais: '', totais: {} } })),
        dailyControlApi.getReembolsosExcecoes(selectedDate).catch(() => ({ data: { reembolsos: [], total: 0 } })),
      ]);
      
      setDayRecords(recordsRes.data || []);
      setWastes(wastesRes.data || []);
      setMeals(mealsRes.data || { 
        planned: 0, served: 0, wasted: 0, notes: '', unit_value: 0, total_value: 0, wasted_value: 0,
        paid_meals_qty: 0, paid_meals_value: 0, paid_meals_method: 'cartao', paid_meals_total: 0
      });
      setPendenciasDia(pendenciasRes.data || { ponto: [], equipamento: [], total_ponto: 0, total_equipamento: 0 });
      setVehicleNotes(vehicleNotesRes.data?.notes || '');
      setVehicleExpenses(vehicleNotesRes.data?.expenses || { fuel: 0, maintenance: 0, other: 0, description: '' });
      setVehicleData(vehicleNotesRes.data?.vehicles || {});
      setValeTransporte(vtRes.data || { total_funcionarios: 0, total_vt: 0, valor_unitario: 4.40, rotas: '', observacoes: '' });
      
      // Dados de refeições por fornecedor
      setRefeicoesDia(refeicoesFornRes.data || { fornecedores: [], reembolsos: [], observacoes_gerais: '', totais: {} });
      
      // Dados de reembolsos de exceções
      setReembolsosExcecoes(reembolsosExcecoesRes.data?.reembolsos || []);
      
      // Guardar todas as OSs para o dropdown
      const allOss = osRes.data || [];
      setTodasOss(allOss);
      
      // Filtrar OSs do dia (considerando período completo) - para destaque
      // Considera tanto data_evento/data_evento_fim quanto data_inicio/data_fim (compatibilidade)
      const osFiltradas = allOss.filter(os => {
        // Pegar data início (tentar várias fontes)
        const dataInicioRaw = os.data_evento || os.data_inicio || os.data;
        if (!dataInicioRaw) return false;
        
        const dataInicio = dataInicioRaw.split('T')[0];
        
        // Pegar data fim (tentar várias fontes)
        const dataFimRaw = os.data_evento_fim || os.data_fim || os.data_final;
        const dataFim = dataFimRaw ? dataFimRaw.split('T')[0] : dataInicio;
        
        // Verificar se o dia selecionado está dentro do período da OS
        return selectedDate >= dataInicio && selectedDate <= dataFim;
      });
      setOssDoDia(osFiltradas);
    } catch (error) {
      console.error('Error loading day data:', error);
      setDayRecords([]);
      setWastes([]);
      setPendenciasDia({ ponto: [], equipamento: [], total_ponto: 0, total_equipamento: 0 });
      setVehicleNotes('');
      setVehicleExpenses({ fuel: 0, maintenance: 0, other: 0, description: '' });
      setVehicleData({});
      setValeTransporte({ total_funcionarios: 0, total_vt: 0, valor_unitario: 4.40, rotas: '', observacoes: '' });
      setOssDoDia([]);
    } finally {
      setLoading(false);
    }
  };

  // Inicializar registros de colaboradores para o dia
  const initializeDayRecords = async () => {
    setSaving(true);
    try {
      await dailyControlApi.initializeDay(selectedDate);
      toast.success('Dia inicializado com colaboradores!');
      loadDayData();
    } catch (error) {
      toast.error('Erro ao inicializar dia');
    } finally {
      setSaving(false);
    }
  };

  // Atualizar registro de apontamento
  // Drafts locais para campos de texto (salvar no blur, evita reload a cada tecla)
  const [fieldDrafts, setFieldDrafts] = useState({});
  const draftValue = (id, field, serverVal) => {
    const k = `${id}:${field}`;
    return fieldDrafts[k] !== undefined ? fieldDrafts[k] : (serverVal || '');
  };
  const setDraft = (id, field, val) => setFieldDrafts(p => ({ ...p, [`${id}:${field}`]: val }));
  const commitDraft = async (id, field, serverVal) => {
    const k = `${id}:${field}`;
    const val = fieldDrafts[k];
    if (val === undefined || val === (serverVal || '')) return;
    await handleUpdateRecord(id, { [field]: val });
    setFieldDrafts(p => { const n = { ...p }; delete n[k]; return n; });
  };

  const handleUpdateRecord = async (recordId, updates) => {
    try {
      await dailyControlApi.updateRecord(selectedDate, recordId, updates);
      loadDayData({ silent: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar registro', { duration: 8000 });
    }
  };

  // Salvar edição completa do registro
  const handleSaveEditRecord = async () => {
    if (!editingRecord) return;
    setSaving(true);
    try {
      await dailyControlApi.updateRecord(selectedDate, editingRecord.id, editingRecord);
      toast.success('Registro atualizado!');
      setShowEditRecord(false);
      setEditingRecord(null);
      loadDayData();
    } catch (error) {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVehicleNotes = async () => {
    setSavingVehicleNotes(true);
    try {
      await dailyControlApi.saveVehicleNotes(selectedDate, {
        notes: vehicleNotes,
        expenses: vehicleExpenses,
        vehicles: vehicleData
      });
      toast.success('Dados de veículos salvos!');
    } catch (error) {
      toast.error('Erro ao salvar dados');
    } finally {
      setSavingVehicleNotes(false);
    }
  };

  const handleSaveTransportVoucher = async () => {
    setSavingVT(true);
    try {
      await dailyControlApi.saveTransportVoucher(selectedDate, valeTransporte);
      toast.success('Vale transporte salvo!');
    } catch (error) {
      toast.error('Erro ao salvar vale transporte');
    } finally {
      setSavingVT(false);
    }
  };

  const handleAddWorker = async () => {
    if (!selectedHuman) {
      toast.error('Selecione uma pessoa');
      return;
    }

    setSaving(true);
    try {
      const data = {
        human_id: selectedHuman.id,
        human_name: selectedHuman.name,
        type: selectedHuman.type,
        is_strategic: selectedHuman.is_strategic || false,
        service_description: workerService,
        service_type: workerServiceType,
        os_number: workerOS,
        value: workerValueType === 'definido' ? parseFloat(workerValue) || 0 : null,
        value_type: workerValueType,
        data_vencimento: workerVencimento || null,
        location: 'externo',
        entry_time: '',
        exit_time: '',
        meal_included: false,
        observations: '',
      };
      
      await dailyControlApi.addRecord(selectedDate, data);
      toast.success('Terceiro adicionado à escala');
      
      // Reset form
      setSelectedHuman(null);
      setWorkerService('');
      setWorkerValue('');
      setWorkerValueType('definido');
      setWorkerOS('');
      setWorkerServiceType('evento');
      setWorkerVencimento('');
      setShowAddWorker(false);
      setSearchTerm('');
      
      loadDayData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao adicionar';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveRecord = async (recordId) => {
    try {
      await dailyControlApi.removeRecord(selectedDate, recordId);
      toast.success('Removido da escala');
      loadDayData();
    } catch (error) {
      toast.error('Erro ao remover');
    }
  };

  const handleAddWaste = async () => {
    if (!wasteDescription.trim()) {
      toast.error('Digite a descrição do desperdício');
      return;
    }

    setSaving(true);
    try {
      const data = {
        description: wasteDescription,
        value: parseFloat(wasteValue) || 0,
        type: wasteType,
        responsible: wasteResponsible,
      };
      
      await dailyControlApi.addWaste(selectedDate, data);
      toast.success('Desperdício registrado');
      
      setWasteDescription('');
      setWasteValue('');
      setWasteType('outros');
      setWasteResponsible('');
      setShowAddWaste(false);
      
      loadDayData();
    } catch (error) {
      toast.error('Erro ao registrar desperdício');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveWaste = async (wasteId) => {
    try {
      await dailyControlApi.removeWaste(selectedDate, wasteId);
      toast.success('Desperdício removido');
      loadDayData();
    } catch (error) {
      toast.error('Erro ao remover');
    }
  };

  const handleSaveMeals = async () => {
    setSaving(true);
    try {
      await dailyControlApi.saveMeals(selectedDate, meals);
      toast.success('Refeições salvas!');
    } catch (error) {
      toast.error('Erro ao salvar refeições');
    } finally {
      setSaving(false);
    }
  };

  // Salvar Refeições por Fornecedor (novo sistema)
  const handleSaveRefeicoesFornecedor = async () => {
    setSavingRefeicoes(true);
    try {
      const res = await refeicoesDiaApi.save(selectedDate, {
        fornecedores: refeicoesDia.fornecedores,
        reembolsos: refeicoesDia.reembolsos,
        observacoes_gerais: refeicoesDia.observacoes_gerais
      });
      setRefeicoesDia(prev => ({ ...prev, totais: res.data?.totais || prev.totais }));
      toast.success('Refeições por fornecedor salvas!');
    } catch (error) {
      console.error('Error saving refeicoes fornecedor:', error);
      toast.error('Erro ao salvar refeições por fornecedor');
    } finally {
      setSavingRefeicoes(false);
    }
  };

  // Adicionar fornecedor ao dia
  const addFornecedorAoDia = (fornecedor) => {
    const jaExiste = refeicoesDia.fornecedores.some(f => f.fornecedor_id === fornecedor.id);
    if (jaExiste) {
      toast.warning('Este fornecedor já foi adicionado ao dia');
      return;
    }
    
    setRefeicoesDia(prev => ({
      ...prev,
      fornecedores: [...prev.fornecedores, {
        fornecedor_id: fornecedor.id,
        fornecedor_nome: fornecedor.nome,
        funcionarios: [],
        valor_unitario: fornecedor.valor_unitario || 0,
        observacoes: ''
      }]
    }));
  };

  // Remover fornecedor do dia
  const removeFornecedorDoDia = (fornecedorId) => {
    setRefeicoesDia(prev => ({
      ...prev,
      fornecedores: prev.fornecedores.filter(f => f.fornecedor_id !== fornecedorId)
    }));
  };

  // Atualizar valor unitário do fornecedor
  const updateFornecedorValor = (fornecedorId, valor) => {
    setRefeicoesDia(prev => ({
      ...prev,
      fornecedores: prev.fornecedores.map(f => 
        f.fornecedor_id === fornecedorId 
          ? { ...f, valor_unitario: parseFloat(valor) || 0 }
          : f
      )
    }));
  };

  // Toggle funcionário em um fornecedor
  const toggleFuncionarioFornecedor = (fornecedorId, funcionarioId, funcionarioNome) => {
    setRefeicoesDia(prev => ({
      ...prev,
      fornecedores: prev.fornecedores.map(f => {
        if (f.fornecedor_id !== fornecedorId) return f;
        
        const jaEstaNaLista = f.funcionarios.includes(funcionarioId);
        return {
          ...f,
          funcionarios: jaEstaNaLista 
            ? f.funcionarios.filter(id => id !== funcionarioId)
            : [...f.funcionarios, funcionarioId]
        };
      })
    }));
  };

  // Adicionar reembolso
  const addReembolso = (funcionarioId, funcionarioNome, valor, formaPagamento, observacoes) => {
    setRefeicoesDia(prev => ({
      ...prev,
      reembolsos: [...prev.reembolsos, {
        funcionario_id: funcionarioId,
        funcionario_nome: funcionarioNome,
        valor: parseFloat(valor) || 0,
        forma_pagamento: formaPagamento || 'pix',
        observacoes: observacoes || ''
      }]
    }));
    setShowAddReembolso(false);
  };

  // Remover reembolso
  const removeReembolso = (index) => {
    setRefeicoesDia(prev => ({
      ...prev,
      reembolsos: prev.reembolsos.filter((_, i) => i !== index)
    }));
  };

  // ==================== REEMBOLSOS EXCEÇÕES ====================
  
  // Adicionar reembolso exceção
  const addReembolsoExcecao = async (funcionarioId, funcionarioNome, valor, descricao, categoria, dataVencimento, formaPagamento, observacoes) => {
    const novoReembolso = {
      funcionario_id: funcionarioId,
      funcionario_nome: funcionarioNome,
      valor: parseFloat(valor) || 0,
      descricao: descricao || '',
      categoria: categoria || 'excecao',
      data_vencimento: dataVencimento || null,
      forma_pagamento: formaPagamento || 'pix',
      observacoes: observacoes || ''
    };
    
    const novosReembolsos = [...reembolsosExcecoes, novoReembolso];
    setReembolsosExcecoes(novosReembolsos);
    setShowAddReembolsoExcecao(false);
    
    // Salvar automaticamente
    try {
      setSavingReembolsosExcecoes(true);
      await dailyControlApi.saveReembolsosExcecoes(selectedDate, { reembolsos: novosReembolsos });
      toast.success('Reembolso adicionado');
    } catch (error) {
      toast.error('Erro ao salvar reembolso');
    } finally {
      setSavingReembolsosExcecoes(false);
    }
  };
  
  // Remover reembolso exceção
  const removeReembolsoExcecao = async (index) => {
    const novosReembolsos = reembolsosExcecoes.filter((_, i) => i !== index);
    setReembolsosExcecoes(novosReembolsos);
    
    try {
      setSavingReembolsosExcecoes(true);
      await dailyControlApi.saveReembolsosExcecoes(selectedDate, { reembolsos: novosReembolsos });
      toast.success('Reembolso removido');
    } catch (error) {
      toast.error('Erro ao remover reembolso');
    } finally {
      setSavingReembolsosExcecoes(false);
    }
  };
  
  // Total de reembolsos exceções
  const totalReembolsosExcecoes = reembolsosExcecoes.reduce((sum, r) => sum + (r.valor || 0), 0);

  // Calcular totais de refeições
  const calcularTotaisRefeicoes = () => {
    const totalRefeicoes = refeicoesDia.fornecedores.reduce((sum, f) => sum + f.funcionarios.length, 0);
    const totalValorRefeicoes = refeicoesDia.fornecedores.reduce((sum, f) => sum + (f.funcionarios.length * f.valor_unitario), 0);
    const totalTaxasAdicionais = refeicoesDia.fornecedores.reduce((sum, f) => sum + (f.taxa_adicional || 0), 0);
    const totalValorFornecedores = totalValorRefeicoes + totalTaxasAdicionais;
    const totalReembolsos = refeicoesDia.reembolsos.reduce((sum, r) => sum + r.valor, 0);
    return {
      total_refeicoes: totalRefeicoes,
      total_valor_refeicoes: totalValorRefeicoes,
      total_taxas_adicionais: totalTaxasAdicionais,
      total_valor_fornecedores: totalValorFornecedores,
      total_reembolsos: totalReembolsos,
      total_geral: totalValorFornecedores + totalReembolsos
    };
  };

  const changeDate = (days) => {
    // Parse date with explicit time to avoid timezone issues
    const [year, month, day] = selectedDate.split('-').map(Number);
    const date = new Date(year, month - 1, day); // Month is 0-indexed
    date.setDate(date.getDate() + days);
    setSelectedDate(format(date, 'yyyy-MM-dd'));
  };

  // ==================== VENCIMENTOS DE REFEIÇÕES ====================
  
  // Carregar vencimentos
  const loadVencimentos = async () => {
    setLoadingVencimentos(true);
    try {
      const params = {};
      if (vencimentosFiltro.dataInicio) params.data_inicio = vencimentosFiltro.dataInicio;
      if (vencimentosFiltro.dataFim) params.data_fim = vencimentosFiltro.dataFim;
      if (vencimentosFiltro.fornecedorId) params.fornecedor_id = vencimentosFiltro.fornecedorId;
      if (vencimentosFiltro.status && vencimentosFiltro.status !== 'todos') params.status = vencimentosFiltro.status;
      
      const res = await vencimentosRefeicaoApi.listar(params);
      setVencimentos(res.data);
    } catch (error) {
      console.error('Erro ao carregar vencimentos:', error);
      toast.error('Erro ao carregar vencimentos');
    } finally {
      setLoadingVencimentos(false);
    }
  };

  // Toggle seleção de vencimento
  const toggleVencimentoSelecionado = (id) => {
    setVencimentosSelecionados(prev => 
      prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
    );
  };

  // Selecionar todos os pendentes de um fornecedor
  const selecionarTodosFornecedor = (fornecedorId) => {
    const idsPendentes = vencimentos.lancamentos
      .filter(l => l.fornecedor_id === fornecedorId && !l.pago)
      .map(l => l.id);
    setVencimentosSelecionados(prev => {
      const outros = prev.filter(id => !idsPendentes.includes(id));
      const todosJaSelecionados = idsPendentes.every(id => prev.includes(id));
      return todosJaSelecionados ? outros : [...outros, ...idsPendentes];
    });
  };

  // Quitar vencimentos selecionados
  const quitarVencimentosSelecionados = async () => {
    if (vencimentosSelecionados.length === 0) {
      toast.warning('Selecione pelo menos um lançamento');
      return;
    }
    
    setProcessandoVencimentos(true);
    try {
      await vencimentosRefeicaoApi.quitar({
        lancamentos: vencimentosSelecionados,
        data_pagamento: format(new Date(), 'yyyy-MM-dd')
      });
      toast.success(`${vencimentosSelecionados.length} lançamento(s) quitado(s)`);
      setVencimentosSelecionados([]);
      loadVencimentos();
    } catch (error) {
      toast.error('Erro ao quitar vencimentos');
    } finally {
      setProcessandoVencimentos(false);
    }
  };

  // Estornar vencimentos selecionados
  const estornarVencimentosSelecionados = async () => {
    if (vencimentosSelecionados.length === 0) {
      toast.warning('Selecione pelo menos um lançamento');
      return;
    }
    
    setProcessandoVencimentos(true);
    try {
      await vencimentosRefeicaoApi.estornar({ lancamentos: vencimentosSelecionados });
      toast.success(`${vencimentosSelecionados.length} lançamento(s) estornado(s)`);
      setVencimentosSelecionados([]);
      loadVencimentos();
    } catch (error) {
      toast.error('Erro ao estornar vencimentos');
    } finally {
      setProcessandoVencimentos(false);
    }
  };

  // ==================== VENCIMENTOS DE TERCEIROS ====================
  
  const loadVencimentosTerceiros = async () => {
    setLoadingVencTerceiros(true);
    try {
      const params = {};
      if (vencimentosFiltro.dataInicio) params.data_inicio = vencimentosFiltro.dataInicio;
      if (vencimentosFiltro.dataFim) params.data_fim = vencimentosFiltro.dataFim;
      if (vencimentosFiltro.status && vencimentosFiltro.status !== 'todos') params.status = vencimentosFiltro.status;
      
      const res = await vencimentosTerceirosApi.listar(params);
      setVencimentosTerceiros(res.data);
    } catch (error) {
      console.error('Erro ao carregar vencimentos terceiros:', error);
      toast.error('Erro ao carregar vencimentos de terceiros');
    } finally {
      setLoadingVencTerceiros(false);
    }
  };

  const toggleVencTerceiroSelecionado = (id) => {
    setVencTerceirosSelecionados(prev => 
      prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
    );
  };

  const selecionarTodosTerceiro = (terceiroId) => {
    const idsPendentes = vencimentosTerceiros.lancamentos
      .filter(l => l.terceiro_id === terceiroId && !l.pago)
      .map(l => l.id);
    setVencTerceirosSelecionados(prev => {
      const outros = prev.filter(id => !idsPendentes.includes(id));
      const todosJaSelecionados = idsPendentes.every(id => prev.includes(id));
      return todosJaSelecionados ? outros : [...outros, ...idsPendentes];
    });
  };

  const quitarTerceirosSelecionados = async () => {
    if (vencTerceirosSelecionados.length === 0) {
      toast.warning('Selecione pelo menos um lançamento');
      return;
    }
    
    setProcessandoVencimentos(true);
    try {
      await vencimentosTerceirosApi.quitar({
        lancamentos: vencTerceirosSelecionados,
        data_pagamento: format(new Date(), 'yyyy-MM-dd')
      });
      toast.success(`${vencTerceirosSelecionados.length} terceiro(s) quitado(s)`);
      setVencTerceirosSelecionados([]);
      loadVencimentosTerceiros();
    } catch (error) {
      toast.error('Erro ao quitar terceiros');
    } finally {
      setProcessandoVencimentos(false);
    }
  };

  const estornarTerceirosSelecionados = async () => {
    if (vencTerceirosSelecionados.length === 0) {
      toast.warning('Selecione pelo menos um lançamento');
      return;
    }
    
    setProcessandoVencimentos(true);
    try {
      await vencimentosTerceirosApi.estornar({ lancamentos: vencTerceirosSelecionados });
      toast.success(`${vencTerceirosSelecionados.length} terceiro(s) estornado(s)`);
      setVencTerceirosSelecionados([]);
      loadVencimentosTerceiros();
    } catch (error) {
      toast.error('Erro ao estornar terceiros');
    } finally {
      setProcessandoVencimentos(false);
    }
  };

  // ==================== DOWNLOAD DE IMAGEM DO RESUMO ====================
  
  const handleDownloadResumoImage = async (tipo = 'inicial') => {
    const ref = tipo === 'inicial' ? resumoInicialRef : resumoFinalRef;
    
    if (!ref.current) {
      toast.error('Elemento do resumo não encontrado');
      return;
    }
    
    setDownloadingImage(true);
    try {
      // Configurações do html2canvas para melhor qualidade
      const canvas = await html2canvas(ref.current, {
        backgroundColor: '#ffffff',
        scale: 2, // Melhor resolução
        useCORS: true,
        logging: false,
        allowTaint: true,
        scrollY: -window.scrollY, // Evita problemas com scroll
      });
      
      // Criar link de download
      const link = document.createElement('a');
      const dataFormatada = format(new Date(selectedDate + 'T12:00:00'), 'dd-MM-yyyy', { locale: ptBR });
      link.download = `resumo-${tipo}-${dataFormatada}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success(`Imagem do Resumo ${tipo === 'inicial' ? 'Inicial' : 'Final'} baixada!`);
    } catch (error) {
      console.error('Erro ao gerar imagem:', error);
      toast.error('Erro ao gerar imagem. Tente novamente.');
    } finally {
      setDownloadingImage(false);
    }
  };

  // ==================== EXPORTAÇÃO PDF RELATÓRIO MENSAL ====================
  
  const handleExportMonthPdf = async () => {
    if (!isAdmin) {
      toast.error('Apenas administradores podem exportar o relatório mensal');
      return;
    }
    
    setExportingMonthPdf(true);
    
    const num = (v) => { const n = Number(v); return isNaN(n) ? 0 : n; };
    const fmtVenc = (d) => {
      try {
        if (!d) return '-';
        const dt = new Date(String(d).substring(0, 10) + 'T12:00:00');
        return isNaN(dt.getTime()) ? '-' : format(dt, 'dd/MM');
      } catch { return '-'; }
    };
    
    try {
      const [ano, mes] = selectedDate.split('-').map(Number);
      
      toast.info('Carregando dados do mês...', { duration: 2000 });
      
      // Buscar dados consolidados do mês
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/daily-control/relatorio-mensal/${ano}/${mes}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Erro ao buscar dados do mês (HTTP ${response.status})`);
      }
      
      const data = await response.json();
      
      toast.info('Gerando PDF...', { duration: 2000 });
      
      // Criar PDF
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.width;
      const margin = 15;
      let y = 20;
      
      // Mês em português
      const mesesPt = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                       'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
      const mesNome = mesesPt[mes - 1];
      
      // ===== CAPA / RESUMO DO MÊS =====
      doc.setFontSize(22);
      doc.setTextColor(30, 64, 175); // Azul
      doc.text('Relatório Mensal de Operações', pageWidth / 2, y, { align: 'center' });
      
      y += 12;
      doc.setFontSize(16);
      doc.setTextColor(100, 100, 100);
      doc.text(`${mesNome} ${ano}`, pageWidth / 2, y, { align: 'center' });
      
      y += 20;
      doc.setFontSize(10);
      doc.setTextColor(50, 50, 50);
      doc.text(`Gerado em: ${format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`, pageWidth / 2, y, { align: 'center' });
      
      // Resumo Geral
      y += 20;
      doc.setFontSize(14);
      doc.setTextColor(30, 64, 175);
      doc.text('Resumo Geral do Mês', margin, y);
      
      y += 8;
      const resumo = data.resumo || {};
      
      autoTable(doc, {
        startY: y,
        head: [['Indicador', 'Valor']],
        body: [
          ['Total de Dias', resumo.total_dias ?? 0],
          ['Dias Úteis', resumo.total_dias_uteis ?? 0],
          ['Feriados', resumo.total_feriados ?? 0],
          ['Total de OSs', resumo.total_oss ?? 0],
          ['Total Colaboradores (apontamentos)', resumo.total_colaboradores_trabalharam ?? 0],
          ['Total Terceiros', resumo.total_terceiros ?? 0],
          ['Valor Total Terceiros', `R$ ${num(resumo.total_valor_terceiros).toFixed(2)}`],
          ['Total Refeições', resumo.total_refeicoes ?? 0],
          ['Valor Total Refeições', `R$ ${num(resumo.total_valor_refeicoes).toFixed(2)}`],
          ['Valor Total Reembolsos', `R$ ${num(resumo.total_valor_reembolsos).toFixed(2)}`],
          ['Despesas Veículos', `R$ ${num(resumo.total_despesas_veiculos).toFixed(2)}`],
        ],
        theme: 'striped',
        headStyles: { fillColor: [30, 64, 175] },
        margin: { left: margin, right: margin },
        styles: { fontSize: 9 }
      });
      
      // ===== PÁGINAS DIÁRIAS =====
      (data.dias || []).forEach((dia, index) => {
        // Verificar se há qualquer tipo de atividade no dia
        const temOs = dia.oss?.length > 0;
        const temColaboradores = dia.colaboradores?.length > 0;
        const temTerceiros = dia.terceiros?.length > 0;
        const temRefeicoes = dia.refeicoes?.fornecedores?.length > 0;
        const temReembolsos = dia.reembolsos_excecoes?.length > 0;
        const temVeiculo = dia.veiculo_notes || dia.veiculo_km_total > 0;
        const temVencimentos = dia.vencimentos?.length > 0;
        
        const temQualquerDado = temOs || temColaboradores || temTerceiros || temRefeicoes || temReembolsos || temVeiculo || temVencimentos;
        
        // Só pula se for FDS/Feriado E não tiver NENHUMA atividade
        // Dias úteis SEMPRE aparecem
        const isDiaUtil = !dia.is_fds && !dia.is_feriado;
        if (!temQualquerDado && !isDiaUtil) return;
        
        doc.addPage();
        y = 15;
        
        // Header do dia
        doc.setFillColor(30, 64, 175);
        doc.rect(0, 0, pageWidth, 25, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(14);
        doc.text(`${dia.data_formatada} - ${dia.dia_semana}`, margin, 12);
        
        if (dia.is_feriado) {
          doc.setFontSize(10);
          doc.text(`FERIADO: ${dia.nome_feriado || 'Feriado'}`, margin, 20);
        } else if (dia.is_fds) {
          doc.setFontSize(10);
          doc.text('(Fim de Semana)', margin, 20);
        }
        
        y = 32;
        doc.setTextColor(50, 50, 50);
        
        // SERVIÇOS/OS
        doc.setFontSize(11);
        doc.setTextColor(30, 64, 175);
        doc.text('SERVIÇOS / OS', margin, y);
        y += 4;
        
        if (dia.oss.length > 0) {
          const osData = dia.oss.map(os => {
            // Montar string de horários completa
            let horarios = [];
            if (os.horario_inicio_montagem) {
              horarios.push(`Mont: ${os.horario_inicio_montagem}${os.horario_fim_montagem ? '-' + os.horario_fim_montagem : ''}`);
            }
            if (os.horario_inicio) {
              horarios.push(`Evento: ${os.horario_inicio}${os.horario_fim ? '-' + os.horario_fim : ''}`);
            }
            const horarioStr = horarios.length > 0 ? horarios.join(' | ') : '-';
            
            // Montar string de datas se for multi-dia
            let datasStr = '';
            if (os.data_montagem && os.data_montagem !== os.data_evento) {
              datasStr = `Mont: ${os.data_montagem?.substring(8,10)}/${os.data_montagem?.substring(5,7)}`;
            }
            if (os.data_evento_fim && os.data_evento_fim !== os.data_evento) {
              datasStr += (datasStr ? ' | ' : '') + `Até: ${os.data_evento_fim?.substring(8,10)}/${os.data_evento_fim?.substring(5,7)}`;
            }
            
            return [
              os.numero_os || '-',
              (os.titulo || '').substring(0, 35),
              (os.cliente_nome || '').substring(0, 20),
              horarioStr,
              (os.local_evento || '').substring(0, 30),
              os.equipe?.length || 0,
              datasStr || ''
            ];
          });
          
          autoTable(doc, {
            startY: y,
            head: [['OS', 'Título', 'Cliente', 'Horários (Montagem | Evento)', 'Local', 'Eq', 'Período']],
            body: osData,
            theme: 'grid',
            headStyles: { fillColor: [59, 130, 246], fontSize: 7 },
            styles: { fontSize: 6, cellPadding: 1.2 },
            margin: { left: margin, right: margin },
            columnStyles: {
              0: { cellWidth: 12 },
              1: { cellWidth: 30 },
              2: { cellWidth: 22 },
              3: { cellWidth: 45 },
              4: { cellWidth: 28 },
              5: { cellWidth: 10 },
              6: { cellWidth: 25 }
            }
          });
          y = doc.lastAutoTable.finalY + 6;
        } else {
          // Sem OS programada
          doc.setFontSize(8);
          doc.setTextColor(150, 150, 150);
          doc.text('Sem OS programada para este dia', margin, y);
          y += 6;
        }
        
        // APONTAMENTOS (Resumo Compacto como no Resumo Inicial)
        const colaboradoresQueApontaram = dia.colaboradores.filter(c => c.entry_time || c.exit_time);
        if (colaboradoresQueApontaram.length > 0) {
          doc.setFontSize(11);
          doc.setTextColor(30, 64, 175);
          doc.text('APONTAMENTOS', margin, y);
          y += 4;
          
          // Formato compacto: NOME (entrada-saída)
          const apontamentosText = colaboradoresQueApontaram.map(c => {
            const nome = c.human_name?.split(' ').slice(0, 2).join(' ') || '?';
            const entrada = c.entry_time || '?';
            const saida = c.exit_time || '?';
            return `${nome} (${entrada}-${saida})`;
          }).join(', ');
          
          doc.setFontSize(8);
          doc.setTextColor(50, 50, 50);
          const apontLines = doc.splitTextToSize(apontamentosText, pageWidth - margin * 2);
          doc.text(apontLines, margin, y);
          y += apontLines.length * 4 + 4;
        }
        
        // COLABORADORES - Tabela Detalhada
        if (dia.colaboradores.length > 0) {
          doc.setFontSize(11);
          doc.setTextColor(30, 64, 175);
          doc.text('COLABORADORES', margin, y);
          y += 4;
          
          const colabData = dia.colaboradores
            .filter(c => c.entry_time || c.exit_time)
            .map(c => {
              const transporteIda = c.transporte_ida || c.transporte_tipo || 'vt';
              const transporteVolta = c.transporte_volta || c.transporte_tipo || 'vt';
              const labelMap = { 'vt': 'VT', 'uber': 'Uber', 'carona': 'Car', 'veiculo_proprio': 'Próp', 'valor_fixo': 'Próp' };
              // Usar 'observations' (nome correto do campo do backend)
              const obs = c.observations || c.observacoes || '';
              return [
                (c.human_name || '').substring(0, 25),
                c.entry_time || '-',
                c.exit_time || '-',
                c.os_number || '-',
                `${labelMap[transporteIda] || 'VT'}/${labelMap[transporteVolta] || 'VT'}`,
                obs.substring(0, 35) || ''
              ];
            });
          
          if (colabData.length > 0) {
            autoTable(doc, {
              startY: y,
              head: [['Nome', 'Entrada', 'Saída', 'OS', 'Transp.', 'Observações']],
              body: colabData,
              theme: 'grid',
              headStyles: { fillColor: [34, 197, 94], fontSize: 8 },
              styles: { fontSize: 7, cellPadding: 1.5 },
              margin: { left: margin, right: margin },
              columnStyles: {
                0: { cellWidth: 35 },
                5: { cellWidth: 45 }
              }
            });
            y = doc.lastAutoTable.finalY + 6;
          }
          
          // FALTAS - Colaboradores que não bateram ponto (EXCETO fins de semana, feriados e folgas)
          // Verificar se o dia é FDS ou feriado - nesses casos, não é falta
          const ehFds = dia.is_fds;
          const ehFeriado = dia.is_feriado;
          
          const colaboradoresFaltaram = dia.colaboradores.filter(c => {
            // Se bateu ponto, não é falta
            if (c.entry_time || c.exit_time) return false;
            
            // Verificar status do colaborador - folgas não são faltas
            const status = (c.status || '').toLowerCase();
            const statusFolga = ['folga', 'folga/banco', 'banco_horas', 'ferias', 'atestado', 'licenca', 'feriado', 'compensacao', 'dispensa', 'abonado'];
            
            // Se tem status de folga, não é falta
            if (statusFolga.some(s => status.includes(s))) return false;
            
            // Se é fim de semana ou feriado E o colaborador não foi escalado (não tem OS), não é falta
            if ((ehFds || ehFeriado) && !c.os_number) return false;
            
            // Caso contrário, é falta
            return true;
          });
          
          if (colaboradoresFaltaram.length > 0) {
            doc.setFontSize(11);
            doc.setTextColor(220, 38, 38); // Vermelho
            doc.text(`FALTAS (${colaboradoresFaltaram.length})`, margin, y);
            y += 4;
            
            const faltasText = colaboradoresFaltaram.map(c => {
              const status = c.status || 'FALTA';
              return `• ${c.human_name?.split(' ').slice(0, 2).join(' ')} - ${status.toUpperCase()}`;
            }).join('\n');
            
            doc.setFontSize(8);
            doc.setTextColor(220, 38, 38);
            const faltasLines = doc.splitTextToSize(faltasText, pageWidth - margin * 2);
            doc.text(faltasLines, margin, y);
            y += faltasLines.length * 4 + 4;
          }
          
          // TRANSPORTE AGRUPADO
          const colaboradoresComPonto = dia.colaboradores.filter(c => c.entry_time || c.exit_time);
          if (colaboradoresComPonto.length > 0) {
            doc.setFontSize(11);
            doc.setTextColor(30, 64, 175);
            doc.text('TRANSPORTE', margin, y);
            y += 4;
            
            // Agrupar por tipo de transporte
            const labelMap = { 'vt': 'VT', 'uber': 'Uber', 'carona': 'Carona', 'veiculo_proprio': 'Próprio', 'valor_fixo': 'Próprio' };
            const transporteGrupos = {};
            
            colaboradoresComPonto.forEach(c => {
              const tipoIda = c.transporte_ida || c.transporte_tipo || 'vt';
              const tipoVolta = c.transporte_volta || c.transporte_tipo || 'vt';
              const tipoLabel = tipoIda === tipoVolta ? labelMap[tipoIda] || 'VT' : `${labelMap[tipoIda] || 'VT'}/${labelMap[tipoVolta] || 'VT'}`;
              
              if (!transporteGrupos[tipoLabel]) transporteGrupos[tipoLabel] = [];
              transporteGrupos[tipoLabel].push(c.human_name?.split(' ')[0] || '?');
            });
            
            let transporteText = Object.entries(transporteGrupos)
              .map(([tipo, nomes]) => `${tipo}: ${nomes.join(', ')}`)
              .join('\n');
            
            doc.setFontSize(8);
            doc.setTextColor(50, 50, 50);
            const transpLines = doc.splitTextToSize(transporteText, pageWidth - margin * 2);
            doc.text(transpLines, margin, y);
            y += transpLines.length * 4 + 4;
          }
        }
        
        // TERCEIROS
        if (dia.terceiros.length > 0) {
          doc.setFontSize(11);
          doc.setTextColor(30, 64, 175);
          doc.text('TERCEIROS', margin, y);
          y += 4;
          
          const tercData = dia.terceiros.map(t => {
            const obs = t.observacoes || t.observations || t.servico || '';
            return [
              t.human_name || '-',
              t.servico || t.funcao || '-',
              num(t.value) ? `R$ ${num(t.value).toFixed(2)}` : '-',
              fmtVenc(t.data_vencimento),
              t.quitado ? 'PAGO' : 'PEND',
              obs.substring(0, 30) || ''
            ];
          });
          
          autoTable(doc, {
            startY: y,
            head: [['Nome', 'Serviço', 'Valor', 'Venc.', 'Status', 'Obs']],
            body: tercData,
            theme: 'grid',
            headStyles: { fillColor: [168, 85, 247], fontSize: 7 },
            styles: { fontSize: 6, cellPadding: 1.2 },
            margin: { left: margin, right: margin },
            columnStyles: {
              0: { cellWidth: 30 },
              1: { cellWidth: 25 },
              2: { cellWidth: 22 },
              3: { cellWidth: 15 },
              4: { cellWidth: 15 },
              5: { cellWidth: 'auto' }
            }
          });
          
          // Total de terceiros
          const totalTerc = dia.terceiros.reduce((acc, t) => acc + num(t.value), 0);
          if (totalTerc > 0) {
            doc.setFontSize(8);
            doc.setTextColor(168, 85, 247);
            doc.setFont(undefined, 'bold');
            doc.text(`Total Terceiros: R$ ${totalTerc.toFixed(2)}`, margin, doc.lastAutoTable.finalY + 4);
            doc.setFont(undefined, 'normal');
          }
          
          y = doc.lastAutoTable.finalY + 8;
        }
        
        // REFEIÇÕES
        if (dia.refeicoes.fornecedores?.length > 0) {
          doc.setFontSize(11);
          doc.setTextColor(30, 64, 175);
          doc.text('REFEIÇÕES', margin, y);
          y += 4;
          
          // Para cada fornecedor, mostrar detalhes e quem comeu
          dia.refeicoes.fornecedores.forEach(f => {
            const funcionariosNomes = (f.funcionarios || []).join(', ');
            const subtotal = (f.funcionarios?.length || 0) * num(f.valor_unitario) + num(f.taxa_adicional);
            
            // Linha do fornecedor
            doc.setFontSize(8);
            doc.setTextColor(50, 50, 50);
            doc.setFont(undefined, 'bold');
            doc.text(`${f.fornecedor_nome || '-'}: ${f.funcionarios?.length || 0} pessoas - R$ ${num(f.valor_unitario).toFixed(2)}/un = R$ ${subtotal.toFixed(2)}`, margin, y);
            y += 4;
            
            // Lista quem comeu (se houver)
            if (funcionariosNomes) {
              doc.setFont(undefined, 'normal');
              doc.setFontSize(7);
              doc.setTextColor(100, 100, 100);
              const funcLines = doc.splitTextToSize(`  → ${funcionariosNomes}`, pageWidth - margin * 2);
              doc.text(funcLines, margin, y);
              y += funcLines.length * 3 + 2;
            }
            
            // Vencimento se houver
            if (f.data_vencimento) {
              doc.setFontSize(7);
              doc.setTextColor(168, 85, 247);
              doc.text(`  Venc: ${fmtVenc(f.data_vencimento)} ${f.quitado ? '(PAGO)' : '(PENDENTE)'}`, margin, y);
              y += 4;
            }
          });
          y += 2;
          
          // Observações gerais das refeições
          if (dia.refeicoes.observacoes_gerais) {
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            const obsRefLines = doc.splitTextToSize(`Obs Refeições: ${dia.refeicoes.observacoes_gerais}`, pageWidth - margin * 2);
            doc.text(obsRefLines, margin, y);
            y += obsRefLines.length * 4 + 2;
          }
        }
        
        // REEMBOLSOS EXCEÇÕES
        if (dia.reembolsos_excecoes?.length > 0) {
          doc.setFontSize(11);
          doc.setTextColor(30, 64, 175);
          doc.text('REEMBOLSOS / EXCEÇÕES', margin, y);
          y += 4;
          
          const reembData = dia.reembolsos_excecoes.map(r => [
            (r.funcionario_nome || '').substring(0, 20),
            r.descricao?.substring(0, 25) || '-',
            `R$ ${num(r.valor).toFixed(2)}`,
            fmtVenc(r.data_vencimento)
          ]);
          
          autoTable(doc, {
            startY: y,
            head: [['Funcionário', 'Descrição', 'Valor', 'Venc.']],
            body: reembData,
            theme: 'grid',
            headStyles: { fillColor: [234, 179, 8], fontSize: 8 },
            styles: { fontSize: 7, cellPadding: 1.5 },
            margin: { left: margin, right: margin }
          });
          y = doc.lastAutoTable.finalY + 6;
        }
        
        // DESPESAS VEÍCULOS
        const expenses = dia.veiculo_expenses || {};
        const vehicleData = dia.veiculo_data || {};
        const totalDesp = num(expenses.fuel) + num(expenses.maintenance) + num(expenses.other);
        const hasVehicleData = Object.keys(vehicleData).some(k => vehicleData[k]?.km || vehicleData[k]?.rota);
        
        if (totalDesp > 0 || dia.veiculo_notes || hasVehicleData) {
          doc.setFontSize(11);
          doc.setTextColor(30, 64, 175);
          doc.text('VEÍCULOS / DESPESAS', margin, y);
          y += 4;
          
          // Mostrar dados de km/rota dos veículos - SEM TRUNCAR
          if (hasVehicleData) {
            const vehKmData = Object.entries(vehicleData)
              .filter(([_, v]) => v?.km || v?.rota)
              .map(([nome, v]) => [
                nome,
                v.km ? `${v.km} km` : '-',
                v.rota || ''  // NÃO TRUNCAR - deixar completo
              ]);
            
            if (vehKmData.length > 0) {
              autoTable(doc, {
                startY: y,
                head: [['Veículo', 'KM', 'Rota/Destino']],
                body: vehKmData,
                theme: 'grid',
                headStyles: { fillColor: [71, 85, 105], fontSize: 8 },
                styles: { fontSize: 6, cellPadding: 1.5, overflow: 'linebreak' }, // quebra linha se necessário
                margin: { left: margin, right: margin },
                columnStyles: {
                  0: { cellWidth: 25 },
                  1: { cellWidth: 20 },
                  2: { cellWidth: 'auto' } // coluna de rota expande para caber tudo
                }
              });
              y = doc.lastAutoTable.finalY + 3;
            }
          }
          
          // Despesas
          const vehData = [];
          if (num(expenses.fuel) > 0) vehData.push(['Combustível', `R$ ${num(expenses.fuel).toFixed(2)}`]);
          if (num(expenses.maintenance) > 0) vehData.push(['Manutenção', `R$ ${num(expenses.maintenance).toFixed(2)}`]);
          if (num(expenses.other) > 0) vehData.push(['Outros', `R$ ${num(expenses.other).toFixed(2)}`]);
          if (totalDesp > 0) vehData.push(['TOTAL DESPESAS', `R$ ${totalDesp.toFixed(2)}`]);
          
          if (vehData.length > 0) {
            autoTable(doc, {
              startY: y,
              head: [['Tipo Despesa', 'Valor']],
              body: vehData,
              theme: 'grid',
              headStyles: { fillColor: [100, 116, 139], fontSize: 8 },
              styles: { fontSize: 7, cellPadding: 1.5 },
              margin: { left: margin, right: margin },
              tableWidth: 80
            });
            y = doc.lastAutoTable.finalY + 4;
          }
          
          // Descrição das despesas
          if (expenses.description) {
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            const descLines = doc.splitTextToSize(`Descrição: ${expenses.description}`, pageWidth - margin * 2);
            doc.text(descLines, margin, y);
            y += descLines.length * 4 + 2;
          }
        }
        
        // VENCIMENTOS DO DIA - Consolidado
        const vencimentosTerceiros = dia.terceiros.filter(t => t.data_vencimento);
        const vencimentosRefeicoes = (dia.refeicoes.fornecedores || []).filter(f => f.data_vencimento);
        const vencimentosReembolsos = (dia.reembolsos_excecoes || []).filter(r => r.data_vencimento);
        const hasVencimentos = vencimentosTerceiros.length > 0 || vencimentosRefeicoes.length > 0 || vencimentosReembolsos.length > 0;
        
        if (hasVencimentos) {
          doc.setFontSize(11);
          doc.setTextColor(30, 64, 175);
          doc.text('VENCIMENTOS DO DIA', margin, y);
          y += 4;
          
          let vencimentosText = '';
          
          // Terceiros com vencimento
          if (vencimentosTerceiros.length > 0) {
            vencimentosText += 'TERCEIROS:\n';
            vencimentosTerceiros.forEach(t => {
              vencimentosText += `  • ${t.human_name} - Venc: ${fmtVenc(t.data_vencimento)} - R$${num(t.value).toFixed(2)}\n`;
            });
          }
          
          // Refeições com vencimento
          if (vencimentosRefeicoes.length > 0) {
            vencimentosText += 'REFEIÇÕES:\n';
            vencimentosRefeicoes.forEach(f => {
              const subtotal = (f.funcionarios?.length || 0) * num(f.valor_unitario);
              vencimentosText += `  • ${f.fornecedor_nome} - Venc: ${fmtVenc(f.data_vencimento)} - R$${subtotal.toFixed(2)}\n`;
            });
          }
          
          // Reembolsos com vencimento
          if (vencimentosReembolsos.length > 0) {
            vencimentosText += 'REEMBOLSOS:\n';
            vencimentosReembolsos.forEach(r => {
              vencimentosText += `  • ${r.funcionario_nome} (${r.descricao || ''}) - Venc: ${fmtVenc(r.data_vencimento)} - R$${num(r.valor).toFixed(2)}\n`;
            });
          }
          
          doc.setFontSize(7);
          doc.setTextColor(50, 50, 50);
          const vencLines = doc.splitTextToSize(vencimentosText.trim(), pageWidth - margin * 2);
          doc.text(vencLines, margin, y);
          y += vencLines.length * 3.5 + 4;
        }
        
        // OBSERVAÇÕES GERAIS DO DIA (vehicle_notes)
        if (dia.veiculo_notes && dia.veiculo_notes.trim()) {
          doc.setFontSize(11);
          doc.setTextColor(30, 64, 175);
          doc.text('OBSERVAÇÕES DO DIA', margin, y);
          y += 4;
          
          doc.setFontSize(8);
          doc.setTextColor(50, 50, 50);
          const notesLines = doc.splitTextToSize(dia.veiculo_notes, pageWidth - margin * 2);
          doc.text(notesLines, margin, y);
          y += notesLines.length * 4 + 4;
        }
        
        // Totais do dia no rodapé
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(
          `Colab: ${dia.totais?.colaboradores ?? 0} | Terc: ${dia.totais?.terceiros ?? 0} (R$${num(dia.totais?.valor_terceiros).toFixed(0)}) | Ref: ${dia.totais?.refeicoes ?? 0} | Desp: R$${num(dia.totais?.despesas_veiculos).toFixed(0)}`,
          margin,
          285
        );
      });
      
      // Salvar PDF
      doc.save(`relatorio_mensal_${mesNome.toLowerCase()}_${ano}.pdf`);
      toast.success('PDF exportado com sucesso!');
      
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.error(`Erro ao gerar PDF do mês: ${error?.message || 'erro desconhecido'}`);
    } finally {
      setExportingMonthPdf(false);
    }
  };

  // ==================== CONTROLE DE SALDO VT ====================
  
  const loadSaldoVT = async () => {
    setLoadingSaldoVT(true);
    try {
      const res = await vtApi.getSaldoMensal(vtMesSelecionado.ano, vtMesSelecionado.mes);
      setSaldoVT(res.data);
    } catch (error) {
      console.error('Erro ao carregar saldo VT:', error);
      toast.error('Erro ao carregar saldo de VT');
    } finally {
      setLoadingSaldoVT(false);
    }
  };

  const loadVtFuncionarioDetalhe = async (funcionarioId) => {
    try {
      const res = await vtApi.getFuncionarioDetalhado(funcionarioId, vtMesSelecionado.ano, vtMesSelecionado.mes);
      setVtFuncionarioDetalhe(res.data);
      setShowVtDetalhe(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes do VT');
    }
  };

  // Carregar extrato VT do próprio usuário (não-admin)
  const loadMeuExtratoVT = async () => {
    try {
      setLoadingSaldoVT(true);
      const res = await vtApi.getMeuExtrato(vtMesSelecionado.ano, vtMesSelecionado.mes);
      setVtFuncionarioDetalhe(res.data);
      setShowVtDetalhe(true);
    } catch (error) {
      console.error('Erro ao carregar meu extrato VT:', error);
      const detail = error.response?.data?.detail || 'Erro ao carregar seu extrato de VT';
      if (detail.includes('não encontrado')) {
        toast.error(
          <div className="text-sm">
            <p className="font-medium mb-1">Funcionário não encontrado</p>
            <p className="text-xs text-slate-400">
              Seu usuário não está vinculado a um funcionário.<br/>
              Peça ao admin para vincular em Configurações → Usuários.
            </p>
          </div>,
          { duration: 8000 }
        );
      } else {
        toast.error(detail);
      }
    } finally {
      setLoadingSaldoVT(false);
    }
  };

  // Atualizar dados de VT de um dia específico
  const updateVtDia = async (recordId, data) => {
    try {
      await vtApi.updateDia(recordId, data);
      toast.success('VT atualizado!');
      // Recarregar extrato
      if (vtFuncionarioDetalhe?.funcionario?.id) {
        loadVtFuncionarioDetalhe(vtFuncionarioDetalhe.funcionario.id);
      } else if (!isAdmin) {
        loadMeuExtratoVT();
      }
    } catch (error) {
      console.error('Erro ao atualizar VT:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar VT');
    }
  };

  // Gerar Documento de Ocorrência em PDF
  const generateTermoOcorrencia = (record, blank = false) => {
    const [year, month, dayNum] = selectedDate.split('-').map(Number);
    const dateObj = new Date(year, month - 1, dayNum);
    const formattedDate = format(dateObj, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
    const dayOfWeek = format(dateObj, 'EEEE', { locale: ptBR });
    
    // Criar conteúdo do termo
    const termoHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Documento de Ocorrência - ${blank ? 'Em Branco' : record?.human_name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
          .header h1 { margin: 0; font-size: 24px; color: #333; }
          .header h2 { margin: 10px 0 0; font-size: 16px; color: #666; font-weight: normal; }
          .company { font-size: 18px; font-weight: bold; color: #6b21a8; margin-bottom: 5px; }
          .content { line-height: 1.8; font-size: 14px; }
          .field { margin: 15px 0; }
          .field-label { font-weight: bold; display: inline-block; min-width: 150px; }
          .field-value { border-bottom: 1px solid #999; display: inline-block; min-width: 300px; padding: 2px 5px; }
          .blank-field { border-bottom: 1px solid #999; display: inline-block; width: 100%; height: 20px; margin: 5px 0; }
          .signature-section { margin-top: 60px; display: flex; justify-content: space-between; }
          .signature-box { text-align: center; width: 45%; }
          .signature-line { border-top: 1px solid #333; padding-top: 5px; margin-top: 60px; }
          .justification { margin: 20px 0; padding: 15px; border: 1px solid #ddd; border-radius: 5px; min-height: 100px; }
          .footer { margin-top: 40px; font-size: 12px; color: #666; text-align: center; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company">GRUPO TOMASELLI</div>
          <h1>TERMO DE OCORRÊNCIA</h1>
          <h2>Registro Manual de Ponto</h2>
        </div>
        
        <div class="content">
          <div class="field">
            <span class="field-label">Data:</span>
            <span class="field-value">${blank ? '' : formattedDate} (${blank ? '' : dayOfWeek})</span>
          </div>
          
          <div class="field">
            <span class="field-label">Colaborador:</span>
            <span class="field-value">${blank ? '' : record?.human_name || ''}</span>
          </div>
          
          <div class="field">
            <span class="field-label">Horário de Entrada:</span>
            <span class="field-value">${blank ? '' : record?.entry_time || '___:___'}</span>
            <span style="margin-left: 20px;">Fonte: ${blank ? '( ) Ponto  ( ) Manual' : record?.entry_source === 'ponto' ? '(X) Ponto  ( ) Manual' : '( ) Ponto  (X) Manual'}</span>
          </div>
          
          <div class="field">
            <span class="field-label">Horário de Saída:</span>
            <span class="field-value">${blank ? '' : record?.exit_time || '___:___'}</span>
            <span style="margin-left: 20px;">Fonte: ${blank ? '( ) Ponto  ( ) Manual' : record?.exit_source === 'ponto' ? '(X) Ponto  ( ) Manual' : '( ) Ponto  (X) Manual'}</span>
          </div>
          
          <div class="field">
            <span class="field-label">Local de Trabalho:</span>
            <span class="field-value">${blank ? '' : record?.location || ''}</span>
          </div>
          
          <div class="field">
            <span class="field-label">Tipo de Serviço:</span>
            <span class="field-value">${blank ? '' : record?.service_type || ''}</span>
          </div>
          
          <p style="margin-top: 30px; font-weight: bold;">JUSTIFICATIVA DO REGISTRO MANUAL:</p>
          <div class="justification">
            ${blank ? '&nbsp;' : (record?.entry_justification || record?.exit_justification || 'Sem justificativa informada.')}
          </div>
          
          <p style="margin-top: 20px; font-weight: bold;">OBSERVAÇÕES:</p>
          <div class="justification">
            ${blank ? '&nbsp;' : (record?.observations || '')}
          </div>
          
          <div class="signature-section">
            <div class="signature-box">
              <div class="signature-line">Assinatura do Colaborador</div>
            </div>
            <div class="signature-box">
              <div class="signature-line">Assinatura do Responsável</div>
            </div>
          </div>
          
          <div class="footer">
            <p>Documento gerado em ${format(new Date(), "dd/MM/yyyy 'às' HH:mm")}</p>
            <p>Este documento deve ser assinado e arquivado junto ao departamento de RH.</p>
          </div>
        </div>
      </body>
      </html>
    `;
    
    // Abrir em nova janela para impressão
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(termoHTML);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 500);
    }
  };

  // Gerar Relatório Individual do Funcionário
  const generateRelatorioFuncionario = async (funcionarioId, funcionarioNome) => {
    try {
      const [year, month] = selectedDate.split('-').map(Number);
      const response = await reportsApi.getFuncionarioReport(funcionarioId, month, year);
      const data = response.data;
      
      // Gerar linhas da tabela
      const diasRows = data.dias.map(dia => {
        const dataFormatada = dia.data ? format(new Date(dia.data + 'T12:00:00'), 'dd/MM (EEE)', { locale: ptBR }) : '';
        const entradaClass = dia.entrada_source === 'manual' ? 'term' : 'ponto';
        const saidaClass = dia.saida_source === 'manual' ? 'term' : 'ponto';
        const localClass = dia.local === 'faltou' ? 'falta' : (dia.local.includes('folga') || dia.local === 'banco_horas' ? 'folga' : '');
        return '<tr>' +
          '<td>' + dataFormatada + '</td>' +
          '<td class="' + entradaClass + '">' + (dia.entrada || '-') + '</td>' +
          '<td class="' + saidaClass + '">' + (dia.saida || '-') + '</td>' +
          '<td>' + (dia.horas || '-') + '</td>' +
          '<td class="' + localClass + '">' + dia.local + '</td>' +
          '<td>' + (dia.servico || '-') + '</td>' +
          '<td>' + (dia.os || '-') + '</td>' +
          '<td>' + (dia.observacao || '') + '</td>' +
          '</tr>';
      }).join('');
      
      // Gerar seção de pendências
      let pendenciasSection = '';
      if (data.pendencias.total > 0) {
        const pendenciasItems = data.pendencias.detalhes.map(p => {
          return '<div class="pendencia-item">' +
            '<strong>' + (p.tipo === 'ponto' ? 'Ponto' : 'Equipamento') + '</strong> - ' + p.data_ocorrencia + ' - ' +
            p.subtipo + ' ' + (p.termo_emitido ? '✓ Termo emitido' : '⚠ Pendente') +
            (p.observacao ? ' - ' + p.observacao : '') +
            '</div>';
        }).join('');
        pendenciasSection = '<div class="section-title">Pendências (' + data.pendencias.total + ')</div>' + pendenciasItems;
      }
      
      const relatorioHTML = '<!DOCTYPE html>' +
        '<html><head><meta charset="UTF-8">' +
        '<title>Relatório - ' + data.funcionario.nome + ' - ' + data.periodo.mes_nome + '/' + data.periodo.ano + '</title>' +
        '<style>' +
        'body { font-family: Arial, sans-serif; padding: 30px; max-width: 1000px; margin: 0 auto; font-size: 12px; }' +
        '.header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #6b21a8; padding-bottom: 15px; }' +
        '.header h1 { margin: 0; font-size: 20px; color: #6b21a8; }' +
        '.header h2 { margin: 5px 0 0; font-size: 14px; color: #666; }' +
        '.info-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 20px; }' +
        '.info-box { background: #f5f3ff; padding: 10px; border-radius: 5px; text-align: center; }' +
        '.info-box .value { font-size: 18px; font-weight: bold; color: #6b21a8; }' +
        '.info-box .label { font-size: 10px; color: #666; }' +
        'table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 10px; }' +
        'th { background: #6b21a8; color: white; padding: 8px 4px; text-align: left; }' +
        'td { padding: 6px 4px; border-bottom: 1px solid #e5e5e5; }' +
        'tr:nth-child(even) { background: #f9f9f9; }' +
        '.term { color: #ea580c; font-weight: bold; }' +
        '.ponto { color: #0284c7; }' +
        '.falta { color: #dc2626; }' +
        '.folga { color: #16a34a; }' +
        '.section-title { font-size: 14px; font-weight: bold; color: #6b21a8; margin: 20px 0 10px; border-bottom: 1px solid #e5e5e5; padding-bottom: 5px; }' +
        '.pendencia-item { background: #fef3c7; padding: 8px; margin: 5px 0; border-radius: 4px; border-left: 3px solid #f59e0b; }' +
        '.footer { margin-top: 30px; text-align: center; font-size: 10px; color: #999; border-top: 1px solid #e5e5e5; padding-top: 10px; }' +
        '@media print { body { padding: 10px; } }' +
        '</style></head><body>' +
        '<div class="header">' +
        '<h1>RELATÓRIO INDIVIDUAL - ' + data.funcionario.nome.toUpperCase() + '</h1>' +
        '<h2>' + data.periodo.mes_nome + ' de ' + data.periodo.ano + ' | ' + (data.funcionario.subtipo || data.funcionario.tipo) + '</h2>' +
        '</div>' +
        '<div class="info-grid">' +
        '<div class="info-box"><div class="value">' + data.resumo.total_dias_trabalhados + '</div><div class="label">Dias Trabalhados</div></div>' +
        '<div class="info-box"><div class="value">' + data.resumo.total_horas + 'h</div><div class="label">Total Horas</div></div>' +
        '<div class="info-box"><div class="value">' + data.resumo.total_faltas + '</div><div class="label">Faltas</div></div>' +
        '<div class="info-box"><div class="value">' + data.resumo.total_termos + '</div><div class="label">Termos</div></div>' +
        '<div class="info-box"><div class="value">' + data.resumo.total_atrasos + '</div><div class="label">Atrasos</div></div>' +
        '<div class="info-box"><div class="value">' + data.resumo.total_saidas_antecipadas + '</div><div class="label">Saídas Antec.</div></div>' +
        '<div class="info-box"><div class="value">' + data.resumo.media_horas_dia + 'h</div><div class="label">Média/Dia</div></div>' +
        '<div class="info-box"><div class="value">' + data.resumo.total_batidas_extras + '</div><div class="label">Batidas Extras</div></div>' +
        '</div>' +
        '<div class="section-title">Detalhamento Diário</div>' +
        '<table><thead><tr><th>Data</th><th>Entrada</th><th>Saída</th><th>Horas</th><th>Local</th><th>Serviço</th><th>OS</th><th>Obs</th></tr></thead>' +
        '<tbody>' + diasRows + '</tbody></table>' +
        pendenciasSection +
        '<div class="footer"><p>Relatório gerado em ' + format(new Date(), "dd/MM/yyyy 'às' HH:mm") + '</p></div>' +
        '</body></html>';
      
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(relatorioHTML);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => printWindow.print(), 500);
      }
      
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error('Erro ao gerar relatório do funcionário');
    }
  };

  // Calcular totais
  const colaboradoresRecords = dayRecords.filter(r => r.type === 'colaborador');
  const terceirosRecords = dayRecords.filter(r => r.type === 'terceiro');
  
  const totalTerceiros = terceirosRecords.reduce((sum, r) => {
    if (r.value_type === 'definido' && r.value) {
      return sum + r.value;
    }
    return sum;
  }, 0);

  const totalWastes = wastes.reduce((sum, w) => sum + (w.value || 0), 0);
  const mealsWasted = meals.wasted || 0;

  // Filtrar terceiros disponíveis
  const addedHumanIds = dayRecords.map(r => r.human_id);
  const availableTerceiros = terceiros.filter(t => !addedHumanIds.includes(t.id));
  
  const filteredTerceiros = searchTerm 
    ? availableTerceiros.filter(t => 
        t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.subtype && t.subtype.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : availableTerceiros;

  // Filtrar colaboradores na aba de apontamentos (persiste ao mudar de dia)
  const filteredColaboradorRecords = filterColaborador
    ? dayRecords.filter(r => r.type === 'colaborador' && r.human_name?.toLowerCase().includes(filterColaborador.toLowerCase()))
    : dayRecords.filter(r => r.type === 'colaborador');

  // Lista de colaboradores únicos para o dropdown de filtro
  const colaboradoresUnicos = [...new Set(dayRecords.filter(r => r.type === 'colaborador').map(r => r.human_name))].sort();

  const getServiceTypeLabel = (type) => {
    // Primeiro tenta encontrar nos tipos dinâmicos da empresa
    const tipoDinamico = tiposAtividade.find(t => t.id === type);
    if (tipoDinamico) return tipoDinamico.nome;
    
    // Fallback para labels fixas (para dados antigos)
    const labels = {
      'evento': 'Evento',
      'montagem': 'Montagem',
      'desmontagem': 'Desmontagem',
      'manutencao': 'Manutenção',
      'entrega': 'Entrega',
      'retirada': 'Retirada',
      'atividade_interna': 'Ativ. Interna c/ Resultado',
      'ativ_interna': 'Ativ. Interna c/ Resultado',
      'nao_informado': 'Não Informado',
      'outros': 'Outros',
    };
    return labels[type] || type || 'N/A';
  };
  
  const getServiceTypeColor = (type) => {
    const tipoDinamico = tiposAtividade.find(t => t.id === type);
    if (tipoDinamico) return tipoDinamico.cor;
    
    // Cores padrão para tipos antigos
    const cores = {
      'evento': '#f59e0b',
      'montagem': '#3b82f6',
      'desmontagem': '#8b5cf6',
      'manutencao': '#10b981',
      'entrega': '#06b6d4',
      'retirada': '#ec4899',
      'atividade_interna': '#6366f1',
      'ativ_interna': '#6366f1',
      'nao_informado': '#6b7280',
    };
    return cores[type] || '#6b7280';
  };

  const getWasteTypeLabel = (type) => {
    const labels = {
      'refeicao_desperdicada': 'Refeição',
      'material_danificado': 'Material Danificado',
      'desvio': 'Desvio',
      'outros': 'Outros',
    };
    return labels[type] || type;
  };

  const openEditRecord = (record) => {
    setEditingRecord({ ...record });
    setShowEditRecord(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div data-testid="daily-control-page" className="space-y-6 animate-fade-in">
      {/* Header with date navigation */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Escala do Dia</h1>
          <p className="text-slate-500 dark:text-slate-400">
            {format(new Date(selectedDate + 'T12:00:00'), "EEEE, dd 'de' MMMM", { locale: ptBR })}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Botão Exportar Mês - Apenas Admin */}
          {isAdmin && (
            <Button 
              variant="outline" 
              onClick={handleExportMonthPdf}
              disabled={exportingMonthPdf}
              className="dark:border-slate-700 bg-emerald-50 border-emerald-300 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-700"
              data-testid="export-month-pdf-btn"
            >
              {exportingMonthPdf ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
              )}
              <span className="hidden sm:inline">Exportar Mês</span>
              <span className="sm:hidden">PDF</span>
            </Button>
          )}
          
          <Button 
            variant="outline" 
            onClick={() => { setFolgaColetivaTipo('folga'); setShowFolgaColetivaModal(true); }}
            className="border-green-500 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-400"
            data-testid="folga-coletiva-btn"
            title="Lançar folga para todos os colaboradores nesta data"
          >
            <Calendar className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Folga p/ Todos</span>
            <span className="sm:hidden">Folga</span>
          </Button>
          
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => changeDate(-1)} 
            className="dark:border-slate-700"
            data-testid="prev-date-btn"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="border-0 focus:ring-0 text-sm font-medium bg-transparent dark:text-slate-100"
              data-testid="date-input"
            />
          </div>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => changeDate(1)} 
            className="dark:border-slate-700"
            data-testid="next-date-btn"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-xs font-medium">Colaboradores</span>
            </div>
            <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{colaboradoresRecords.length}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-indigo-50 border-indigo-200 dark:bg-indigo-950/30 dark:border-indigo-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 mb-1">
              <Briefcase className="w-4 h-4" />
              <span className="text-xs font-medium">Terceiros</span>
            </div>
            <p className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">{terceirosRecords.length}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-1">
              <DollarSign className="w-4 h-4" />
              <span className="text-xs font-medium">Total Terceiros</span>
            </div>
            <p className="text-2xl font-bold text-green-700 dark:text-green-300">
              R$ {totalTerceiros.toFixed(2)}
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 mb-1">
              <Utensils className="w-4 h-4" />
              <span className="text-xs font-medium">Refeições</span>
            </div>
            <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">
              {meals.served}/{meals.planned}
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400 mb-1">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-xs font-medium">Desperdícios</span>
            </div>
            <p className="text-2xl font-bold text-red-700 dark:text-red-300">
              R$ {totalWastes.toFixed(2)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="resumo" className="space-y-4">
        <TabsList className="grid grid-cols-9 w-full max-w-5xl">
          <TabsTrigger value="resumo" className="text-xs bg-emerald-500/10 data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
            <FileText className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Inicial</span><span className="sm:hidden">Ini</span>
          </TabsTrigger>
          <TabsTrigger value="resumo-final" className="text-xs bg-blue-500/10 data-[state=active]:bg-blue-500 data-[state=active]:text-white">
            <CheckSquare className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Final</span><span className="sm:hidden">Fin</span>
          </TabsTrigger>
          <TabsTrigger value="apontamentos" className="text-xs">
            <ClipboardList className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Aponta</span><span className="sm:hidden">Apt</span>
          </TabsTrigger>
          <TabsTrigger value="terceiros" className="text-xs">
            <Briefcase className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Terceiros</span><span className="sm:hidden">3º</span>
          </TabsTrigger>
          <TabsTrigger value="veiculos" className="text-xs">
            <Truck className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Veículos</span><span className="sm:hidden">Veíc</span>
          </TabsTrigger>
          <TabsTrigger value="refeicoes" className="text-xs">
            <Utensils className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Refeições</span><span className="sm:hidden">Ref</span>
          </TabsTrigger>
          <TabsTrigger value="vencimentos" className="text-xs">
            <CreditCard className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Vencimentos</span><span className="sm:hidden">Venc</span>
          </TabsTrigger>
          <TabsTrigger value="vt" className="text-xs">
            <Bus className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">VT</span><span className="sm:hidden">VT</span>
          </TabsTrigger>
          <TabsTrigger value="desperdicios" className="text-xs">
            <AlertTriangle className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Desper</span><span className="sm:hidden">Desp</span>
          </TabsTrigger>
        </TabsList>

        {/* RESUMO INICIAL TAB - Relatório do dia para print */}
        <TabsContent value="resumo">
          <Card className="dark:bg-slate-900 dark:border-slate-700 print:shadow-none print:border-none max-w-2xl">
            <CardHeader className="pb-1 print:pb-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2 dark:text-slate-100">
                  <FileText className="w-4 h-4 text-emerald-500 print:hidden" />
                  Resumo Inicial
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDownloadResumoImage('inicial')}
                  disabled={downloadingImage}
                  className="print:hidden"
                >
                  {downloadingImage ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  ) : (
                    <FileDown className="w-4 h-4 mr-1" />
                  )}
                  Baixar Imagem
                </Button>
              </div>
            </CardHeader>
            {/* Área capturável para imagem */}
            <div ref={resumoInicialRef} className="bg-white dark:bg-slate-900">
            <CardContent className="space-y-3 text-sm print:text-xs pt-0">
              {/* Data e Dia da Semana - Compacto */}
              <div className="text-center pb-2 border-b border-slate-200 dark:border-slate-700">
                <p className="text-xl font-bold text-slate-800 dark:text-slate-100 print:text-lg">
                  {format(new Date(selectedDate + 'T12:00:00'), "dd/MM/yyyy", { locale: ptBR })}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400 capitalize">
                  {format(new Date(selectedDate + 'T12:00:00'), "EEEE", { locale: ptBR })}
                </p>
                <p className="text-[10px] text-slate-400 dark:text-slate-500">
                  gerado em {format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </p>
              </div>

              {/* SERVIÇOS - OSs do dia com equipe prevista */}
              <div className="space-y-2">
                <h3 className="font-bold text-emerald-600 dark:text-emerald-400 text-xs uppercase tracking-wider">SERVIÇOS</h3>
                {ossDoDia.length > 0 ? (
                  <div className="space-y-3">
                    {ossDoDia.map(os => {
                      // Pegar equipe prevista da OS (só quem tem fase marcada p/ este dia)
                      const equipePrevista = (os.equipe || []).filter(ep => membroEsperadoNoDia(os, ep, selectedDate));
                      
                      // Horários do evento - tentar várias fontes
                      let horarioInicio = os.horario_inicio || os.horario || '';
                      let horarioFim = os.horario_fim || '';
                      
                      // Se não tem na OS, pegar do primeiro membro da equipe que tem horário
                      if (!horarioInicio && !horarioFim && equipePrevista.length > 0) {
                        const membroComHorario = equipePrevista.find(m => m.horario_inicio || m.horario_fim || m.horario);
                        if (membroComHorario) {
                          horarioInicio = membroComHorario.horario_inicio || membroComHorario.horario || '';
                          horarioFim = membroComHorario.horario_fim || '';
                        }
                      }
                      
                      // Cruzar com apontamentos do dia - quem da equipe bateu ponto
                      const escaladosRealizados = dayRecords.filter(r => 
                        r.type === 'colaborador' && 
                        equipePrevista.some(ep => ep.human_id === r.human_id)
                      );
                      
                      return (
                        <div key={os.id || os.numero} className="border-l-2 border-emerald-500 pl-2 space-y-1">
                          <div className="leading-tight">
                            <span className="font-semibold text-slate-800 dark:text-slate-100 text-sm">
                              OS {os.numero} - {os.cliente}
                            </span>
                            {os.local && (
                              <span className="block text-slate-500 text-xs leading-tight">
                                📍 {os.local}{os.servicos ? ` - ${os.servicos}` : ''}
                              </span>
                            )}
                          </div>
                          
                          {/* Horários inline compacto */}
                          <div className="flex flex-wrap gap-1 text-[10px]">
                            {(os.horario_inicio_montagem) && (
                              <span className="bg-orange-500/20 text-orange-700 dark:text-orange-300 px-1.5 py-0.5 rounded">
                                Mont: {os.horario_inicio_montagem}{os.horario_fim_montagem ? `-${os.horario_fim_montagem}` : ''}
                              </span>
                            )}
                            {(horarioInicio || horarioFim) && (
                              <span className="bg-amber-500/20 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded">
                                Ev: {horarioInicio || '?'}{horarioFim ? `-${horarioFim}` : ''}
                              </span>
                            )}
                          </div>
                          {/* Mensagem se não tiver horário */}
                          {!os.horario_inicio_montagem && !horarioInicio && (
                            <p className="text-[10px] text-slate-500 italic">Horário não informado</p>
                          )}
                          
                          {/* Equipe - Compacta */}
                          {equipePrevista.length > 0 ? (
                            <div className="mt-1">
                              <span className="font-medium text-xs text-slate-600 dark:text-slate-400">Equipe:</span>
                              <div className="mt-0.5 space-y-0.5 text-xs">
                                {equipePrevista.map(ep => {
                                  const colaborador = colaboradores.find(c => c.id === ep.human_id);
                                  const nomeColaborador = ep.nome || colaborador?.name || '?';
                                  const primeiroNome = nomeColaborador.split(' ').slice(0, 2).join(' ');
                                  
                                  const apontamento = dayRecords.find(r => 
                                    r.type === 'colaborador' && r.human_id === ep.human_id
                                  );
                                  const entrada = apontamento?.entry_time && apontamento.entry_time !== '--:--' ? apontamento.entry_time : null;
                                  const saida = apontamento?.exit_time && apontamento.exit_time !== '--:--' ? apontamento.exit_time : null;
                                  const bateuPonto = entrada || saida;
                                  
                                  // Verificar ressalva de início antecipado
                                  const funcaoLower = (ep.funcao || '').toLowerCase();
                                  const ehMontagemOuDesmontagem = funcaoLower.includes('montagem') || funcaoLower.includes('desmontagem');
                                  let ressalvaInicioAntecipado = null;
                                  
                                  if (!ehMontagemOuDesmontagem && entrada) {
                                    const parseTime = (t) => {
                                      if (!t) return null;
                                      const [h, m] = t.split(':').map(Number);
                                      return h * 60 + m;
                                    };
                                    const entradaMin = parseTime(entrada);
                                    const horarioMontagemInicio = os.horario_inicio_montagem;
                                    
                                    if (horarioMontagemInicio) {
                                      const montagemMin = parseTime(horarioMontagemInicio);
                                      if (montagemMin !== null && entradaMin !== null) {
                                        const diffMontagemMin = montagemMin - entradaMin;
                                        if (diffMontagemMin > 150) {
                                          const horasAntes = Math.floor(diffMontagemMin / 60);
                                          const minutosAntes = diffMontagemMin % 60;
                                          ressalvaInicioAntecipado = `${horasAntes}h${minutosAntes > 0 ? minutosAntes.toString().padStart(2, '0') : ''} antes mont.`;
                                        }
                                      }
                                    } else if (horarioInicio) {
                                      const eventoMin = parseTime(horarioInicio);
                                      if (eventoMin !== null && entradaMin !== null) {
                                        const diffMin = eventoMin - entradaMin;
                                        if (diffMin > 150) {
                                          const horasAntes = Math.floor(diffMin / 60);
                                          const minutosAntes = diffMin % 60;
                                          ressalvaInicioAntecipado = `${horasAntes}h${minutosAntes > 0 ? minutosAntes.toString().padStart(2, '0') : ''} antes ev.`;
                                        }
                                      }
                                    }
                                  }
                                  
                                  return (
                                    <div key={ep.human_id} className="flex flex-wrap items-center gap-x-1 leading-tight">
                                      <span className={bateuPonto ? "text-slate-700 dark:text-slate-300" : "text-slate-400 line-through"}>
                                        {primeiroNome}
                                      </span>
                                      {bateuPonto ? (
                                        <span className="text-green-600 dark:text-green-400">✓ {entrada}-{saida || '?'}</span>
                                      ) : (
                                        <span className="text-red-500">✗</span>
                                      )}
                                      {ressalvaInicioAntecipado && (
                                        <span className="text-orange-500 dark:text-orange-400">⚠{ressalvaInicioAntecipado}</span>
                                      )}
                                      {apontamento?.observations && (
                                        <span className="text-amber-600 dark:text-amber-400 text-[10px]">→ {apontamento.observations}</span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            <p className="text-[10px] text-slate-500 italic">Sem equipe cadastrada</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-slate-500 italic text-xs">Sem OS programada</p>
                )}
              </div>

              {/* APONTAMENTOS EXTRAS - Compacto */}
              {(() => {
                const idsPrevistosNaOS = ossDoDia.flatMap(os => (os.equipe || []).filter(ep => membroEsperadoNoDia(os, ep, selectedDate)).map(ep => ep.human_id));
                const temPrevisaoOS = idsPrevistosNaOS.length > 0;
                const colaboradoresQueApontaram = dayRecords.filter(r => 
                  r.type === 'colaborador' && (r.entry_time && r.entry_time !== '--:--')
                );
                const apontamentosParaMostrar = temPrevisaoOS 
                  ? colaboradoresQueApontaram.filter(r => !idsPrevistosNaOS.includes(r.human_id))
                  : colaboradoresQueApontaram;
                
                if (apontamentosParaMostrar.length === 0) return null;
                
                return (
                  <div className="space-y-1">
                    <h3 className={`font-bold text-xs uppercase tracking-wider ${temPrevisaoOS ? 'text-amber-500' : 'text-emerald-600 dark:text-emerald-400'}`}>
                      {temPrevisaoOS ? 'EXTRAS' : 'APONTAMENTOS'}
                    </h3>
                    {temPrevisaoOS && <p className="text-[10px] text-slate-500 italic">Não previstos na OS</p>}
                    <div className="space-y-0.5 text-xs">
                      {apontamentosParaMostrar.map(r => (
                        <div key={r.id} className="flex flex-wrap items-center gap-x-1 text-slate-700 dark:text-slate-300">
                          <span className="font-medium">{r.human_name?.split(' ').slice(0,2).join(' ')}</span>
                          <span className="text-slate-500">({r.entry_time || '?'}-{r.exit_time || '?'})</span>
                          {r.observations && <span className="text-amber-600 dark:text-amber-400 text-[10px]">→ {r.observations}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* TERCEIROS - Um por linha com valor */}
              <div className="space-y-1">
                <h3 className="font-bold text-emerald-600 dark:text-emerald-400 text-xs uppercase tracking-wider">TERCEIROS</h3>
                {terceirosRecords.length > 0 ? (
                  <div className="text-xs text-slate-700 dark:text-slate-300 space-y-0.5">
                    {terceirosRecords.map(t => (
                      <div key={t.id} className="flex justify-between items-center">
                        <span>• {t.human_name?.split(' ').slice(0, 2).join(' ')}</span>
                        {t.value > 0 && <span className="text-green-600 font-medium">R${t.value.toFixed(0)}</span>}
                      </div>
                    ))}
                    {terceirosRecords.some(t => t.value > 0) && (
                      <div className="flex justify-between items-center border-t border-slate-200 dark:border-slate-700 pt-1 mt-1 font-medium">
                        <span>Total:</span>
                        <span className="text-green-600">R${terceirosRecords.reduce((acc, t) => acc + (t.value || 0), 0).toFixed(0)}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-slate-500 text-xs">Sem</p>
                )}
              </div>
              
              {/* FALTAS - Funcionários que não bateram ponto */}
              {(() => {
                // Verificar se é dia útil (seg=1 a sex=5)
                const dataObj = new Date(selectedDate + 'T12:00:00');
                const diaSemana = dataObj.getDay(); // 0=dom, 1=seg, ..., 6=sab
                const ehFimDeSemana = diaSemana === 0 || diaSemana === 6;
                
                // Verificar se é feriado
                const ehFeriado = feriados.some(f => f.data === selectedDate);
                
                // Dia útil = não é fim de semana E não é feriado
                const ehDiaUtil = !ehFimDeSemana && !ehFeriado;
                
                // Pegar IDs de quem bateu ponto
                const idsBateramPonto = dayRecords
                  .filter(r => r.type === 'colaborador' && (r.entry_time && r.entry_time !== '--:--'))
                  .map(r => r.human_id);
                
                // Pegar IDs de quem tem justificativa válida
                // Valores: banco_horas (folga banco de horas), atestado
                const locaisJustificados = ['banco_horas', 'atestado'];
                const idsComJustificativa = dayRecords
                  .filter(r => r.type === 'colaborador' && (
                    locaisJustificados.includes(r.location) ||
                    locaisJustificados.includes(r.status)
                  ))
                  .map(r => r.human_id);
                
                let faltas = [];
                
                if (ehDiaUtil) {
                  // Dia útil: todos os CLT ativos devem trabalhar
                  // Filtrar colaboradores CLT ativos
                  const cltAtivos = colaboradores.filter(c => 
                    c.status === 'ativo' && 
                    (c.tipo_contrato === 'clt' || !c.tipo_contrato) // Se não tem tipo, assume CLT
                  );
                  
                  // Quem é CLT ativo e NÃO bateu ponto e NÃO tem justificativa = FALTA
                  faltas = cltAtivos
                    .filter(c => !idsBateramPonto.includes(c.id) && !idsComJustificativa.includes(c.id))
                    .map(c => ({
                      id: c.id,
                      nome: c.name,
                      os_numero: null,
                      motivo: 'Dia útil sem apontamento'
                    }));
                } else {
                  // Fim de semana OU feriado: apenas quem estava escalado na OS
                  const escaladosNasOss = ossDoDia.flatMap(os => (os.equipe || []).filter(ep => membroEsperadoNoDia(os, ep, selectedDate)).map(ep => ({
                    id: ep.human_id,
                    nome: ep.nome || colaboradores.find(c => c.id === ep.human_id)?.name || 'N/A',
                    os_numero: os.numero,
                    funcao: ep.funcao
                  })));
                  
                  // Quem estava escalado mas NÃO bateu ponto e NÃO tem justificativa = FALTA
                  faltas = escaladosNasOss
                    .filter(e => !idsBateramPonto.includes(e.id) && !idsComJustificativa.includes(e.id));
                  
                  // Remover duplicados
                  faltas = faltas.filter((f, i, arr) => arr.findIndex(x => x.id === f.id) === i);
                }
                
                if (faltas.length === 0) return null;
                
                return (
                  <div className="space-y-1">
                    <h3 className="font-bold text-red-600 dark:text-red-400 text-xs uppercase tracking-wider">⚠️ FALTAS ({faltas.length})</h3>
                    <div className="text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 p-2 rounded border border-red-200 dark:border-red-800">
                      {faltas.map((f, i) => {
                        // Buscar se tem observação/justificativa
                        const registro = dayRecords.find(r => r.human_id === f.id);
                        const obs = registro?.observations || '';
                        return (
                          <div key={f.id || i} className="flex flex-wrap items-center gap-1">
                            <span className="font-medium">{f.nome?.split(' ').slice(0, 2).join(' ')}</span>
                            <span className="text-red-500">- FALTA</span>
                            {f.os_numero && <span className="text-slate-500">(OS {f.os_numero})</span>}
                            {obs && <span className="text-amber-600 dark:text-amber-400">→ {obs}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* VEÍCULOS - Com valores de despesas */}
              <div className="space-y-1">
                <h3 className="font-bold text-emerald-600 dark:text-emerald-400 text-xs uppercase tracking-wider">VEÍCULOS</h3>
                {(veiculosEmpresa.length > 0 && Object.keys(vehicleData).some(k => vehicleData[k]?.km || vehicleData[k]?.rota)) || 
                 (vehicleExpenses?.fuel > 0 || vehicleExpenses?.maintenance > 0 || vehicleExpenses?.other > 0) ? (
                  <div className="text-xs text-slate-700 dark:text-slate-300 space-y-0.5">
                    {/* Mostrar veículos com km/rota */}
                    {veiculosEmpresa.filter(v => vehicleData[v.nome]?.km || vehicleData[v.nome]?.rota || vehicleData[v.id]?.km).map(v => {
                      const data = vehicleData[v.nome] || vehicleData[v.id] || {};
                      return (
                        <div key={v.id || v.nome} className="leading-tight">
                          <span className="font-medium">{v.nome}</span>
                          {data.km && <span> • {data.km}km</span>}
                          {data.rota && <span className="text-slate-500 text-[10px] block ml-2">↳ {data.rota}</span>}
                        </div>
                      );
                    })}
                    {/* Mostrar despesas se houver */}
                    {(vehicleExpenses?.fuel > 0 || vehicleExpenses?.maintenance > 0 || vehicleExpenses?.other > 0) && (
                      <div className="mt-1 pt-1 border-t border-slate-200 dark:border-slate-700 space-y-0.5">
                        {vehicleExpenses?.fuel > 0 && (
                          <div className="flex justify-between">
                            <span>Combustível:</span>
                            <span className="text-green-600 font-medium">R${vehicleExpenses.fuel.toFixed(2)}</span>
                          </div>
                        )}
                        {vehicleExpenses?.maintenance > 0 && (
                          <div className="flex justify-between">
                            <span>Manutenção:</span>
                            <span className="text-amber-600 font-medium">R${vehicleExpenses.maintenance.toFixed(2)}</span>
                          </div>
                        )}
                        {vehicleExpenses?.other > 0 && (
                          <div className="flex justify-between">
                            <span>Outros:</span>
                            <span className="text-slate-600 font-medium">R${vehicleExpenses.other.toFixed(2)}</span>
                          </div>
                        )}
                        {vehicleExpenses?.description && (
                          <p className="text-[10px] text-slate-500 italic">{vehicleExpenses.description}</p>
                        )}
                        <div className="flex justify-between font-medium pt-1 border-t border-slate-200 dark:border-slate-700">
                          <span>Total:</span>
                          <span className="text-blue-600">R${((vehicleExpenses?.fuel || 0) + (vehicleExpenses?.maintenance || 0) + (vehicleExpenses?.other || 0)).toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-slate-500 text-xs">Sem</p>
                )}
              </div>

              {/* REFEIÇÕES - Compacto */}
              <div className="space-y-1">
                <h3 className="font-bold text-emerald-600 dark:text-emerald-400 text-xs uppercase tracking-wider">REFEIÇÕES</h3>
                {refeicoesDia.fornecedores?.some(f => f.funcionarios?.length > 0) ? (
                  <div className="text-xs text-slate-700 dark:text-slate-300">
                    {refeicoesDia.fornecedores.filter(f => f.funcionarios?.length > 0).map(f => {
                      const nomes = f.funcionarios.map(fid => {
                        const colab = colaboradores.find(c => c.id === fid);
                        const terc = terceirosRecords.find(t => t.human_id === fid);
                        return colab?.name?.split(' ')[0] || terc?.human_name?.split(' ')[0] || '?';
                      }).join(', ');
                      return (
                        <div key={f.fornecedor_id}>
                          <span className="font-medium">{f.fornecedor_nome}</span>: {nomes}
                          <span className="text-slate-500"> (R${(f.funcionarios.length * f.valor_unitario).toFixed(0)})</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-slate-500 text-xs">Sem</p>
                )}
              </div>

              {/* TRANSPORTE - Agrupado por tipo */}
              <div className="space-y-1">
                <h3 className="font-bold text-emerald-600 dark:text-emerald-400 text-xs uppercase tracking-wider">TRANSPORTE</h3>
                {dayRecords.filter(r => (r.entry_time || r.exit_time) && r.type === 'colaborador').length > 0 ? (
                  (() => {
                    // Mapear transporte de cada funcionário
                    const transporteData = dayRecords
                      .filter(r => (r.entry_time || r.exit_time) && r.type === 'colaborador')
                      .map(r => {
                        const colaborador = colaboradores.find(c => c.id === r.human_id);
                        let tipoConducao = colaborador?.tipo_conducao || 'vt';
                        if (tipoConducao === 'valor_fixo') tipoConducao = 'veiculo_proprio';
                        
                        const ida = r.transporte_ida || r.transporte_tipo || tipoConducao;
                        const volta = r.transporte_volta || r.transporte_tipo || tipoConducao;
                        const nome = r.human_name?.split(' ')[0];
                        
                        return { nome, ida, volta };
                      });
                    
                    // Agrupar por combinação de transporte (ida-volta)
                    const grupos = {};
                    const labelMap = { 'vt': 'VT', 'uber': 'Uber', 'carona': 'Carona', 'veiculo_proprio': 'Próprio', 'valor_fixo': 'Próprio' };
                    
                    transporteData.forEach(t => {
                      const idaLabel = labelMap[t.ida] || 'VT';
                      const voltaLabel = labelMap[t.volta] || 'VT';
                      const key = t.ida === t.volta ? idaLabel : `${idaLabel}/${voltaLabel}`;
                      
                      if (!grupos[key]) grupos[key] = [];
                      grupos[key].push(t.nome);
                    });
                    
                    const colorMap = {
                      'VT': 'text-slate-600 dark:text-slate-400',
                      'Uber': 'text-purple-600 dark:text-purple-400',
                      'Carona': 'text-green-600 dark:text-green-400',
                      'Próprio': 'text-blue-600 dark:text-blue-400'
                    };
                    
                    return (
                      <div className="text-xs space-y-0.5">
                        {Object.entries(grupos).map(([tipo, nomes]) => {
                          const corPrincipal = colorMap[tipo.split('/')[0]] || colorMap['VT'];
                          return (
                            <div key={tipo} className="flex items-start gap-1">
                              <span className={`font-medium min-w-[50px] ${corPrincipal}`}>{tipo}:</span>
                              <span className="text-slate-700 dark:text-slate-300">{nomes.join(', ')}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()
                ) : (
                  <p className="text-slate-500 text-xs">Sem</p>
                )}
              </div>

              {/* VENCIMENTOS - Um abaixo do outro com valores */}
              <div className="space-y-1">
                <h3 className="font-bold text-emerald-600 dark:text-emerald-400 text-xs uppercase tracking-wider">VENCIMENTOS</h3>
                <div className="text-xs text-slate-700 dark:text-slate-300 space-y-2">
                  {/* Terceiros com vencimento */}
                  {terceirosRecords.filter(t => t.data_vencimento).length > 0 && (
                    <div className="space-y-0.5">
                      <span className="text-indigo-500 font-medium text-[10px] uppercase">Terceiros</span>
                      {terceirosRecords.filter(t => t.data_vencimento).map(t => (
                        <div key={t.id} className="flex justify-between items-center pl-2">
                          <span>• {t.human_name?.split(' ').slice(0, 2).join(' ')}</span>
                          <div className="flex gap-2">
                            <span className="text-indigo-600">{format(new Date(t.data_vencimento + 'T12:00:00'), 'dd/MM')}</span>
                            {t.value > 0 && <span className="text-green-600 font-medium">R${t.value.toFixed(0)}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Refeições com vencimento */}
                  {refeicoesDia.fornecedores?.filter(f => f.data_vencimento).length > 0 && (
                    <div className="space-y-0.5">
                      <span className="text-amber-500 font-medium text-[10px] uppercase">Refeições</span>
                      {refeicoesDia.fornecedores.filter(f => f.data_vencimento).map(f => {
                        const subtotal = (f.funcionarios?.length || 0) * (f.valor_unitario || 0) + (f.taxa_adicional || 0);
                        return (
                          <div key={f.fornecedor_id} className="flex justify-between items-center pl-2">
                            <span>• {f.fornecedor_nome}</span>
                            <div className="flex gap-2">
                              <span className="text-amber-600">{format(new Date(f.data_vencimento + 'T12:00:00'), 'dd/MM')}</span>
                              <span className="text-green-600 font-medium">R${subtotal.toFixed(0)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Reembolsos com vencimento */}
                  {reembolsosExcecoes.filter(r => r.data_vencimento).length > 0 && (
                    <div className="space-y-0.5">
                      <span className="text-orange-500 font-medium text-[10px] uppercase">Reembolsos</span>
                      {reembolsosExcecoes.filter(r => r.data_vencimento).map((r, i) => (
                        <div key={i} className="flex justify-between items-center pl-2">
                          <span>• {r.funcionario_nome?.split(' ').slice(0, 2).join(' ')} <span className="text-slate-500">({r.descricao?.substring(0,12)})</span></span>
                          <div className="flex gap-2">
                            <span className="text-orange-600">{format(new Date(r.data_vencimento + 'T12:00:00'), 'dd/MM')}</span>
                            <span className="text-green-600 font-medium">R${r.valor?.toFixed(0)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Mensagem se não houver vencimentos */}
                  {terceirosRecords.filter(t => t.data_vencimento).length === 0 && 
                   (!refeicoesDia.fornecedores || refeicoesDia.fornecedores.filter(f => f.data_vencimento).length === 0) &&
                   reembolsosExcecoes.filter(r => r.data_vencimento).length === 0 && (
                    <p className="text-slate-500">Sem vencimentos</p>
                  )}
                </div>
              </div>
              
              {/* REEMBOLSOS EXCEÇÕES - Se houver - Um por linha */}
              {reembolsosExcecoes.length > 0 && (
                <div className="space-y-1">
                  <h3 className="font-bold text-amber-600 dark:text-amber-400 text-xs uppercase tracking-wider">REEMBOLSOS EXCEÇÕES</h3>
                  <div className="text-xs text-slate-700 dark:text-slate-300 space-y-0.5">
                    {reembolsosExcecoes.map((r, i) => (
                      <div key={i} className="flex justify-between items-center">
                        <span>• {r.funcionario_nome?.split(' ').slice(0, 2).join(' ')}: {r.descricao?.substring(0, 20)}</span>
                        <span className="text-green-600 font-medium">R${r.valor?.toFixed(0)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center border-t border-amber-200 dark:border-amber-800 pt-1 mt-1 font-medium">
                      <span>Total:</span>
                      <span className="text-amber-600">R${totalReembolsosExcecoes.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* OBSERVAÇÕES GERAIS - Compacto */}
              {vehicleNotes && vehicleNotes.trim() && (
                <div className="space-y-1 border-t border-red-300 dark:border-red-700 pt-2">
                  <h3 className="font-bold text-red-600 dark:text-red-400 text-xs uppercase tracking-wider">OBS</h3>
                  <p className="text-xs text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{vehicleNotes}</p>
                </div>
              )}

              {/* Rodapé removido - relatório gerado agora fica no topo */}
            </CardContent>
            </div>{/* Fim da área capturável */}
          </Card>
        </TabsContent>

        {/* RESUMO FINAL TAB - Versão auditada com observações do admin */}
        <TabsContent value="resumo-final">
          <Card className="dark:bg-slate-900 dark:border-slate-700 print:shadow-none print:border-none">
            <CardHeader className="pb-2 print:pb-1">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2 dark:text-slate-100">
                  <CheckSquare className="w-5 h-5 text-blue-500 print:hidden" />
                  Resumo Final (Revisão)
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDownloadResumoImage('final')}
                  disabled={downloadingImage}
                  className="print:hidden"
                >
                  {downloadingImage ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  ) : (
                    <FileDown className="w-4 h-4 mr-1" />
                  )}
                  Baixar Imagem
                </Button>
              </div>
            </CardHeader>
            {/* Área capturável para imagem */}
            <div ref={resumoFinalRef} className="bg-white dark:bg-slate-900">
            <CardContent className="space-y-4 text-sm print:text-xs">
              {/* Data e Identificação */}
              <div className="text-center pb-3 border-b border-slate-200 dark:border-slate-700">
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                  {format(new Date(selectedDate + 'T12:00:00'), "dd/MM/yyyy", { locale: ptBR })}
                </p>
                <p className="text-lg text-slate-600 dark:text-slate-400 capitalize">
                  {format(new Date(selectedDate + 'T12:00:00'), "EEEE", { locale: ptBR })}
                </p>
                <p className="text-xs text-blue-500 dark:text-blue-400 mt-1 font-medium">
                  RESUMO FINAL - VERSÃO REVISADA
                </p>
              </div>

              {/* Lista de apontamentos com campo de observação de revisão */}
              <div className="space-y-3">
                <h3 className="font-bold text-slate-800 dark:text-blue-400 uppercase tracking-wide">APONTAMENTOS</h3>
                {dayRecords.filter(r => r.type === 'colaborador').length > 0 ? (
                  <div className="space-y-2">
                    {dayRecords.filter(r => r.type === 'colaborador').map(r => {
                      const entrada = r.entry_time && r.entry_time !== '--:--' ? r.entry_time : null;
                      const saida = r.exit_time && r.exit_time !== '--:--' ? r.exit_time : null;
                      
                      return (
                        <div key={r.id} className="p-2 bg-slate-50 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className="font-medium text-slate-800 dark:text-slate-200">{r.human_name}</span>
                            <span className="text-slate-500 text-xs">
                              {entrada || '--:--'} - {saida || '--:--'}
                            </span>
                            {r.os_number && (
                              <span className="text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-1.5 py-0.5 rounded">
                                OS {r.os_number}
                              </span>
                            )}
                          </div>
                          {/* Obs original do apontamento */}
                          {r.observations && (
                            <p className="text-xs text-slate-600 dark:text-slate-400 mb-2 italic">
                              Obs: {r.observations}
                            </p>
                          )}
                          {/* Campo de observação da revisão (admin) */}
                          <div className="flex gap-2 items-start">
                            <span className="text-xs text-blue-600 dark:text-blue-400 font-medium whitespace-nowrap">Revisão:</span>
                            <Input
                              type="text"
                              data-testid={`revisao-colaborador-${r.id}`}
                              value={draftValue(r.id, 'obs_auditoria', r.obs_auditoria)}
                              onChange={(e) => setDraft(r.id, 'obs_auditoria', e.target.value)}
                              onBlur={() => commitDraft(r.id, 'obs_auditoria', r.obs_auditoria)}
                              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                              placeholder="Observação do revisor..."
                              className="h-7 text-xs flex-1 dark:bg-slate-900 dark:border-slate-600"
                              disabled={user?.role !== 'admin' && user?.role !== 'super_admin' && user?.role !== 'diretoria'}
                            />
                          </div>
                          {/* Decisão estruturada da revisão (Fase 2) */}
                          <div className="flex flex-wrap gap-2 items-center mt-1.5">
                            <span className="text-[10px] text-teal-600 dark:text-teal-400 font-semibold uppercase whitespace-nowrap">Decisão:</span>
                            <select
                              data-testid={`revisao-decisao-${r.id}`}
                              value={r.revisao_decisao || ''}
                              onChange={(e) => handleUpdateRecord(r.id, { revisao_decisao: e.target.value })}
                              disabled={user?.role !== 'admin' && user?.role !== 'super_admin' && user?.role !== 'diretoria'}
                              className="h-6 text-[11px] rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-1"
                            >
                              <option value="">— sem intervenção (vale o apontamento) —</option>
                              <option value="horario_aceito">Horário informado aceito p/ cálculo</option>
                              <option value="debito_abonado">Débito de horas ABONADO</option>
                              <option value="falta_justificada">Falta JUSTIFICADA (sem desconto)</option>
                              <option value="atestado_aceito">ATESTADO aceito excepcionalmente (direção)</option>
                              <option value="desconto_mantido">Desconto MANTIDO</option>
                            </select>
                            <span className="text-[10px] text-rose-600 dark:text-rose-400 font-semibold uppercase whitespace-nowrap">Advertência:</span>
                            <select
                              data-testid={`revisao-advertencia-${r.id}`}
                              value={r.revisao_advertencia || ''}
                              onChange={(e) => handleUpdateRecord(r.id, { revisao_advertencia: e.target.value })}
                              disabled={user?.role !== 'admin' && user?.role !== 'super_admin' && user?.role !== 'diretoria'}
                              className="h-6 text-[11px] rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-1"
                            >
                              <option value="">— n/a —</option>
                              <option value="sim">SIM — advertir</option>
                              <option value="dispensada">Dispensada pela gestão</option>
                            </select>
                            <Input
                              type="text"
                              data-testid={`revisao-adv-motivo-${r.id}`}
                              value={draftValue(r.id, 'revisao_advertencia_motivo', r.revisao_advertencia_motivo)}
                              onChange={(e) => setDraft(r.id, 'revisao_advertencia_motivo', e.target.value)}
                              onBlur={() => commitDraft(r.id, 'revisao_advertencia_motivo', r.revisao_advertencia_motivo)}
                              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                              placeholder="Motivo (advertência/decisão)..."
                              className="h-6 text-[11px] flex-1 min-w-[140px] dark:bg-slate-900 dark:border-slate-600"
                              disabled={user?.role !== 'admin' && user?.role !== 'super_admin' && user?.role !== 'diretoria'}
                            />
                            {r.revisao_por && (
                              <span className="text-[9px] text-slate-400" data-testid={`revisao-autor-${r.id}`}>por {r.revisao_por}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">Nenhum apontamento</p>
                )}
              </div>

              {/* Terceiros com campo de revisão */}
              <div className="space-y-3">
                <h3 className="font-bold text-slate-800 dark:text-blue-400 uppercase tracking-wide">TERCEIROS</h3>
                {terceirosRecords.length > 0 ? (
                  <div className="space-y-2">
                    {terceirosRecords.map(t => (
                      <div key={t.id} className="p-2 bg-slate-50 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="font-medium text-slate-800 dark:text-slate-200">{t.human_name}</span>
                          <span className="text-slate-500 text-xs">R$ {t.value?.toFixed(2)}</span>
                          {t.service_description && (
                            <span className="text-xs text-slate-500">- {t.service_description}</span>
                          )}
                        </div>
                        {/* Campo de observação da revisão */}
                        <div className="flex gap-2 items-start">
                          <span className="text-xs text-blue-600 dark:text-blue-400 font-medium whitespace-nowrap">Revisão:</span>
                          <Input
                            type="text"
                            data-testid={`revisao-terceiro-${t.id}`}
                            value={draftValue(t.id, 'obs_auditoria', t.obs_auditoria)}
                            onChange={(e) => setDraft(t.id, 'obs_auditoria', e.target.value)}
                            onBlur={() => commitDraft(t.id, 'obs_auditoria', t.obs_auditoria)}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                            placeholder="Observação do revisor..."
                            className="h-7 text-xs flex-1 dark:bg-slate-900 dark:border-slate-600"
                            disabled={user?.role !== 'admin' && user?.role !== 'super_admin' && user?.role !== 'diretoria'}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">Sem terceiros</p>
                )}
              </div>

              {/* Observação geral da revisão */}
              <div className="space-y-2">
                <h3 className="font-bold text-slate-800 dark:text-blue-400 uppercase tracking-wide">PARECER GERAL DA REVISÃO</h3>
                <Textarea
                  value={vehicleNotes ? `${vehicleNotes}\n\n--- PARECER REVISÃO ---\n` : ''}
                  placeholder="Parecer geral do revisor sobre o dia..."
                  className="min-h-[80px] text-sm dark:bg-slate-800 dark:border-slate-600"
                  disabled={user?.role !== 'admin' && user?.role !== 'super_admin' && user?.role !== 'diretoria'}
                />
                <p className="text-xs text-slate-500">* Somente usuários Admin podem editar os campos de revisão</p>
              </div>
            </CardContent>
            </div>{/* Fim da área capturável do Resumo Final */}
          </Card>
        </TabsContent>

        {/* APONTAMENTOS TAB - Controle de Ponto dos Colaboradores */}
        <TabsContent value="apontamentos">
          <Card className="dark:bg-slate-800/50 dark:border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg flex items-center gap-2 dark:text-slate-100">
                <ClipboardList className="w-5 h-5 text-blue-500" />
                Apontamentos do Dia
              </CardTitle>
              {colaboradoresRecords.length === 0 && colaboradores.length > 0 && (
                <Button size="sm" onClick={initializeDayRecords} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                  <Plus className="w-4 h-4 mr-1" />
                  Inicializar Dia
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-2 sm:p-6">
              {dayRecords.length === 0 ? (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <ClipboardList className="w-12 h-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                  <p>Nenhum apontamento registrado</p>
                  <p className="text-sm mt-1">Clique em &quot;Inicializar Dia&quot; para criar os registros dos colaboradores</p>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-2 sm:mx-0">
                  {/* Filtro por funcionário e Legenda de cores */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3 px-2 sm:px-0">
                    {/* Filtro por funcionário */}
                    <div className="flex items-center gap-2">
                      <Search className="w-4 h-4 text-slate-400" />
                      <Select value={filterColaborador || 'all'} onValueChange={(v) => setFilterColaborador(v === 'all' ? '' : v)}>
                        <SelectTrigger className="h-8 w-48 text-xs dark:bg-slate-900 dark:border-slate-600">
                          <SelectValue placeholder="Filtrar funcionário..." />
                        </SelectTrigger>
                        <SelectContent className="dark:bg-slate-800 dark:border-slate-700 max-h-60">
                          <SelectItem value="all" className="text-xs dark:text-slate-200">Todos os funcionários</SelectItem>
                          {colaboradoresUnicos.map(nome => (
                            <SelectItem key={nome} value={nome} className="text-xs dark:text-slate-200">{nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {filterColaborador && (
                        <Button variant="ghost" size="sm" onClick={() => setFilterColaborador('')} className="h-8 px-2">
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                      {filterColaborador && (
                        <>
                          <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                            Filtro ativo
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/20"
                            onClick={() => {
                              const record = filteredColaboradorRecords[0];
                              if (record) {
                                generateRelatorioFuncionario(record.human_id, record.human_name);
                              }
                            }}
                          >
                            <FileDown className="w-3 h-3 mr-1" />
                            Relatório Mês
                          </Button>
                        </>
                      )}
                    </div>
                    
                    {/* Legenda de cores e Termo em Branco */}
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-slate-600 dark:text-slate-400">
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-sky-100 border border-sky-300"></div>
                        <Check className="w-3 h-3 text-sky-500" />
                        <span className="hidden sm:inline">Batido no</span> Ponto
                      </span>
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-orange-100 border border-orange-300"></div>
                        <Clock className="w-3 h-3 text-orange-500" />
                        <span className="hidden sm:inline">Manual</span>
                      </span>
                      {/* Controles de Visualização */}
                      <div className="flex items-center gap-1 ml-auto">
                        {/* Botões de modo de visualização */}
                        <div className="flex border rounded-md dark:border-slate-600">
                          <Button
                            variant={viewMode === 'compact' ? 'secondary' : 'ghost'}
                            size="sm"
                            className="h-7 px-2 rounded-r-none"
                            onClick={() => setViewMode('compact')}
                            title="Visualização compacta"
                          >
                            <Minimize2 className="w-3 h-3" />
                          </Button>
                          <Button
                            variant={viewMode === 'normal' ? 'secondary' : 'ghost'}
                            size="sm"
                            className="h-7 px-2 rounded-none border-x dark:border-slate-600"
                            onClick={() => setViewMode('normal')}
                            title="Visualização normal"
                          >
                            <Columns className="w-3 h-3" />
                          </Button>
                          <Button
                            variant={viewMode === 'expanded' ? 'secondary' : 'ghost'}
                            size="sm"
                            className="h-7 px-2 rounded-l-none"
                            onClick={() => setViewMode('expanded')}
                            title="Visualização expandida"
                          >
                            <Maximize2 className="w-3 h-3" />
                          </Button>
                        </div>
                        
                        {/* Menu de configuração de colunas */}
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="h-7 px-2 dark:border-slate-600">
                              <Settings2 className="w-3 h-3" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-2 dark:bg-slate-800 dark:border-slate-700" align="end">
                            <div className="text-xs font-medium mb-2 dark:text-slate-300">Colunas visíveis</div>
                            <div className="space-y-1">
                              {[
                                { key: 'pOficial', label: 'Ponto Oficial' },
                                { key: 'batidas', label: 'Batidas Extras' },
                                { key: 'transporte', label: 'Transporte' },
                                { key: 'servico', label: 'Serviço' },
                                { key: 'os', label: 'OS' },
                                { key: 'obs', label: 'Observações' },
                              ].map(col => (
                                <label key={col.key} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 p-1 rounded">
                                  <input
                                    type="checkbox"
                                    checked={visibleColumns[col.key]}
                                    onChange={(e) => setVisibleColumns({ ...visibleColumns, [col.key]: e.target.checked })}
                                    className="rounded border-slate-300 dark:border-slate-600"
                                  />
                                  <span className="dark:text-slate-300">{col.label}</span>
                                </label>
                              ))}
                            </div>
                            <div className="mt-2 pt-2 border-t dark:border-slate-700">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="w-full h-7 text-xs"
                                onClick={() => setVisibleColumns({
                                  nome: true, entrada: true, saida: true, pOficial: true, batidas: true,
                                  local: true, transporte: true, servico: true, os: true, obs: true, acoes: true
                                })}
                              >
                                Mostrar todas
                              </Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                  </div>
                  <Table className={`text-xs sm:text-sm ${viewMode === 'expanded' ? 'table-fixed' : ''}`}>
                    <TableHeader>
                      <TableRow className="bg-slate-50 dark:bg-slate-800">
                        <TableHead className={`dark:text-slate-300 text-xs ${viewMode === 'compact' ? 'min-w-[80px]' : viewMode === 'expanded' ? 'w-40' : 'min-w-[100px] sm:w-32'}`}>Nome</TableHead>
                        <TableHead className={`dark:text-slate-300 text-xs ${viewMode === 'compact' ? 'min-w-[70px]' : viewMode === 'expanded' ? 'w-28' : 'min-w-[90px] sm:w-28'}`} title="Azul = Ponto | Laranja = Manual">Entrada</TableHead>
                        <TableHead className={`dark:text-slate-300 text-xs ${viewMode === 'compact' ? 'min-w-[70px]' : viewMode === 'expanded' ? 'w-28' : 'min-w-[90px] sm:w-28'}`} title="Azul = Ponto | Laranja = Manual">Saída</TableHead>
                        {visibleColumns.pOficial && <TableHead className={`dark:text-slate-300 text-xs text-center ${viewMode === 'compact' ? 'min-w-[60px]' : viewMode === 'expanded' ? 'w-24' : 'min-w-[70px] sm:w-20'}`} title="Ponto Oficial (para conferência)">P.Oficial</TableHead>}
                        {visibleColumns.batidas && <TableHead className={`dark:text-slate-300 text-xs text-center ${viewMode === 'compact' ? 'min-w-[40px]' : 'min-w-[60px] sm:w-16'}`} title="Batidas extras (saída/retorno intermediário)">+Bat.</TableHead>}
                        {visibleColumns.obs && <TableHead className={`dark:text-slate-300 text-xs ${viewMode === 'compact' ? 'min-w-[100px]' : viewMode === 'expanded' ? 'w-64' : 'min-w-[150px] sm:min-w-[180px]'}`}>Obs.</TableHead>}
                        <TableHead className={`dark:text-slate-300 text-xs ${viewMode === 'compact' ? 'min-w-[60px]' : viewMode === 'expanded' ? 'w-28' : 'min-w-[70px] sm:w-24'}`}>Local</TableHead>
                        {visibleColumns.transporte && <TableHead className={`dark:text-slate-300 text-xs ${viewMode === 'compact' ? 'min-w-[70px]' : 'min-w-[80px] sm:w-24'}`} title="Tipo de transporte do dia">Transp.</TableHead>}
                        {visibleColumns.servico && <TableHead className={`dark:text-slate-300 text-xs ${viewMode === 'compact' ? 'min-w-[70px]' : viewMode === 'expanded' ? 'w-36' : 'min-w-[90px] sm:w-32'}`}>Serviço</TableHead>}
                        {visibleColumns.os && <TableHead className={`dark:text-slate-300 text-xs ${viewMode === 'compact' ? 'min-w-[50px]' : 'min-w-[60px] sm:w-20'}`}>OS</TableHead>}
                        {visibleColumns.os && <TableHead className={`dark:text-slate-300 text-xs ${viewMode === 'compact' ? 'min-w-[60px]' : 'min-w-[70px] sm:w-24'}`}>Local Ev.</TableHead>}
                        <TableHead className="dark:text-slate-300 min-w-[40px] sm:w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredColaboradorRecords.map(record => (
                        <TableRow key={record.id} className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 ${record.observations ? 'bg-amber-50/30 dark:bg-amber-900/10' : ''}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-medium">
                                {record.human_name?.charAt(0).toUpperCase() || '?'}
                              </div>
                              <span className="font-medium dark:text-slate-200">{record.human_name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {/* Para colaboradores sem entrada - aviso de falta de batida */}
                              {record.type === 'colaborador' && !record.entry_time && (
                                <div className="relative group">
                                  <AlertTriangle className="w-4 h-4 text-red-500 animate-pulse" />
                                  <div className="absolute left-0 top-6 z-50 w-64 p-2 bg-red-50 dark:bg-red-900/90 border border-red-200 dark:border-red-700 rounded shadow-lg text-xs text-red-700 dark:text-red-200 hidden group-hover:block">
                                    Não é possível fechar as horas por falta de batida. Vai aparecer no espelho do ponto: <strong>falta de batida</strong>.
                                  </div>
                                </div>
                              )}
                              <Input
                                type="time"
                                value={record.entry_time || ''}
                                onChange={(e) => handleUpdateRecord(record.id, { entry_time: e.target.value })}
                                className={`h-8 w-20 text-sm ${
                                  record.type !== 'colaborador'
                                    ? 'bg-slate-50 border-slate-300 dark:bg-slate-800 dark:border-slate-600'
                                    : !record.entry_time 
                                      ? 'bg-red-50 border-red-300 dark:bg-red-900/30 dark:border-red-700'
                                      : (record.entry_source || 'ponto') === 'ponto' 
                                        ? 'bg-sky-50 border-sky-300 dark:bg-sky-900/30 dark:border-sky-700' 
                                        : 'bg-orange-50 border-orange-300 dark:bg-orange-900/30 dark:border-orange-700'
                                }`}
                                title={record.type !== 'colaborador' ? 'Horário informado' : !record.entry_time ? 'Falta de batida!' : (record.entry_source || 'ponto') === 'ponto' ? 'Batido no ponto' : 'Horário manual'}
                              />
                              {/* Botão de toggle ponto/manual só para colaboradores */}
                              {record.type === 'colaborador' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={`h-6 w-6 p-0 ${
                                    (record.entry_source || 'ponto') === 'ponto' 
                                      ? 'text-sky-500 hover:text-sky-700' 
                                      : 'text-orange-500 hover:text-orange-700'
                                  }`}
                                  onClick={() => handleUpdateRecord(record.id, { 
                                    entry_source: (record.entry_source || 'ponto') === 'ponto' ? 'manual' : 'ponto',
                                    entry_justification: (record.entry_source || 'ponto') === 'ponto' ? '' : record.entry_justification
                                  })}
                                  title={(record.entry_source || 'ponto') === 'ponto' ? 'Clique para marcar como manual' : 'Clique para marcar como batido no ponto'}
                                >
                                  {(record.entry_source || 'ponto') === 'ponto' ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {/* Para colaboradores sem saída - aviso de falta de batida */}
                              {record.type === 'colaborador' && !record.exit_time && (
                                <div className="relative group">
                                  <AlertTriangle className="w-4 h-4 text-red-500 animate-pulse" />
                                  <div className="absolute left-0 top-6 z-50 w-64 p-2 bg-red-50 dark:bg-red-900/90 border border-red-200 dark:border-red-700 rounded shadow-lg text-xs text-red-700 dark:text-red-200 hidden group-hover:block">
                                    Não é possível fechar as horas por falta de batida. Vai aparecer no espelho do ponto: <strong>falta de batida</strong>.
                                  </div>
                                </div>
                              )}
                              <Input
                                type="time"
                                value={record.exit_time || ''}
                                onChange={(e) => handleUpdateRecord(record.id, { exit_time: e.target.value })}
                                className={`h-8 w-20 text-sm ${
                                  record.type !== 'colaborador'
                                    ? 'bg-slate-50 border-slate-300 dark:bg-slate-800 dark:border-slate-600'
                                    : !record.exit_time 
                                      ? 'bg-red-50 border-red-300 dark:bg-red-900/30 dark:border-red-700'
                                      : (record.exit_source || 'ponto') === 'ponto' 
                                        ? 'bg-sky-50 border-sky-300 dark:bg-sky-900/30 dark:border-sky-700' 
                                        : 'bg-orange-50 border-orange-300 dark:bg-orange-900/30 dark:border-orange-700'
                                }`}
                                title={record.type !== 'colaborador' ? 'Horário informado' : !record.exit_time ? 'Falta de batida!' : (record.exit_source || 'ponto') === 'ponto' ? 'Batido no ponto' : 'Horário manual'}
                              />
                              {/* Botão de toggle ponto/manual só para colaboradores */}
                              {record.type === 'colaborador' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={`h-6 w-6 p-0 ${
                                    (record.exit_source || 'ponto') === 'ponto' 
                                      ? 'text-sky-500 hover:text-sky-700' 
                                      : 'text-orange-500 hover:text-orange-700'
                                  }`}
                                  onClick={() => handleUpdateRecord(record.id, { 
                                    exit_source: (record.exit_source || 'ponto') === 'ponto' ? 'manual' : 'ponto',
                                    exit_justification: (record.exit_source || 'ponto') === 'ponto' ? '' : record.exit_justification
                                  })}
                                  title={(record.exit_source || 'ponto') === 'ponto' ? 'Clique para marcar como manual' : 'Clique para marcar como batido no ponto'}
                                >
                                  {(record.exit_source || 'ponto') === 'ponto' ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          {/* Coluna Ponto Oficial (para conferência de divergências) */}
                          {visibleColumns.pOficial && (
                          <TableCell className="text-center">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-1">
                                <Input
                                  type="time"
                                  value={record.entry_time_oficial || ''}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    const hasDivergence = newValue && record.entry_time && newValue !== record.entry_time;
                                    handleUpdateRecord(record.id, { 
                                      entry_time_oficial: newValue,
                                      has_divergence: hasDivergence || (record.exit_time_oficial && record.exit_time && record.exit_time_oficial !== record.exit_time)
                                    });
                                  }}
                                  className={`h-6 w-16 text-xs ${
                                    record.entry_time_oficial && record.entry_time && record.entry_time_oficial !== record.entry_time
                                      ? 'bg-red-50 border-red-300 dark:bg-red-900/30 dark:border-red-700'
                                      : 'bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-600'
                                  }`}
                                  placeholder="Ent."
                                  title="Entrada no ponto oficial"
                                />
                              </div>
                              <div className="flex items-center gap-1">
                                <Input
                                  type="time"
                                  value={record.exit_time_oficial || ''}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    const hasDivergence = newValue && record.exit_time && newValue !== record.exit_time;
                                    handleUpdateRecord(record.id, { 
                                      exit_time_oficial: newValue,
                                      has_divergence: hasDivergence || (record.entry_time_oficial && record.entry_time && record.entry_time_oficial !== record.entry_time)
                                    });
                                  }}
                                  className={`h-6 w-16 text-xs ${
                                    record.exit_time_oficial && record.exit_time && record.exit_time_oficial !== record.exit_time
                                      ? 'bg-red-50 border-red-300 dark:bg-red-900/30 dark:border-red-700'
                                      : 'bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-600'
                                  }`}
                                  placeholder="Saí."
                                  title="Saída no ponto oficial"
                                />
                              </div>
                              {record.has_divergence && (
                                <Badge variant="destructive" className="text-xs px-1">
                                  <AlertCircle className="w-3 h-3" />
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          )}
                          {/* Coluna de Batidas Extras */}
                          {visibleColumns.batidas && (
                          <TableCell className="text-center">
                            <div className="flex flex-col items-center gap-1">
                              {record.extra_entries && record.extra_entries.length > 0 && (
                                <Badge 
                                  variant="outline" 
                                  className="text-xs bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 cursor-pointer hover:bg-blue-100"
                                  onClick={() => {
                                    setEditingExtraRecord(record);
                                    setShowExtraEntries(true);
                                  }}
                                >
                                  {record.extra_entries.length}
                                </Badge>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                                onClick={() => {
                                  setEditingExtraRecord(record);
                                  setShowExtraEntries(true);
                                }}
                                title="Gerenciar batidas extras (saída/retorno)"
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                          )}
                          {/* Coluna de Obs. do Dia - MOVIDA para cá */}
                          {visibleColumns.obs && (
                          <TableCell>
                            <Textarea
                              data-testid={`obs-dia-${record.id}`}
                              value={draftValue(record.id, 'observations', record.observations)}
                              onChange={(e) => setDraft(record.id, 'observations', e.target.value)}
                              onBlur={() => commitDraft(record.id, 'observations', record.observations)}
                              placeholder="Observações do dia..."
                              className="min-h-[32px] h-8 text-xs dark:bg-slate-900 dark:border-slate-600 resize-none"
                              rows={1}
                            />
                          </TableCell>
                          )}
                          <TableCell>
                            <Select 
                              value={record.location || 'interno'} 
                              onValueChange={(v) => handleUpdateRecord(record.id, { location: v })}
                            >
                              <SelectTrigger className="h-8 w-28 text-xs dark:bg-slate-900 dark:border-slate-600">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                                <SelectItem value="interno" className="text-xs dark:text-slate-200">
                                  <span className="flex items-center gap-1"><Building className="w-3 h-3" /> Interno</span>
                                </SelectItem>
                                <SelectItem value="externo" className="text-xs dark:text-slate-200">
                                  <span className="flex items-center gap-1"><Truck className="w-3 h-3" /> Externo</span>
                                </SelectItem>
                                <SelectItem value="faltou" className="text-xs dark:text-slate-200">
                                  <span className="flex items-center gap-1"><X className="w-3 h-3" /> Faltou</span>
                                </SelectItem>
                                <SelectItem value="folga_abonada" className="text-xs dark:text-slate-200">
                                  <span className="flex items-center gap-1"><Home className="w-3 h-3" /> Folga Abonada</span>
                                </SelectItem>
                                <SelectItem value="folga" className="text-xs dark:text-slate-200">
                                  <span className="flex items-center gap-1"><Home className="w-3 h-3" /> Folga (legado)</span>
                                </SelectItem>
                                <SelectItem value="banco_horas" className="text-xs dark:text-slate-200">
                                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Banco Horas</span>
                                </SelectItem>
                                <SelectItem value="atestado" className="text-xs dark:text-slate-200">
                                  <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> Atestado Dia</span>
                                </SelectItem>
                                <SelectItem value="atestado_horas" className="text-xs dark:text-slate-200">
                                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Atestado Horas</span>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          {/* Coluna de Transporte - IDA e VOLTA */}
                          {visibleColumns.transporte && (
                          <TableCell>
                            {(() => {
                              // Buscar tipo_conducao do colaborador para usar como padrão
                              const colaborador = colaboradores.find(c => c.id === record.human_id);
                              const tipoPadrao = colaborador?.tipo_conducao === 'valor_fixo' ? 'veiculo_proprio' : (colaborador?.tipo_conducao || 'vt');
                              
                              const valorIda = record.transporte_ida || record.transporte_tipo || tipoPadrao;
                              const valorVolta = record.transporte_volta || record.transporte_tipo || tipoPadrao;
                              
                              return (
                            <div className="flex flex-col gap-1">
                              {/* IDA */}
                              <div className="flex items-center gap-1">
                                <span className="text-[10px] text-slate-400 w-6">IDA</span>
                                <Select 
                                  value={valorIda} 
                                  onValueChange={(v) => handleUpdateRecord(record.id, { transporte_ida: v })}
                                >
                                  <SelectTrigger className={`h-6 w-20 text-[10px] ${
                                    valorIda === 'uber' ? 'bg-purple-50 border-purple-300 dark:bg-purple-900/30' :
                                    valorIda === 'carona' ? 'bg-green-50 border-green-300 dark:bg-green-900/30' :
                                    valorIda === 'veiculo_proprio' ? 'bg-blue-50 border-blue-300 dark:bg-blue-900/30' :
                                    'dark:bg-slate-900 dark:border-slate-600'
                                  }`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                                    <SelectItem value="vt" className="text-xs">VT</SelectItem>
                                    <SelectItem value="uber" className="text-xs">Uber</SelectItem>
                                    <SelectItem value="carona" className="text-xs">Carona</SelectItem>
                                    <SelectItem value="veiculo_proprio" className="text-xs">Vl. Fixo</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              {/* VOLTA */}
                              <div className="flex items-center gap-1">
                                <span className="text-[10px] text-slate-400 w-6">VOL</span>
                                <Select 
                                  value={valorVolta} 
                                  onValueChange={(v) => handleUpdateRecord(record.id, { transporte_volta: v })}
                                >
                                  <SelectTrigger className={`h-6 w-20 text-[10px] ${
                                    valorVolta === 'uber' ? 'bg-purple-50 border-purple-300 dark:bg-purple-900/30' :
                                    valorVolta === 'carona' ? 'bg-green-50 border-green-300 dark:bg-green-900/30' :
                                    valorVolta === 'veiculo_proprio' ? 'bg-blue-50 border-blue-300 dark:bg-blue-900/30' :
                                    'dark:bg-slate-900 dark:border-slate-600'
                                  }`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                                    <SelectItem value="vt" className="text-xs">VT</SelectItem>
                                    <SelectItem value="uber" className="text-xs">Uber</SelectItem>
                                    <SelectItem value="carona" className="text-xs">Carona</SelectItem>
                                    <SelectItem value="veiculo_proprio" className="text-xs">Vl. Fixo</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              {/* Campo para quantidade manual de passagens VT */}
                              {(valorIda === 'vt' || valorVolta === 'vt') && (
                                <div className="flex items-center gap-1 mt-1">
                                  <span className="text-[10px] text-blue-500">Pass:</span>
                                  <Input
                                    type="number"
                                    min="0"
                                    max="10"
                                    value={record.passagens_vt_manual !== null && record.passagens_vt_manual !== undefined ? record.passagens_vt_manual : ''}
                                    onChange={(e) => {
                                      const val = e.target.value === '' ? null : parseInt(e.target.value);
                                      handleUpdateRecord(record.id, { passagens_vt_manual: val });
                                    }}
                                    placeholder={(valorIda === 'vt' ? 1 : 0) + (valorVolta === 'vt' ? 1 : 0)}
                                    className="h-5 w-12 text-[10px] dark:bg-slate-900 dark:border-slate-600 text-center"
                                    title="Quantidade de passagens VT usadas (deixe vazio para calcular automaticamente)"
                                  />
                                </div>
                              )}
                              {/* Campos extras para Uber */}
                              {(valorIda === 'uber' || valorVolta === 'uber') && (
                                <div className="flex gap-1 mt-1">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={record.transporte_valor_uber || ''}
                                    onChange={(e) => handleUpdateRecord(record.id, { transporte_valor_uber: parseFloat(e.target.value) || null })}
                                    placeholder="R$"
                                    className="h-5 w-14 text-[10px] dark:bg-slate-900 dark:border-slate-600"
                                    title="Valor do Uber"
                                  />
                                  <Input
                                    type="text"
                                    value={record.transporte_motivo_uber || ''}
                                    onChange={(e) => handleUpdateRecord(record.id, { transporte_motivo_uber: e.target.value })}
                                    placeholder="Motivo"
                                    className="h-5 w-16 text-[10px] dark:bg-slate-900 dark:border-slate-600"
                                    title="Motivo do Uber"
                                  />
                                </div>
                              )}
                            </div>
                              );
                            })()}
                          </TableCell>
                          )}
                          {visibleColumns.servico && (
                          <TableCell>
                            <Select 
                              value={record.service_type || 'evento'} 
                              onValueChange={(v) => handleUpdateRecord(record.id, { service_type: v })}
                            >
                              <SelectTrigger className="h-8 w-32 text-xs dark:bg-slate-900 dark:border-slate-600">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                                {tiposAtividade.length > 0 ? (
                                  tiposAtividade.map(tipo => (
                                    <SelectItem key={tipo.id} value={tipo.id} className="text-xs dark:text-slate-200">
                                      <span className="flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tipo.cor }}></span>
                                        {tipo.nome}
                                      </span>
                                    </SelectItem>
                                  ))
                                ) : (
                                  <>
                                    <SelectItem value="evento" className="text-xs dark:text-slate-200">Evento</SelectItem>
                                    <SelectItem value="montagem" className="text-xs dark:text-slate-200">Montagem</SelectItem>
                                    <SelectItem value="desmontagem" className="text-xs dark:text-slate-200">Desmontagem</SelectItem>
                                    <SelectItem value="manutencao" className="text-xs dark:text-slate-200">Manutenção</SelectItem>
                                    <SelectItem value="entrega" className="text-xs dark:text-slate-200">Entrega</SelectItem>
                                    <SelectItem value="retirada" className="text-xs dark:text-slate-200">Retirada</SelectItem>
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          )}
                          {visibleColumns.os && (
                          <TableCell>
                            <Select
                              value={record.os_number || '__none__'}
                              onValueChange={(value) => {
                                // Ao selecionar OS, preencher o local automaticamente
                                const realValue = value === '__none__' ? '' : value;
                                const osSelecionada = todasOss.find(os => os.numero === realValue);
                                const updates = { os_number: realValue };
                                if (osSelecionada && osSelecionada.local) {
                                  updates.event_location = osSelecionada.local;
                                }
                                handleUpdateRecord(record.id, updates);
                              }}
                            >
                              <SelectTrigger className="h-8 w-28 text-xs dark:bg-slate-900 dark:border-slate-600">
                                <SelectValue placeholder="OS" />
                              </SelectTrigger>
                              <SelectContent className="dark:bg-slate-800 dark:border-slate-700 max-h-72 overflow-y-auto">
                                <SelectItem value="__none__" className="text-xs dark:text-slate-400">Nenhuma</SelectItem>
                                {/* OSs do dia destacadas primeiro */}
                                {ossDoDia.length > 0 && (
                                  <>
                                    <div className="px-2 py-1 text-[10px] text-emerald-400 font-semibold border-b border-slate-700">📅 OSs do Dia</div>
                                    {ossDoDia.map(os => (
                                      <SelectItem key={os.numero} value={os.numero} className="text-xs text-emerald-300 font-medium">
                                        #{os.numero} - {os.cliente?.substring(0, 12) || os.servicos?.substring(0, 12) || 'OS'}
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                                {/* Todas as outras OSs */}
                                {todasOss.filter(os => !ossDoDia.some(osd => osd.numero === os.numero)).length > 0 && (
                                  <>
                                    <div className="px-2 py-1 text-[10px] text-slate-400 font-semibold border-b border-slate-700 mt-1">📋 Outras OSs</div>
                                    {todasOss
                                      .filter(os => !ossDoDia.some(osd => osd.numero === os.numero))
                                      .map(os => (
                                        <SelectItem key={os.numero} value={os.numero} className="text-xs dark:text-slate-300">
                                          #{os.numero} - {os.cliente?.substring(0, 12) || os.servicos?.substring(0, 12) || 'OS'}
                                        </SelectItem>
                                      ))}
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          )}
                          {visibleColumns.os && (
                          <TableCell>
                            <Input
                              value={record.event_location || ''}
                              onChange={(e) => handleUpdateRecord(record.id, { event_location: e.target.value })}
                              placeholder="Local"
                              className="h-8 w-24 text-xs dark:bg-slate-900 dark:border-slate-600"
                            />
                          </TableCell>
                          )}
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-slate-400 hover:text-blue-600"
                                onClick={() => openEditRecord(record)}
                                title="Editar registro"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {(record.entry_source === 'manual' || record.exit_source === 'manual') && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-orange-400 hover:text-orange-600"
                                  onClick={() => generateTermoOcorrencia(record)}
                                  title="Imprimir Documento de Ocorrência"
                                >
                                  <Printer className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TERCEIROS TAB */}
        <TabsContent value="terceiros">
          <Card className="dark:bg-slate-800/50 dark:border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg flex items-center gap-2 dark:text-slate-100">
                <Briefcase className="w-5 h-5 text-indigo-500" />
                Terceiros do Dia
              </CardTitle>
              <Button size="sm" onClick={() => setShowAddWorker(true)}>
                <Plus className="w-4 h-4 mr-1" />
                Adicionar
              </Button>
            </CardHeader>
            <CardContent>
              {terceirosRecords.length === 0 ? (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <Briefcase className="w-12 h-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                  <p>Nenhum terceiro escalado para hoje</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 dark:bg-slate-800">
                        <TableHead className="dark:text-slate-300">Nome</TableHead>
                        <TableHead className="dark:text-slate-300">Serviço</TableHead>
                        <TableHead className="dark:text-slate-300">OS</TableHead>
                        <TableHead className="dark:text-slate-300">Valor</TableHead>
                        <TableHead className="dark:text-slate-300">Vencimento</TableHead>
                        <TableHead className="dark:text-slate-300">Fechado por</TableHead>
                        <TableHead className="dark:text-slate-300 w-20">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {terceirosRecords.map(record => (
                        <TableRow key={record.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium ${record.is_strategic ? 'bg-blue-500' : 'bg-indigo-500'}`}>
                                {record.human_name?.charAt(0).toUpperCase() || '?'}
                              </div>
                              <div>
                                <span className="font-medium dark:text-slate-200">{record.human_name}</span>
                                {record.is_strategic && (
                                  <Badge className="ml-2 text-[9px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                    Diretoria
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <Badge variant="outline" className="text-xs dark:border-slate-600 dark:text-slate-300">
                                {getServiceTypeLabel(record.service_type)}
                              </Badge>
                              {record.service_description && (
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{record.service_description}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm dark:text-slate-300">
                            {record.os_number || '-'}
                          </TableCell>
                          <TableCell>
                            {record.value_type === 'acertado_diretoria' ? (
                              <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-400">
                                Acertado c/ Diretoria
                              </Badge>
                            ) : (
                              <span className="font-bold text-green-600 dark:text-green-400">
                                R$ {(record.value || 0).toFixed(2)}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {record.data_vencimento ? (
                              <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                                {format(new Date(record.data_vencimento + 'T12:00:00'), 'dd/MM/yyyy')}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-slate-500 dark:text-slate-400">
                            {record.closed_by_name}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-slate-400 hover:text-blue-600"
                                onClick={() => openEditRecord(record)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-slate-400 hover:text-red-600"
                                onClick={() => handleRemoveRecord(record.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {/* Total */}
                  <div className="flex justify-between items-center pt-4 border-t dark:border-slate-700 mt-4">
                    <span className="font-medium text-slate-700 dark:text-slate-300">Total a Pagar Terceiros:</span>
                    <span className="text-xl font-bold text-green-600 dark:text-green-400">
                      R$ {totalTerceiros.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Seção de Reembolsos Exceções */}
              <div className="mt-6 pt-6 border-t dark:border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-slate-800 dark:text-slate-200 flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-amber-500" />
                    Reembolsos Exceções
                    <span className="text-xs text-slate-500">(transporte, materiais, etc.)</span>
                  </h3>
                  <Button size="sm" variant="outline" onClick={() => setShowAddReembolsoExcecao(true)}>
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar
                  </Button>
                </div>
                
                {reembolsosExcecoes.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-4">Nenhum reembolso de exceção registrado</p>
                ) : (
                  <div className="space-y-2">
                    {reembolsosExcecoes.map((r, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-800 dark:text-slate-200">{r.funcionario_nome}</span>
                            <Badge variant="outline" className="text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400">
                              {r.categoria === 'transporte' ? 'Transporte' : r.categoria === 'material' ? 'Material' : 'Exceção'}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-400">{r.descricao}</p>
                          {r.data_vencimento && (
                            <p className="text-xs text-indigo-600 dark:text-indigo-400">
                              Venc: {format(new Date(r.data_vencimento + 'T12:00:00'), 'dd/MM/yyyy')}
                            </p>
                          )}
                          {r.observacoes && (
                            <p className="text-xs text-slate-500">{r.observacoes}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-green-600 dark:text-green-400">R$ {r.valor?.toFixed(2)}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30"
                            onClick={() => removeReembolsoExcecao(index)}
                            disabled={savingReembolsosExcecoes}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    
                    {/* Total Reembolsos Exceções */}
                    <div className="flex justify-between items-center pt-3 border-t dark:border-slate-700">
                      <span className="font-medium text-slate-700 dark:text-slate-300">Total Reembolsos Exceções:</span>
                      <span className="text-lg font-bold text-amber-600 dark:text-amber-400">
                        R$ {totalReembolsosExcecoes.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* VEÍCULOS/CONTELE TAB */}
        <TabsContent value="veiculos">
          <Card className="dark:bg-slate-800/50 dark:border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 dark:text-slate-100">
                <Truck className="w-5 h-5 text-cyan-500" />
                Veículos / Contele
              </CardTitle>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Registre o resumo diário dos veículos (dados do Contele) e gastos
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Tabela de veículos simplificada */}
                <div className="border rounded-lg dark:border-slate-700 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 dark:bg-slate-800">
                        <TableHead className="dark:text-slate-300 w-28">Veículo</TableHead>
                        <TableHead className="dark:text-slate-300 w-24">KM Rodado</TableHead>
                        <TableHead className="dark:text-slate-300">Rota (copie do Contele)</TableHead>
                        <TableHead className="dark:text-slate-300">Observação</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(veiculosEmpresa.length > 0 ? veiculosEmpresa : [
                        { id: 'ducato', nome: 'Ducato' },
                        { id: 'fiorino', nome: 'Fiorino' },
                        { id: 'kombi', nome: 'Kombi' },
                        { id: 'iveco', nome: 'Iveco' },
                        { id: 'outro', nome: 'Outro' }
                      ]).map((veiculo) => (
                        <TableRow key={veiculo.id || veiculo.nome}>
                          <TableCell className="font-medium dark:text-slate-200">
                            {veiculo.nome}
                            {veiculo.placa && (
                              <span className="text-xs text-slate-400 ml-1">({veiculo.placa})</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              placeholder="0"
                              value={vehicleData[veiculo.nome]?.km || vehicleData[veiculo.id]?.km || ''}
                              onChange={(e) => setVehicleData({
                                ...vehicleData,
                                [veiculo.nome]: { ...vehicleData[veiculo.nome], km: e.target.value }
                              })}
                              className="h-8 w-20 text-sm dark:bg-slate-900 dark:border-slate-600"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              placeholder="Ex: Depósito → Centro → Cliente X → Retorno"
                              value={vehicleData[veiculo.nome]?.rota || vehicleData[veiculo.id]?.rota || ''}
                              onChange={(e) => setVehicleData({
                                ...vehicleData,
                                [veiculo.nome]: { ...vehicleData[veiculo.nome], rota: e.target.value }
                              })}
                              className="h-8 text-sm dark:bg-slate-900 dark:border-slate-600"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              placeholder="Ex: Foi para conserto, troca de pneu..."
                              value={vehicleData[veiculo.nome]?.obs || vehicleData[veiculo.id]?.obs || ''}
                              onChange={(e) => setVehicleData({
                                ...vehicleData,
                                [veiculo.nome]: { ...vehicleData[veiculo.nome], obs: e.target.value }
                              })}
                              className="h-8 text-sm dark:bg-slate-900 dark:border-slate-600"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Gastos do dia */}
                <div className="border-t pt-4 dark:border-slate-700">
                  <h3 className="font-medium text-slate-800 dark:text-slate-200 mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-emerald-500" />
                    Gastos do Dia (Veículos)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="dark:text-slate-300">Combustível (R$)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0,00"
                        value={vehicleExpenses?.fuel || ''}
                        onChange={(e) => setVehicleExpenses({ ...vehicleExpenses, fuel: parseFloat(e.target.value) || 0 })}
                        className="mt-1 dark:bg-slate-900 dark:border-slate-600"
                      />
                    </div>
                    <div>
                      <Label className="dark:text-slate-300">Manutenção (R$)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0,00"
                        value={vehicleExpenses?.maintenance || ''}
                        onChange={(e) => setVehicleExpenses({ ...vehicleExpenses, maintenance: parseFloat(e.target.value) || 0 })}
                        className="mt-1 dark:bg-slate-900 dark:border-slate-600"
                      />
                    </div>
                    <div>
                      <Label className="dark:text-slate-300">Outros Gastos (R$)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0,00"
                        value={vehicleExpenses?.other || ''}
                        onChange={(e) => setVehicleExpenses({ ...vehicleExpenses, other: parseFloat(e.target.value) || 0 })}
                        className="mt-1 dark:bg-slate-900 dark:border-slate-600"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label className="dark:text-slate-300">Descrição dos Gastos</Label>
                    <Textarea
                      placeholder="Descreva os gastos: onde abasteceu, qual manutenção foi feita, etc."
                      value={vehicleExpenses?.description || ''}
                      onChange={(e) => setVehicleExpenses({ ...vehicleExpenses, description: e.target.value })}
                      className="mt-1 min-h-[80px] dark:bg-slate-900 dark:border-slate-600"
                    />
                  </div>
                </div>

                {/* Resumo texto livre */}
                <div className="border-t pt-4 dark:border-slate-700">
                  <Label className="dark:text-slate-300">Observações Gerais do Dia (opcional)</Label>
                  <Textarea
                    value={vehicleNotes}
                    onChange={(e) => setVehicleNotes(e.target.value)}
                    placeholder="Qualquer outra informação relevante sobre veículos..."
                    className="min-h-[60px] mt-1 dark:bg-slate-900 dark:border-slate-600"
                  />
                </div>
                
                <div className="flex justify-end">
                  <Button onClick={handleSaveVehicleNotes} disabled={savingVehicleNotes}>
                    {savingVehicleNotes ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Salvar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* REFEIÇÕES TAB */}
        <TabsContent value="refeicoes">
          <Card className="dark:bg-slate-800/50 dark:border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 dark:text-slate-100">
                <Utensils className="w-5 h-5 text-amber-500" />
                Controle de Refeições por Fornecedor
              </CardTitle>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Registre nominalmente quem comeu onde e o valor pago a cada fornecedor
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* Resumo Financeiro Totais */}
              {(() => {
                const totais = calcularTotaisRefeicoes();
                return (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <div className="text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total Refeições</p>
                      <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{totais.total_refeicoes}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Fornecedores</p>
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">R$ {totais.total_valor_refeicoes.toFixed(2)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Taxas/Adicionais</p>
                      <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">R$ {totais.total_taxas_adicionais.toFixed(2)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Reembolsos</p>
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">R$ {totais.total_reembolsos.toFixed(2)}</p>
                    </div>
                    <div className="text-center border-l-2 border-amber-300 dark:border-amber-700 pl-3">
                      <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total Geral</p>
                      <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">R$ {totais.total_geral.toFixed(2)}</p>
                    </div>
                  </div>
                );
              })()}

              {/* Adicionar Fornecedor */}
              <div className="border-t pt-4 dark:border-slate-700">
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Adicionar Fornecedor ao Dia
                </h4>
                {fornecedoresRefeicao.length === 0 ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400 italic">
                    Nenhum fornecedor cadastrado. Cadastre fornecedores em Humanas → Fornecedores.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {fornecedoresRefeicao
                      .filter(f => !refeicoesDia.fornecedores.some(rf => rf.fornecedor_id === f.id))
                      .map(f => (
                        <Button
                          key={f.id}
                          variant="outline"
                          size="sm"
                          onClick={() => addFornecedorAoDia(f)}
                          className="dark:border-slate-600 dark:hover:bg-slate-700"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          {f.nome} (R$ {(f.valor_unitario || 0).toFixed(2)})
                        </Button>
                      ))}
                  </div>
                )}
              </div>

              {/* Fornecedores do Dia */}
              {refeicoesDia.fornecedores.length > 0 && (
                <div className="space-y-4">
                  {refeicoesDia.fornecedores.map((forn) => {
                    const subtotal = forn.funcionarios.length * forn.valor_unitario;
                    return (
                      <div key={forn.fornecedor_id} className="border dark:border-slate-700 rounded-lg p-4 bg-slate-50/50 dark:bg-slate-900/30">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <Package className="w-5 h-5 text-amber-500" />
                            <span className="font-semibold text-slate-800 dark:text-slate-200">{forn.fornecedor_nome}</span>
                            <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                              {forn.funcionarios.length} refeição(ões)
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-slate-500">R$/un:</Label>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={forn.valor_unitario || 0}
                                onChange={(e) => updateFornecedorValor(forn.fornecedor_id, e.target.value)}
                                className="w-24 h-8 text-sm dark:bg-slate-800 dark:border-slate-600"
                              />
                            </div>
                            <div className="px-3 py-1 bg-green-100 dark:bg-green-900/30 rounded text-sm font-semibold text-green-700 dark:text-green-400">
                              Subtotal: R$ {(subtotal + (forn.taxa_adicional || 0)).toFixed(2)}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFornecedorDoDia(forn.fornecedor_id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Taxa de Entrega / Adicional + Vencimento + Forma de Pagamento */}
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3 p-3 bg-slate-100 dark:bg-slate-800/50 rounded-lg">
                          <div>
                            <Label className="text-xs text-slate-500">Taxa/Adicional (R$)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={forn.taxa_adicional || ''}
                              onChange={(e) => {
                                const valor = parseFloat(e.target.value) || 0;
                                setRefeicoesDia(prev => ({
                                  ...prev,
                                  fornecedores: prev.fornecedores.map(f => 
                                    f.fornecedor_id === forn.fornecedor_id 
                                      ? { ...f, taxa_adicional: valor }
                                      : f
                                  )
                                }));
                              }}
                              placeholder="0.00"
                              className="h-8 text-sm dark:bg-slate-900 dark:border-slate-600"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Justificativa</Label>
                            <Input
                              value={forn.justificativa_adicional || ''}
                              onChange={(e) => {
                                setRefeicoesDia(prev => ({
                                  ...prev,
                                  fornecedores: prev.fornecedores.map(f => 
                                    f.fornecedor_id === forn.fornecedor_id 
                                      ? { ...f, justificativa_adicional: e.target.value }
                                      : f
                                  )
                                }));
                              }}
                              placeholder="Taxa entrega, embalagem..."
                              className="h-8 text-sm dark:bg-slate-900 dark:border-slate-600"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Vencimento</Label>
                            <Input
                              type="date"
                              value={forn.data_vencimento || ''}
                              onChange={(e) => {
                                setRefeicoesDia(prev => ({
                                  ...prev,
                                  fornecedores: prev.fornecedores.map(f => 
                                    f.fornecedor_id === forn.fornecedor_id 
                                      ? { ...f, data_vencimento: e.target.value }
                                      : f
                                  )
                                }));
                              }}
                              className="h-8 text-sm dark:bg-slate-900 dark:border-slate-600"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Forma Pagamento</Label>
                            <Select
                              value={forn.forma_pagamento || 'pix'}
                              onValueChange={(v) => {
                                setRefeicoesDia(prev => ({
                                  ...prev,
                                  fornecedores: prev.fornecedores.map(f => 
                                    f.fornecedor_id === forn.fornecedor_id 
                                      ? { ...f, forma_pagamento: v }
                                      : f
                                  )
                                }));
                              }}
                            >
                              <SelectTrigger className="h-8 text-sm dark:bg-slate-900 dark:border-slate-600">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="dark:bg-slate-800">
                                <SelectItem value="pix">PIX</SelectItem>
                                <SelectItem value="dinheiro">Dinheiro</SelectItem>
                                <SelectItem value="cartao">Cartão</SelectItem>
                                <SelectItem value="boleto">Boleto</SelectItem>
                                <SelectItem value="transferencia">Transferência</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Status</Label>
                            <Select
                              value={forn.status_pagamento || 'pendente'}
                              onValueChange={(v) => {
                                setRefeicoesDia(prev => ({
                                  ...prev,
                                  fornecedores: prev.fornecedores.map(f => 
                                    f.fornecedor_id === forn.fornecedor_id 
                                      ? { ...f, status_pagamento: v }
                                      : f
                                  )
                                }));
                              }}
                            >
                              <SelectTrigger className="h-8 text-sm dark:bg-slate-900 dark:border-slate-600">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="dark:bg-slate-800">
                                <SelectItem value="pendente">Pendente</SelectItem>
                                <SelectItem value="pago">Pago</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* Lista de funcionários E terceiros - Grid de checkboxes */}
                        <div className="mt-3">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                              Selecione quem comeu neste fornecedor (colaboradores e terceiros):
                            </p>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  // Marcar funcionários da escala do dia (colaboradores + terceiros)
                                  const idsNaEscala = dayRecords.map(r => r.human_id);
                                  const colabDisponiveis = colaboradores
                                    .filter(c => idsNaEscala.includes(c.id))
                                    .filter(c => !refeicoesDia.fornecedores
                                      .filter(f => f.fornecedor_id !== forn.fornecedor_id)
                                      .some(f => f.funcionarios.includes(c.id)))
                                    .map(c => c.id);
                                  const tercDisponiveis = terceirosRecords
                                    .filter(t => !refeicoesDia.fornecedores
                                      .filter(f => f.fornecedor_id !== forn.fornecedor_id)
                                      .some(f => f.funcionarios.includes(t.human_id)))
                                    .map(t => t.human_id);
                                  setRefeicoesDia(prev => ({
                                    ...prev,
                                    fornecedores: prev.fornecedores.map(f => 
                                      f.fornecedor_id === forn.fornecedor_id 
                                        ? { ...f, funcionarios: [...colabDisponiveis, ...tercDisponiveis] }
                                        : f
                                    )
                                  }));
                                }}
                                className="text-xs h-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                              >
                                <Users className="w-3 h-3 mr-1" />
                                Escala ({dayRecords.length + terceirosRecords.length})
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  // Marcar todos disponíveis (colaboradores + terceiros)
                                  const colabDisponiveis = colaboradores
                                    .filter(c => !refeicoesDia.fornecedores
                                      .filter(f => f.fornecedor_id !== forn.fornecedor_id)
                                      .some(f => f.funcionarios.includes(c.id)))
                                    .map(c => c.id);
                                  const tercDisponiveis = terceirosRecords
                                    .filter(t => !refeicoesDia.fornecedores
                                      .filter(f => f.fornecedor_id !== forn.fornecedor_id)
                                      .some(f => f.funcionarios.includes(t.human_id)))
                                    .map(t => t.human_id);
                                  setRefeicoesDia(prev => ({
                                    ...prev,
                                    fornecedores: prev.fornecedores.map(f => 
                                      f.fornecedor_id === forn.fornecedor_id 
                                        ? { ...f, funcionarios: [...colabDisponiveis, ...tercDisponiveis] }
                                        : f
                                    )
                                  }));
                                }}
                                className="text-xs h-7 text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
                              >
                                <CheckSquare className="w-3 h-3 mr-1" />
                                Todos
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setRefeicoesDia(prev => ({
                                    ...prev,
                                    fornecedores: prev.fornecedores.map(f => 
                                      f.fornecedor_id === forn.fornecedor_id 
                                        ? { ...f, funcionarios: [] }
                                        : f
                                    )
                                  }));
                                }}
                                className="text-xs h-7 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700"
                              >
                                <XSquare className="w-3 h-3 mr-1" />
                                Limpar
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 max-h-64 overflow-y-auto p-2 bg-white dark:bg-slate-800 rounded border dark:border-slate-700">
                            {/* Colaboradores CLT */}
                            {colaboradores.map(colab => {
                              const isSelected = forn.funcionarios.includes(colab.id);
                              // Verificar se já está em outro fornecedor
                              const emOutroFornecedor = refeicoesDia.fornecedores
                                .filter(f => f.fornecedor_id !== forn.fornecedor_id)
                                .some(f => f.funcionarios.includes(colab.id));
                              
                              return (
                                <div
                                  key={colab.id}
                                  className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-all ${
                                    isSelected 
                                      ? 'bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700' 
                                      : emOutroFornecedor
                                        ? 'bg-slate-100 dark:bg-slate-700/50 opacity-50'
                                        : 'hover:bg-slate-100 dark:hover:bg-slate-700'
                                  }`}
                                  onClick={() => !emOutroFornecedor && toggleFuncionarioFornecedor(forn.fornecedor_id, colab.id, colab.name)}
                                >
                                  <Checkbox
                                    checked={isSelected}
                                    disabled={emOutroFornecedor}
                                    className="pointer-events-none"
                                  />
                                  <span className={`text-sm truncate ${isSelected ? 'font-medium text-amber-800 dark:text-amber-300' : 'text-slate-700 dark:text-slate-300'}`}>
                                    {colab.name}
                                  </span>
                                  {emOutroFornecedor && (
                                    <span className="text-[10px] text-slate-400">(outro)</span>
                                  )}
                                </div>
                              );
                            })}
                            
                            {/* Terceiros escalados no dia */}
                            {terceirosRecords.length > 0 && (
                              <>
                                <div className="col-span-full border-t border-slate-200 dark:border-slate-600 my-2 pt-2">
                                  <span className="text-xs text-indigo-500 dark:text-indigo-400 font-medium">TERCEIROS DO DIA</span>
                                </div>
                                {terceirosRecords.map(terc => {
                                  const isSelected = forn.funcionarios.includes(terc.human_id);
                                  const emOutroFornecedor = refeicoesDia.fornecedores
                                    .filter(f => f.fornecedor_id !== forn.fornecedor_id)
                                    .some(f => f.funcionarios.includes(terc.human_id));
                                  
                                  return (
                                    <div
                                      key={terc.id}
                                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-all ${
                                        isSelected 
                                          ? 'bg-indigo-100 dark:bg-indigo-900/30 border border-indigo-300 dark:border-indigo-700' 
                                          : emOutroFornecedor
                                            ? 'bg-slate-100 dark:bg-slate-700/50 opacity-50'
                                            : 'hover:bg-slate-100 dark:hover:bg-slate-700'
                                      }`}
                                      onClick={() => !emOutroFornecedor && toggleFuncionarioFornecedor(forn.fornecedor_id, terc.human_id, terc.human_name)}
                                    >
                                      <Checkbox
                                        checked={isSelected}
                                        disabled={emOutroFornecedor}
                                        className="pointer-events-none"
                                      />
                                      <span className={`text-sm truncate ${isSelected ? 'font-medium text-indigo-800 dark:text-indigo-300' : 'text-slate-700 dark:text-slate-300'}`}>
                                        {terc.human_name}
                                      </span>
                                      <Badge className="text-[9px] px-1 py-0 bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">3º</Badge>
                                      {emOutroFornecedor && (
                                        <span className="text-[10px] text-slate-400">(outro)</span>
                                      )}
                                    </div>
                                  );
                                })}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Seção de Reembolsos (Exceções) */}
              <div className="border-t pt-4 dark:border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-500" />
                    Reembolsos (Exceções)
                    <Badge variant="outline" className="text-xs">{refeicoesDia.reembolsos.length}</Badge>
                  </h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddReembolso(true)}
                    className="dark:border-slate-600"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar Reembolso
                  </Button>
                </div>
                
                {refeicoesDia.reembolsos.length > 0 && (
                  <div className="space-y-2">
                    {refeicoesDia.reembolsos.map((reemb, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800"
                      >
                        <div className="flex items-center gap-3">
                          <Users className="w-4 h-4 text-green-600" />
                          <span className="font-medium text-slate-800 dark:text-slate-200">{reemb.funcionario_nome}</span>
                          <Badge className="bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400">
                            R$ {reemb.valor.toFixed(2)}
                          </Badge>
                          <span className="text-xs text-slate-500">{reemb.forma_pagamento?.toUpperCase()}</span>
                          {reemb.observacoes && (
                            <span className="text-xs text-slate-500 italic">&ldquo;{reemb.observacoes}&rdquo;</span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeReembolso(idx)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Observações Gerais */}
              <div className="border-t pt-4 dark:border-slate-700">
                <Label className="dark:text-slate-300">Observações Gerais do Dia</Label>
                <Textarea
                  value={refeicoesDia.observacoes_gerais || ''}
                  onChange={(e) => setRefeicoesDia(prev => ({ ...prev, observacoes_gerais: e.target.value }))}
                  placeholder="Observações sobre as refeições do dia..."
                  className="mt-1 dark:bg-slate-900 dark:border-slate-600"
                  rows={3}
                />
              </div>
              
              {/* Botão Salvar */}
              <div className="flex justify-end pt-4 border-t dark:border-slate-700">
                <Button onClick={handleSaveRefeicoesFornecedor} disabled={savingRefeicoes}>
                  {savingRefeicoes && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Refeições
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Dialog para Adicionar Reembolso */}
          <Dialog open={showAddReembolso} onOpenChange={setShowAddReembolso}>
            <DialogContent className="dark:bg-slate-800 dark:border-slate-700">
              <DialogHeader>
                <DialogTitle className="dark:text-slate-100">Adicionar Reembolso</DialogTitle>
                <DialogDescription className="dark:text-slate-400">
                  Registre um reembolso de refeição para um funcionário
                </DialogDescription>
              </DialogHeader>
              <ReembolsoForm 
                colaboradores={colaboradores}
                onSave={addReembolso}
                onCancel={() => setShowAddReembolso(false)}
              />
            </DialogContent>
          </Dialog>
        </TabsContent>
        
        {/* Dialog para Adicionar Reembolso Exceção */}
        <Dialog open={showAddReembolsoExcecao} onOpenChange={setShowAddReembolsoExcecao}>
          <DialogContent className="dark:bg-slate-800 dark:border-slate-700">
            <DialogHeader>
              <DialogTitle className="dark:text-slate-100">Adicionar Reembolso Exceção</DialogTitle>
              <DialogDescription className="dark:text-slate-400">
                Registre um reembolso de transporte, material ou outra exceção
              </DialogDescription>
            </DialogHeader>
            <ReembolsoExcecaoForm 
              colaboradores={colaboradores}
              terceiros={terceirosRecords}
              onSave={addReembolsoExcecao}
              onCancel={() => setShowAddReembolsoExcecao(false)}
            />
          </DialogContent>
        </Dialog>

        {/* VENCIMENTOS TAB */}
        <TabsContent value="vencimentos">
          <Card className="dark:bg-slate-800/50 dark:border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 dark:text-slate-100">
                <CreditCard className="w-5 h-5 text-green-500" />
                Controle de Vencimentos
              </CardTitle>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Gerencie pagamentos de terceiros e refeições
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Subabas Terceiros / Refeições */}
              <Tabs value={vencimentosAbaAtiva} onValueChange={setVencimentosAbaAtiva} className="w-full">
                <TabsList className="grid w-full grid-cols-3 bg-slate-100 dark:bg-slate-900">
                  <TabsTrigger value="terceiros" className="data-[state=active]:bg-indigo-500 data-[state=active]:text-white">
                    <Users className="w-4 h-4 mr-2" />
                    Terceiros
                  </TabsTrigger>
                  <TabsTrigger value="refeicoes" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                    <Utensils className="w-4 h-4 mr-2" />
                    Refeições
                  </TabsTrigger>
                  <TabsTrigger value="reembolsos" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                    <Receipt className="w-4 h-4 mr-2" />
                    Reembolsos
                  </TabsTrigger>
                </TabsList>
                
                {/* TERCEIROS VENCIMENTOS */}
                <TabsContent value="terceiros" className="space-y-6 pt-4">
                  {/* Filtros Terceiros */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                    <div>
                      <Label className="text-xs text-slate-500">Data Vencimento Início</Label>
                      <Input
                        type="date"
                        value={vencimentosFiltro.dataInicio}
                        onChange={(e) => setVencimentosFiltro(prev => ({ ...prev, dataInicio: e.target.value }))}
                        className="h-9 dark:bg-slate-800 dark:border-slate-600"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Data Vencimento Fim</Label>
                      <Input
                        type="date"
                        value={vencimentosFiltro.dataFim}
                        onChange={(e) => setVencimentosFiltro(prev => ({ ...prev, dataFim: e.target.value }))}
                        className="h-9 dark:bg-slate-800 dark:border-slate-600"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Status</Label>
                      <Select
                        value={vencimentosFiltro.status}
                        onValueChange={(v) => setVencimentosFiltro(prev => ({ ...prev, status: v }))}
                      >
                        <SelectTrigger className="h-9 dark:bg-slate-800 dark:border-slate-600">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          <SelectItem value="pendente">Pendente</SelectItem>
                          <SelectItem value="pago">Pago</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <Button onClick={loadVencimentosTerceiros} disabled={loadingVencTerceiros} className="w-full">
                        {loadingVencTerceiros ? <Loader2 className="w-4 h-4 animate-spin" /> : <Filter className="w-4 h-4 mr-1" />}
                        Filtrar
                      </Button>
                    </div>
                  </div>

                  {/* Resumo por Terceiro */}
                  {vencimentosTerceiros.resumo_terceiros?.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {vencimentosTerceiros.resumo_terceiros.map(terc => (
                        <div 
                          key={terc.terceiro_id} 
                          className="p-4 bg-gradient-to-br from-indigo-50 to-slate-100 dark:from-indigo-900/20 dark:to-slate-900 rounded-lg border dark:border-slate-700"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-slate-800 dark:text-slate-200">{terc.terceiro_nome}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => selecionarTodosTerceiro(terc.terceiro_id)}
                              className="h-7 text-xs"
                            >
                              Selecionar
                            </Button>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div>
                              <p className="text-xs text-slate-500">Serviços</p>
                              <p className="text-lg font-bold text-slate-700 dark:text-slate-300">{terc.total_servicos}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Pendente</p>
                              <p className="text-lg font-bold text-red-500">R$ {(terc.total_pendente || 0).toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Pago</p>
                              <p className="text-lg font-bold text-green-500">R$ {(terc.total_pago || 0).toFixed(2)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Totais Terceiros */}
                  <div className="grid grid-cols-3 gap-4 p-4 bg-gradient-to-r from-indigo-50 to-slate-50 dark:from-indigo-900/20 dark:to-slate-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
                    <div className="text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400 uppercase">Total Geral</p>
                      <p className="text-2xl font-bold text-slate-700 dark:text-slate-200">R$ {(vencimentosTerceiros.total_geral || 0).toFixed(2)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400 uppercase">Pendente</p>
                      <p className="text-2xl font-bold text-red-500">R$ {(vencimentosTerceiros.total_pendente || 0).toFixed(2)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400 uppercase">Pago</p>
                      <p className="text-2xl font-bold text-green-500">R$ {(vencimentosTerceiros.total_pago || 0).toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Ações em Lote Terceiros */}
                  {vencTerceirosSelecionados.length > 0 && (
                    <div className="flex items-center justify-between p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
                      <span className="text-sm text-indigo-700 dark:text-indigo-300">
                        <strong>{vencTerceirosSelecionados.length}</strong> lançamento(s) selecionado(s)
                        {' - '}
                        Total: <strong>R$ {vencimentosTerceiros.lancamentos
                          .filter(l => vencTerceirosSelecionados.includes(l.id))
                          .reduce((sum, l) => sum + (l.valor || 0), 0).toFixed(2)}</strong>
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setVencTerceirosSelecionados([])}
                          className="text-slate-600"
                        >
                          Limpar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={estornarTerceirosSelecionados}
                          disabled={processandoVencimentos}
                          className="text-orange-600 border-orange-300 hover:bg-orange-50"
                        >
                          Estornar
                        </Button>
                        <Button
                          size="sm"
                          onClick={quitarTerceirosSelecionados}
                          disabled={processandoVencimentos}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {processandoVencimentos ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                          Quitar
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Tabela de Lançamentos Terceiros */}
                  {loadingVencTerceiros ? (
                    <div className="flex justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                    </div>
                  ) : vencimentosTerceiros.lancamentos?.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Nenhum vencimento de terceiro encontrado</p>
                      <p className="text-sm">Adicione terceiros com data de vencimento na aba Terceiros</p>
                    </div>
                  ) : (
                    <div className="border dark:border-slate-700 rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50 dark:bg-slate-800">
                            <TableHead className="w-10">
                              <Checkbox
                                checked={vencimentosTerceiros.lancamentos?.every(l => vencTerceirosSelecionados.includes(l.id))}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setVencTerceirosSelecionados(vencimentosTerceiros.lancamentos.map(l => l.id));
                                  } else {
                                    setVencTerceirosSelecionados([]);
                                  }
                                }}
                              />
                            </TableHead>
                            <TableHead>Vencimento</TableHead>
                            <TableHead>Terceiro</TableHead>
                            <TableHead>Data Serviço</TableHead>
                            <TableHead>OS</TableHead>
                            <TableHead className="text-right">Valor</TableHead>
                            <TableHead className="text-center">Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {vencimentosTerceiros.lancamentos?.map(lanc => (
                            <TableRow 
                              key={lanc.id}
                              className={`${vencTerceirosSelecionados.includes(lanc.id) ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''} ${lanc.pago ? 'opacity-60' : ''}`}
                            >
                              <TableCell>
                                <Checkbox
                                  checked={vencTerceirosSelecionados.includes(lanc.id)}
                                  onCheckedChange={() => toggleVencTerceiroSelecionado(lanc.id)}
                                />
                              </TableCell>
                              <TableCell className="font-medium text-indigo-600 dark:text-indigo-400">
                                {lanc.data_vencimento ? format(new Date(lanc.data_vencimento + 'T12:00:00'), 'dd/MM/yyyy') : '-'}
                              </TableCell>
                              <TableCell className="font-medium">{lanc.terceiro_nome}</TableCell>
                              <TableCell>
                                {format(new Date(lanc.date + 'T12:00:00'), 'dd/MM/yyyy')}
                              </TableCell>
                              <TableCell>{lanc.os_number || '-'}</TableCell>
                              <TableCell className="text-right font-semibold">
                                {lanc.value_type === 'acertado_diretoria' ? (
                                  <span className="text-amber-600 dark:text-amber-400 text-xs">Acertado Dir.</span>
                                ) : (
                                  `R$ ${(lanc.valor || 0).toFixed(2)}`
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                {lanc.pago ? (
                                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Pago
                                  </Badge>
                                ) : (
                                  <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                    <Circle className="w-3 h-3 mr-1" />
                                    Pendente
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </TabsContent>
                
                {/* REFEIÇÕES VENCIMENTOS */}
                <TabsContent value="refeicoes" className="space-y-6 pt-4">
                  {/* Filtros Refeições */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                    <div>
                      <Label className="text-xs text-slate-500">Data Início</Label>
                      <Input
                        type="date"
                        value={vencimentosFiltro.dataInicio}
                        onChange={(e) => setVencimentosFiltro(prev => ({ ...prev, dataInicio: e.target.value }))}
                        className="h-9 dark:bg-slate-800 dark:border-slate-600"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Data Fim</Label>
                      <Input
                        type="date"
                        value={vencimentosFiltro.dataFim}
                        onChange={(e) => setVencimentosFiltro(prev => ({ ...prev, dataFim: e.target.value }))}
                        className="h-9 dark:bg-slate-800 dark:border-slate-600"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Fornecedor</Label>
                      <Select
                        value={vencimentosFiltro.fornecedorId}
                        onValueChange={(v) => setVencimentosFiltro(prev => ({ ...prev, fornecedorId: v === 'all' ? '' : v }))}
                      >
                        <SelectTrigger className="h-9 dark:bg-slate-800 dark:border-slate-600">
                          <SelectValue placeholder="Todos" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          {fornecedoresRefeicao.map(f => (
                            <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Status</Label>
                      <Select
                        value={vencimentosFiltro.status}
                        onValueChange={(v) => setVencimentosFiltro(prev => ({ ...prev, status: v }))}
                      >
                        <SelectTrigger className="h-9 dark:bg-slate-800 dark:border-slate-600">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          <SelectItem value="pendente">Pendente</SelectItem>
                          <SelectItem value="pago">Pago</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <Button onClick={loadVencimentos} disabled={loadingVencimentos} className="w-full">
                        {loadingVencimentos ? <Loader2 className="w-4 h-4 animate-spin" /> : <Filter className="w-4 h-4 mr-1" />}
                        Filtrar
                      </Button>
                    </div>
                  </div>

                  {/* Resumo por Fornecedor */}
                  {vencimentos.resumo_fornecedores.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {vencimentos.resumo_fornecedores.map(forn => (
                        <div 
                          key={forn.fornecedor_id} 
                          className="p-4 bg-gradient-to-br from-amber-50 to-slate-100 dark:from-amber-900/20 dark:to-slate-900 rounded-lg border dark:border-slate-700"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-slate-800 dark:text-slate-200">{forn.fornecedor_nome}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => selecionarTodosFornecedor(forn.fornecedor_id)}
                              className="h-7 text-xs"
                            >
                              Selecionar
                            </Button>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div>
                              <p className="text-xs text-slate-500">Refeições</p>
                              <p className="text-lg font-bold text-slate-700 dark:text-slate-300">{forn.total_refeicoes}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Pendente</p>
                              <p className="text-lg font-bold text-red-500">R$ {forn.total_pendente.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Pago</p>
                              <p className="text-lg font-bold text-green-500">R$ {forn.total_pago.toFixed(2)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Totais Refeições */}
                  <div className="grid grid-cols-3 gap-4 p-4 bg-gradient-to-r from-amber-50 to-slate-50 dark:from-amber-900/20 dark:to-slate-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <div className="text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400 uppercase">Total Geral</p>
                      <p className="text-2xl font-bold text-slate-700 dark:text-slate-200">R$ {vencimentos.total_geral.toFixed(2)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400 uppercase">Pendente</p>
                      <p className="text-2xl font-bold text-red-500">R$ {vencimentos.total_pendente.toFixed(2)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400 uppercase">Pago</p>
                      <p className="text-2xl font-bold text-green-500">R$ {vencimentos.total_pago.toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Ações em Lote Refeições */}
                  {vencimentosSelecionados.length > 0 && (
                    <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                      <span className="text-sm text-amber-700 dark:text-amber-300">
                        <strong>{vencimentosSelecionados.length}</strong> lançamento(s) selecionado(s)
                        {' - '}
                        Total: <strong>R$ {vencimentos.lancamentos
                          .filter(l => vencimentosSelecionados.includes(l.id))
                          .reduce((sum, l) => sum + l.total, 0).toFixed(2)}</strong>
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setVencimentosSelecionados([])}
                          className="text-slate-600"
                        >
                          Limpar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={estornarVencimentosSelecionados}
                          disabled={processandoVencimentos}
                          className="text-orange-600 border-orange-300 hover:bg-orange-50"
                        >
                          Estornar
                        </Button>
                        <Button
                          size="sm"
                          onClick={quitarVencimentosSelecionados}
                          disabled={processandoVencimentos}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {processandoVencimentos ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                          Quitar
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Tabela de Lançamentos Refeições */}
                  {loadingVencimentos ? (
                    <div className="flex justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                    </div>
                  ) : vencimentos.lancamentos.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Utensils className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Nenhum lançamento de refeição encontrado</p>
                      <p className="text-sm">Cadastre fornecedores e lance refeições na aba Refeições</p>
                    </div>
                  ) : (
                    <div className="border dark:border-slate-700 rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50 dark:bg-slate-800">
                            <TableHead className="w-10">
                              <Checkbox
                                checked={vencimentos.lancamentos.every(l => vencimentosSelecionados.includes(l.id))}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setVencimentosSelecionados(vencimentos.lancamentos.map(l => l.id));
                                  } else {
                                    setVencimentosSelecionados([]);
                                  }
                                }}
                              />
                            </TableHead>
                            <TableHead>Data</TableHead>
                            <TableHead>Fornecedor</TableHead>
                            <TableHead className="text-center">Qtd</TableHead>
                            <TableHead className="text-right">Valor Un.</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                            <TableHead className="text-center">Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {vencimentos.lancamentos.map(lanc => (
                            <TableRow 
                              key={lanc.id}
                              className={`${vencimentosSelecionados.includes(lanc.id) ? 'bg-amber-50 dark:bg-amber-900/20' : ''} ${lanc.pago ? 'opacity-60' : ''}`}
                            >
                              <TableCell>
                                <Checkbox
                                  checked={vencimentosSelecionados.includes(lanc.id)}
                                  onCheckedChange={() => toggleVencimentoSelecionado(lanc.id)}
                                />
                              </TableCell>
                              <TableCell className="font-medium">
                                {format(new Date(lanc.date + 'T12:00:00'), 'dd/MM/yyyy')}
                              </TableCell>
                              <TableCell>{lanc.fornecedor_nome}</TableCell>
                              <TableCell className="text-center">{lanc.qtd_refeicoes}</TableCell>
                              <TableCell className="text-right">R$ {lanc.valor_unitario.toFixed(2)}</TableCell>
                              <TableCell className="text-right font-semibold">R$ {lanc.total.toFixed(2)}</TableCell>
                              <TableCell className="text-center">
                                {lanc.pago ? (
                                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Pago
                                  </Badge>
                                ) : (
                                  <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                    <Circle className="w-3 h-3 mr-1" />
                                    Pendente
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </TabsContent>
                
                {/* REEMBOLSOS EXCEÇÕES VENCIMENTOS */}
                <TabsContent value="reembolsos" className="space-y-6 pt-4">
                  <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                    <h4 className="font-medium text-orange-800 dark:text-orange-300 mb-2 flex items-center gap-2">
                      <Receipt className="w-4 h-4" />
                      Reembolsos Exceções com Vencimento
                    </h4>
                    <p className="text-sm text-orange-600 dark:text-orange-400">
                      Lista de todos os reembolsos de exceção (transporte, materiais, etc.) com data de vencimento definida.
                    </p>
                  </div>
                  
                  {/* Lista de Reembolsos com vencimento de todos os dias */}
                  {reembolsosExcecoes.filter(r => r.data_vencimento).length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50 dark:bg-slate-800">
                            <TableHead className="dark:text-slate-300">Data Serviço</TableHead>
                            <TableHead className="dark:text-slate-300">Funcionário</TableHead>
                            <TableHead className="dark:text-slate-300">Descrição</TableHead>
                            <TableHead className="dark:text-slate-300">Categoria</TableHead>
                            <TableHead className="dark:text-slate-300">Valor</TableHead>
                            <TableHead className="dark:text-slate-300">Vencimento</TableHead>
                            <TableHead className="dark:text-slate-300">Pagamento</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {reembolsosExcecoes.filter(r => r.data_vencimento).map((r, i) => (
                            <TableRow key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                              <TableCell className="text-sm dark:text-slate-300">
                                {selectedDate ? format(new Date(selectedDate + 'T12:00:00'), 'dd/MM/yyyy') : '-'}
                              </TableCell>
                              <TableCell className="font-medium dark:text-slate-200">
                                {r.funcionario_nome}
                              </TableCell>
                              <TableCell className="text-sm dark:text-slate-300">
                                {r.descricao}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className={`text-xs ${
                                  r.categoria === 'transporte' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                                  r.categoria === 'material' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                  'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                                }`}>
                                  {r.categoria === 'transporte' ? 'Transporte' : r.categoria === 'material' ? 'Material' : 'Exceção'}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-bold text-green-600 dark:text-green-400">
                                R$ {r.valor?.toFixed(2)}
                              </TableCell>
                              <TableCell>
                                <span className="text-orange-600 dark:text-orange-400 font-medium">
                                  {format(new Date(r.data_vencimento + 'T12:00:00'), 'dd/MM/yyyy')}
                                </span>
                              </TableCell>
                              <TableCell className="text-sm text-slate-500 dark:text-slate-400">
                                {r.forma_pagamento?.toUpperCase() || 'PIX'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      
                      {/* Total */}
                      <div className="flex justify-between items-center pt-4 border-t dark:border-slate-700 mt-4">
                        <span className="font-medium text-slate-700 dark:text-slate-300">Total Reembolsos Exceções:</span>
                        <span className="text-xl font-bold text-orange-600 dark:text-orange-400">
                          R$ {reembolsosExcecoes.filter(r => r.data_vencimento).reduce((sum, r) => sum + (r.valor || 0), 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                      <Receipt className="w-12 h-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                      <p>Nenhum reembolso de exceção com vencimento registrado</p>
                      <p className="text-xs mt-2">Adicione reembolsos na aba "Terceiros" do controle diário</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>

        {/* VALE TRANSPORTE TAB */}
        <TabsContent value="vt">
          <Card className="dark:bg-slate-800/50 dark:border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 dark:text-slate-100">
                <Bus className="w-5 h-5 text-blue-500" />
                Controle de Vale Transporte (VT)
              </CardTitle>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Acompanhe o saldo de VT por funcionário (crédito mensal × consumo)
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                💡 Edite o transporte (IDA/VOLTA), quantidade de passagens e valor diretamente no extrato
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Seletor de Mês e Botão Filtrar */}
              <div className="flex flex-wrap items-end gap-4 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                <div>
                  <Label className="text-xs text-slate-500">Mês</Label>
                  <Select
                    value={vtMesSelecionado.mes.toString()}
                    onValueChange={(v) => setVtMesSelecionado(prev => ({ ...prev, mes: parseInt(v) }))}
                  >
                    <SelectTrigger className="w-32 h-9 dark:bg-slate-800 dark:border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                        <SelectItem key={m} value={m.toString()}>
                          {format(new Date(2024, m-1, 1), 'MMMM', { locale: ptBR })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Ano</Label>
                  <Select
                    value={vtMesSelecionado.ano.toString()}
                    onValueChange={(v) => setVtMesSelecionado(prev => ({ ...prev, ano: parseInt(v) }))}
                  >
                    <SelectTrigger className="w-24 h-9 dark:bg-slate-800 dark:border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map(a => (
                        <SelectItem key={a} value={a.toString()}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button onClick={loadSaldoVT} disabled={loadingSaldoVT}>
                  {loadingSaldoVT ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Filter className="w-4 h-4 mr-1" />}
                  Carregar Saldo
                </Button>
                
                <div className="ml-auto text-sm text-slate-500 dark:text-slate-400">
                  <span className="font-medium">{saldoVT.dias_uteis || 0}</span> dias úteis no mês
                  {saldoVT.dias_restantes > 0 && (
                    <span className="ml-2">• <span className="font-medium">{saldoVT.dias_restantes}</span> restantes</span>
                  )}
                </div>
              </div>

              {/* Resumo Geral */}
              {saldoVT.funcionarios?.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gradient-to-r from-blue-50 to-slate-50 dark:from-blue-900/20 dark:to-slate-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="text-center">
                    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase">Funcionários c/ VT</p>
                    <p className="text-2xl font-bold text-slate-700 dark:text-slate-200">
                      {saldoVT.funcionarios.filter(f => f.tipo_conducao !== 'valor_fixo').length}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase">Passagens VT Usadas</p>
                    <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                      {saldoVT.funcionarios.reduce((acc, f) => acc + (f.total_passagens_vt || (f.dias_vt || 0) * 2), 0)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase">Dias c/ Uber</p>
                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {saldoVT.funcionarios.reduce((acc, f) => acc + (f.dias_uber || 0), 0)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase">Valor Fixo</p>
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {saldoVT.funcionarios.filter(f => f.tipo_conducao === 'valor_fixo').length}
                    </p>
                  </div>
                </div>
              )}

              {/* Tabela de Funcionários */}
              {loadingSaldoVT ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : saldoVT.funcionarios?.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Bus className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nenhum funcionário com VT configurado</p>
                  <p className="text-sm mt-1">Configure o tipo de condução em <strong>Humanas → CLT</strong></p>
                  <p className="text-xs mt-2 text-slate-400">Marque Tipo Condução = VT e informe o valor da passagem</p>
                </div>
              ) : (
                <div className="border dark:border-slate-700 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 dark:bg-slate-800">
                        <TableHead>Funcionário</TableHead>
                        <TableHead className="text-center" title="Total de passagens de VT usadas">Pass. VT</TableHead>
                        <TableHead className="text-center">Dias Uber</TableHead>
                        <TableHead className="text-center">Dias Carona</TableHead>
                        <TableHead className="text-center">Veíc. Próprio</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {saldoVT.funcionarios.map(func => (
                        <TableRow 
                          key={func.funcionario_id}
                          className={func.tipo_conducao === 'valor_fixo' ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-1">
                              {func.funcionario_nome}
                              {func.tipo_conducao === 'valor_fixo' && (
                                <span className="text-[10px] text-blue-500" title="Valor Fixo">🏍️</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="bg-blue-50 dark:bg-blue-900/20">
                              {func.total_passagens_vt || ((func.dias_vt || 0) * 2)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {(func.dias_uber || 0) > 0 ? (
                              <Badge variant="outline" className="bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400">
                                {func.dias_uber}
                              </Badge>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {(func.dias_carona || 0) > 0 ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400">
                                {func.dias_carona}
                              </Badge>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {(func.dias_veiculo_proprio || 0) > 0 ? (
                              <Badge variant="outline" className="bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                                {func.dias_veiculo_proprio}
                              </Badge>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => loadVtFuncionarioDetalhe(func.funcionario_id)}
                              title="Ver detalhes"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Legenda */}
              <div className="flex flex-wrap gap-4 text-xs text-slate-500 dark:text-slate-400 p-3 bg-slate-50 dark:bg-slate-900/30 rounded-lg">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-blue-500"></div>
                  <span>VT = Usou vale transporte</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-purple-500"></div>
                  <span>Uber = Empresa pagou transporte</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-green-500"></div>
                  <span>Carona = Não usou VT</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-slate-500"></div>
                  <span>Veíc. Próprio = Valor fixo</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* MODAL DETALHE VT FUNCIONÁRIO - EDITÁVEL */}
        <Dialog open={showVtDetalhe} onOpenChange={setShowVtDetalhe}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto dark:bg-slate-800">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="flex items-center gap-2">
                  <Bus className="w-5 h-5 text-blue-500" />
                  Extrato VT - {vtFuncionarioDetalhe?.funcionario?.nome}
                </DialogTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.print()}
                  className="no-print"
                >
                  <Printer className="w-4 h-4 mr-1" />
                  Imprimir
                </Button>
              </div>
              <DialogDescription>
                {vtFuncionarioDetalhe?.mes?.mes && format(new Date(vtFuncionarioDetalhe.mes.ano, vtFuncionarioDetalhe.mes.mes - 1, 1), 'MMMM yyyy', { locale: ptBR })}
                {' • '}<span className="text-amber-600">Clique nos campos para editar</span>
              </DialogDescription>
            </DialogHeader>
            {vtFuncionarioDetalhe && (
              <div className="space-y-4" id="vt-extrato-print">
                {/* Resumo VT */}
                <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Crédito Mensal</p>
                    <p className="text-xl font-bold text-blue-600">R$ {vtFuncionarioDetalhe.vt.credito_mensal?.toFixed(2) || '0.00'}</p>
                    <p className="text-[10px] text-slate-400">{vtFuncionarioDetalhe.mes?.dias_uteis || 0} dias úteis</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Consumido VT</p>
                    <p className="text-xl font-bold text-amber-600">R$ {vtFuncionarioDetalhe.vt.consumido?.toFixed(2) || '0.00'}</p>
                    <p className="text-[10px] text-slate-400">{vtFuncionarioDetalhe.vt.total_passagens_vt || 0} passagens</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Saldo VT</p>
                    <p className={`text-xl font-bold ${(vtFuncionarioDetalhe.vt.saldo || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      R$ {vtFuncionarioDetalhe.vt.saldo?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                </div>
                
                {/* Resumo Outros Transportes */}
                {(vtFuncionarioDetalhe.vt.dias_com_uber > 0 || vtFuncionarioDetalhe.vt.dias_com_carona > 0 || vtFuncionarioDetalhe.vt.dias_com_proprio > 0) && (
                  <div className="grid grid-cols-3 gap-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                    <div className="text-center">
                      <p className="text-xs text-purple-600 dark:text-purple-400">Dias c/ Uber</p>
                      <p className="text-lg font-bold text-purple-700 dark:text-purple-300">{vtFuncionarioDetalhe.vt.dias_com_uber || 0}</p>
                      {vtFuncionarioDetalhe.vt.total_valor_uber > 0 && (
                        <p className="text-xs text-purple-600">R$ {vtFuncionarioDetalhe.vt.total_valor_uber.toFixed(2)}</p>
                      )}
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-green-600 dark:text-green-400">Dias c/ Carona</p>
                      <p className="text-lg font-bold text-green-700 dark:text-green-300">{vtFuncionarioDetalhe.vt.dias_com_carona || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-600 dark:text-slate-400">Veíc. Próprio</p>
                      <p className="text-lg font-bold text-slate-700 dark:text-slate-300">{vtFuncionarioDetalhe.vt.dias_com_proprio || 0}</p>
                    </div>
                  </div>
                )}
                
                {/* Calendário do mês - EDITÁVEL */}
                <div className="border dark:border-slate-700 rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 dark:bg-slate-800 sticky top-0 z-10">
                        <TableHead className="w-16">Data</TableHead>
                        <TableHead className="w-12">Dia</TableHead>
                        <TableHead className="text-center w-16">Tipo</TableHead>
                        <TableHead className="text-center">IDA</TableHead>
                        <TableHead className="text-center">VOLTA</TableHead>
                        <TableHead className="text-center w-16">Pass.</TableHead>
                        <TableHead className="text-center w-20">R$/Pass</TableHead>
                        <TableHead className="text-right w-20">Total VT</TableHead>
                        <TableHead className="text-right w-20">Uber</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vtFuncionarioDetalhe.calendario?.map(dia => {
                        const labelMap = { 'vt': 'VT', 'uber': 'Uber', 'carona': 'Carona', 'veiculo_proprio': 'Próprio', 'valor_fixo': 'Próprio' };
                        const colorMap = { 'vt': 'bg-blue-100 border-blue-300', 'uber': 'bg-purple-100 border-purple-300', 'carona': 'bg-green-100 border-green-300', 'veiculo_proprio': 'bg-slate-100 border-slate-300', 'valor_fixo': 'bg-slate-100 border-slate-300' };
                        const valorPassagemPadrao = vtFuncionarioDetalhe.funcionario?.valor_passagem || 0;
                        
                        return (
                          <TableRow 
                            key={dia.data}
                            className={`${dia.is_fds || dia.is_feriado ? 'bg-slate-50/50 dark:bg-slate-900/30' : ''} ${dia.trabalhou && !dia.is_dia_util ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}
                          >
                            <TableCell className="font-medium text-xs">
                              {format(new Date(dia.data + 'T12:00:00'), 'dd/MM')}
                            </TableCell>
                            <TableCell className={`text-xs ${dia.is_fds || dia.is_feriado ? 'text-slate-400' : ''}`}>
                              {dia.dia_semana}
                            </TableCell>
                            <TableCell className="text-center">
                              {dia.is_feriado ? (
                                <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400">Fer</Badge>
                              ) : dia.is_fds ? (
                                <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-500">FDS</Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">Útil</Badge>
                              )}
                            </TableCell>
                            {/* IDA - Editável */}
                            <TableCell className="text-center">
                              {dia.trabalhou && dia.record_id ? (
                                <Select 
                                  value={dia.transporte_ida || 'vt'} 
                                  onValueChange={(v) => updateVtDia(dia.record_id, { transporte_ida: v })}
                                >
                                  <SelectTrigger className={`h-6 w-20 text-[10px] ${colorMap[dia.transporte_ida] || ''}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="vt" className="text-xs">VT</SelectItem>
                                    <SelectItem value="uber" className="text-xs">Uber</SelectItem>
                                    <SelectItem value="carona" className="text-xs">Carona</SelectItem>
                                    <SelectItem value="veiculo_proprio" className="text-xs">Próprio</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </TableCell>
                            {/* VOLTA - Editável */}
                            <TableCell className="text-center">
                              {dia.trabalhou && dia.record_id ? (
                                <Select 
                                  value={dia.transporte_volta || 'vt'} 
                                  onValueChange={(v) => updateVtDia(dia.record_id, { transporte_volta: v })}
                                >
                                  <SelectTrigger className={`h-6 w-20 text-[10px] ${colorMap[dia.transporte_volta] || ''}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="vt" className="text-xs">VT</SelectItem>
                                    <SelectItem value="uber" className="text-xs">Uber</SelectItem>
                                    <SelectItem value="carona" className="text-xs">Carona</SelectItem>
                                    <SelectItem value="veiculo_proprio" className="text-xs">Próprio</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </TableCell>
                            {/* Passagens - Editável */}
                            <TableCell className="text-center">
                              {dia.trabalhou && dia.record_id && (dia.transporte_ida === 'vt' || dia.transporte_volta === 'vt') ? (
                                <Input
                                  type="number"
                                  min="0"
                                  max="10"
                                  value={dia.passagens_vt_manual !== null && dia.passagens_vt_manual !== undefined ? dia.passagens_vt_manual : ''}
                                  onChange={(e) => {
                                    const val = e.target.value === '' ? null : parseInt(e.target.value);
                                    updateVtDia(dia.record_id, { passagens_vt_manual: val });
                                  }}
                                  placeholder={(dia.transporte_ida === 'vt' ? 1 : 0) + (dia.transporte_volta === 'vt' ? 1 : 0)}
                                  className="h-6 w-12 text-[10px] text-center"
                                />
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </TableCell>
                            {/* Valor por Passagem - Editável */}
                            <TableCell className="text-center">
                              {dia.trabalhou && dia.record_id && (dia.transporte_ida === 'vt' || dia.transporte_volta === 'vt') ? (
                                <Input
                                  type="number"
                                  step="0.10"
                                  min="0"
                                  value={dia.valor_passagem_vt_dia !== null && dia.valor_passagem_vt_dia !== undefined ? dia.valor_passagem_vt_dia : ''}
                                  onChange={(e) => {
                                    const val = e.target.value === '' ? null : parseFloat(e.target.value);
                                    updateVtDia(dia.record_id, { valor_passagem_vt_dia: val });
                                  }}
                                  placeholder={valorPassagemPadrao.toFixed(2)}
                                  className="h-6 w-16 text-[10px] text-center"
                                />
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </TableCell>
                            {/* Total VT do dia */}
                            <TableCell className="text-right text-xs">
                              {dia.vt_consumido > 0 ? (
                                <span className={dia.is_dia_util ? 'text-slate-700 dark:text-slate-300 font-medium' : 'text-amber-600 dark:text-amber-400 font-medium'}>
                                  R$ {dia.vt_consumido.toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </TableCell>
                            {/* Uber */}
                            <TableCell className="text-right text-xs">
                              {dia.trabalhou && dia.record_id && (dia.transporte_ida === 'uber' || dia.transporte_volta === 'uber') ? (
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={dia.valor_uber || ''}
                                  onChange={(e) => {
                                    const val = e.target.value === '' ? null : parseFloat(e.target.value);
                                    updateVtDia(dia.record_id, { transporte_valor_uber: val });
                                  }}
                                  placeholder="R$"
                                  className="h-6 w-16 text-[10px] text-right"
                                />
                              ) : dia.valor_uber > 0 ? (
                                <span className="text-purple-600 font-medium">R$ {dia.valor_uber.toFixed(2)}</span>
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Totais para Impressão */}
                <div className="p-3 bg-slate-100 dark:bg-slate-900 rounded-lg text-xs">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">RESUMO PARA FECHAMENTO:</span>
                    <div className="flex gap-4">
                      <span>VT: <strong className="text-amber-600">R$ {vtFuncionarioDetalhe.vt.consumido?.toFixed(2) || '0.00'}</strong></span>
                      {vtFuncionarioDetalhe.vt.total_valor_uber > 0 && (
                        <span>Uber: <strong className="text-purple-600">R$ {vtFuncionarioDetalhe.vt.total_valor_uber?.toFixed(2) || '0.00'}</strong></span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* DESPERDÍCIOS TAB */}
        <TabsContent value="desperdicios">
          <Card className="dark:bg-slate-800/50 dark:border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg flex items-center gap-2 dark:text-slate-100">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Desperdícios do Dia
              </CardTitle>
              <Button size="sm" variant="destructive" onClick={() => setShowAddWaste(true)}>
                <Plus className="w-4 h-4 mr-1" />
                Registrar
              </Button>
            </CardHeader>
            <CardContent>
              {wastes.length === 0 ? (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                  <p>Nenhum desperdício registrado hoje</p>
                  <p className="text-sm text-green-600 dark:text-green-400 mt-1">Ótimo! Continue assim!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {wastes.map(waste => (
                    <div 
                      key={waste.id}
                      className="p-4 rounded-lg border bg-red-50/50 border-red-200 dark:bg-red-950/20 dark:border-red-800"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900 dark:text-slate-100">{waste.description}</p>
                            <Badge variant="outline" className="text-xs">
                              {getWasteTypeLabel(waste.type)}
                            </Badge>
                          </div>
                          {waste.responsible && (
                            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                              Responsável: {waste.responsible}
                            </p>
                          )}
                          <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                            Registrado por: {waste.created_by_name}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <p className="text-lg font-bold text-red-600 dark:text-red-400">
                            R$ {(waste.value || 0).toFixed(2)}
                          </p>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700 dark:hover:bg-red-950/50"
                            onClick={() => handleRemoveWaste(waste.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Total */}
                  <div className="flex justify-between items-center pt-3 border-t dark:border-slate-700">
                    <span className="font-medium text-slate-700 dark:text-slate-300">Total Desperdícios:</span>
                    <span className="text-xl font-bold text-red-600 dark:text-red-400">
                      R$ {totalWastes.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog: Adicionar Terceiro */}
      <Dialog open={showFolgaColetivaModal} onOpenChange={setShowFolgaColetivaModal}>
        <DialogContent className="max-w-md" data-testid="folga-coletiva-modal">
          <DialogHeader>
            <DialogTitle>Folga para Todos — {format(new Date(selectedDate + 'T12:00:00'), 'dd/MM/yyyy')}</DialogTitle>
            <DialogDescription>
              Lança folga para todos os colaboradores CLT ativos nesta data. Quem já tiver apontamento (entrada/saída) NÃO será sobrescrito.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Tipo de folga</Label>
            <Select value={folgaColetivaTipo} onValueChange={setFolgaColetivaTipo}>
              <SelectTrigger data-testid="folga-coletiva-tipo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="folga">Folga (liberada pela empresa)</SelectItem>
                <SelectItem value="folga_abonada">Folga Abonada</SelectItem>
                <SelectItem value="banco_horas">Folga Banco de Horas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFolgaColetivaModal(false)}>Cancelar</Button>
            <Button onClick={handleFolgaColetiva} disabled={lancandoFolgaColetiva} className="bg-green-600 hover:bg-green-700 text-white" data-testid="folga-coletiva-confirmar">
              {lancandoFolgaColetiva ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Lançar Folga p/ Todos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddWorker} onOpenChange={setShowAddWorker}>
        <DialogContent className="max-w-md bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
              <UserPlus className="w-5 h-5 text-indigo-500" />
              Adicionar Terceiro à Escala
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Busca */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar terceiro..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600"
              />
            </div>
            
            {/* Lista de terceiros */}
            <ScrollArea className="h-40 border rounded-lg border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
              {filteredTerceiros.length === 0 ? (
                <div className="p-4 text-center text-slate-500 dark:text-slate-400">
                  {searchTerm ? 'Nenhum terceiro encontrado' : 'Todos os terceiros já foram adicionados'}
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {filteredTerceiros.map(terceiro => (
                    <div
                      key={terceiro.id}
                      onClick={() => setSelectedHuman(terceiro)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedHuman?.id === terceiro.id
                          ? 'bg-indigo-100 border-indigo-300 dark:bg-indigo-950/50 dark:border-indigo-700'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                      } border dark:border-slate-700`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900 dark:text-slate-100">{terceiro.name}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {terceiro.subtype}
                            {terceiro.is_strategic && ' • Diretoria'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
            
            {selectedHuman && (
              <div className="space-y-4 pt-4 border-t dark:border-slate-700">
                <div className="p-3 bg-indigo-50 dark:bg-indigo-950/30 rounded-lg">
                  <p className="font-medium text-indigo-800 dark:text-indigo-300">
                    Selecionado: {selectedHuman.name}
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="dark:text-slate-300">Tipo de Serviço</Label>
                    <Select value={workerServiceType} onValueChange={setWorkerServiceType}>
                      <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                        {tiposAtividade.length > 0 ? (
                          tiposAtividade.map(tipo => (
                            <SelectItem key={tipo.id} value={tipo.id} className="dark:text-slate-200">
                              <span className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tipo.cor }}></span>
                                {tipo.nome}
                              </span>
                            </SelectItem>
                          ))
                        ) : (
                          <>
                            <SelectItem value="evento" className="dark:text-slate-200">Evento</SelectItem>
                            <SelectItem value="montagem" className="dark:text-slate-200">Montagem</SelectItem>
                            <SelectItem value="desmontagem" className="dark:text-slate-200">Desmontagem</SelectItem>
                            <SelectItem value="manutencao" className="dark:text-slate-200">Manutenção</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="dark:text-slate-300">Nº OS</Label>
                    <Input
                      value={workerOS}
                      onChange={(e) => setWorkerOS(e.target.value)}
                      placeholder="Ex: 12345"
                      className="dark:bg-slate-900 dark:border-slate-600"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="dark:text-slate-300">Descrição do Serviço</Label>
                  <Input
                    value={workerService}
                    onChange={(e) => setWorkerService(e.target.value)}
                    placeholder="Ex: Carregar equipamentos..."
                    className="dark:bg-slate-900 dark:border-slate-600"
                  />
                </div>
                
                <div>
                  <Label className="dark:text-slate-300">Valor a Pagar</Label>
                  <div className="flex gap-2">
                    <Select value={workerValueType} onValueChange={setWorkerValueType}>
                      <SelectTrigger className="w-44 dark:bg-slate-900 dark:border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                        <SelectItem value="definido" className="dark:text-slate-200">Valor Definido</SelectItem>
                        <SelectItem value="acertado_diretoria" className="dark:text-slate-200">Acertado c/ Diretoria</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {workerValueType === 'definido' && (
                      <Input
                        type="number"
                        step="0.01"
                        value={workerValue}
                        onChange={(e) => setWorkerValue(e.target.value)}
                        placeholder="R$ 0,00"
                        className="flex-1 dark:bg-slate-900 dark:border-slate-600"
                      />
                    )}
                  </div>
                </div>
                
                <div>
                  <Label className="dark:text-slate-300">Data de Vencimento</Label>
                  <Input
                    type="date"
                    value={workerVencimento}
                    onChange={(e) => setWorkerVencimento(e.target.value)}
                    className="dark:bg-slate-900 dark:border-slate-600"
                  />
                  <p className="text-xs text-slate-500 mt-1">Data para pagamento do serviço</p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddWorker(false)} className="dark:border-slate-600 dark:text-slate-300">
              Cancelar
            </Button>
            <Button onClick={handleAddWorker} disabled={!selectedHuman || saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Editar Registro */}
      <Dialog open={showEditRecord} onOpenChange={setShowEditRecord}>
        <DialogContent className="max-w-lg bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
              <Edit className="w-5 h-5 text-blue-500" />
              Editar Apontamento
            </DialogTitle>
          </DialogHeader>
          
          {editingRecord && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                <p className="font-medium text-slate-900 dark:text-slate-200">{editingRecord.human_name}</p>
                <p className="text-xs text-slate-500">{editingRecord.type === 'colaborador' ? 'Colaborador' : 'Terceiro'}</p>
              </div>
              
              {/* Entrada */}
              <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-slate-700 dark:text-slate-300 font-medium">Entrada</Label>
                  {/* Terceiros não batem ponto - apenas horário manual */}
                  {editingRecord.type === 'colaborador' && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Fonte:</span>
                      <Button
                        variant={editingRecord.entry_source === 'ponto' ? 'default' : 'outline'}
                        size="sm"
                        className={`h-6 text-xs ${editingRecord.entry_source === 'ponto' ? 'bg-sky-500 hover:bg-sky-600' : ''}`}
                        onClick={() => setEditingRecord({ ...editingRecord, entry_source: 'ponto' })}
                      >
                        <Check className="w-3 h-3 mr-1" /> Ponto
                      </Button>
                      <Button
                        variant={editingRecord.entry_source === 'manual' ? 'default' : 'outline'}
                        size="sm"
                        className={`h-6 text-xs ${editingRecord.entry_source === 'manual' ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                        onClick={() => setEditingRecord({ ...editingRecord, entry_source: 'manual' })}
                      >
                        <Clock className="w-3 h-3 mr-1" /> Manual
                      </Button>
                    </div>
                  )}
                  {editingRecord.type !== 'colaborador' && (
                    <span className="text-xs text-slate-500 italic">Terceiros: horário informado</span>
                  )}
                </div>
                <Input
                  type="time"
                  value={editingRecord.entry_time || ''}
                  onChange={(e) => setEditingRecord({ ...editingRecord, entry_time: e.target.value })}
                  className={`dark:bg-slate-900 dark:border-slate-600 ${
                    editingRecord.type === 'colaborador' 
                      ? editingRecord.entry_source === 'ponto' 
                        ? 'border-sky-300 bg-sky-50 dark:bg-sky-900/20' 
                        : 'border-orange-300 bg-orange-50 dark:bg-orange-900/20'
                      : 'border-slate-300 dark:border-slate-600'
                  }`}
                />
                {editingRecord.type === 'colaborador' && editingRecord.entry_source === 'manual' && (
                  <div className="mt-2">
                    <Label className="text-xs text-orange-600 dark:text-orange-400">Justificativa</Label>
                    <Input
                      value={editingRecord.entry_justification || ''}
                      onChange={(e) => setEditingRecord({ ...editingRecord, entry_justification: e.target.value })}
                      placeholder="Ex: Esqueceu de bater, problemas no app..."
                      className="mt-1 text-sm dark:bg-slate-900 dark:border-slate-600"
                    />
                  </div>
                )}
              </div>
              
              {/* Saída */}
              <div className="p-3 rounded-lg border dark:border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <Label className="dark:text-slate-300 font-medium">Saída</Label>
                  {/* Terceiros não batem ponto */}
                  {editingRecord.type === 'colaborador' && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Fonte:</span>
                      <Button
                        variant={editingRecord.exit_source === 'ponto' ? 'default' : 'outline'}
                        size="sm"
                        className={`h-6 text-xs ${editingRecord.exit_source === 'ponto' ? 'bg-sky-500 hover:bg-sky-600' : ''}`}
                        onClick={() => setEditingRecord({ ...editingRecord, exit_source: 'ponto' })}
                      >
                        <Check className="w-3 h-3 mr-1" /> Ponto
                      </Button>
                      <Button
                        variant={editingRecord.exit_source === 'manual' ? 'default' : 'outline'}
                        size="sm"
                        className={`h-6 text-xs ${editingRecord.exit_source === 'manual' ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                        onClick={() => setEditingRecord({ ...editingRecord, exit_source: 'manual' })}
                      >
                        <Clock className="w-3 h-3 mr-1" /> Manual
                      </Button>
                    </div>
                  )}
                  {editingRecord.type !== 'colaborador' && (
                    <span className="text-xs text-slate-500 italic">Terceiros: horário informado</span>
                  )}
                </div>
                <Input
                  type="time"
                  value={editingRecord.exit_time || ''}
                  onChange={(e) => setEditingRecord({ ...editingRecord, exit_time: e.target.value })}
                  className={`dark:bg-slate-900 dark:border-slate-600 ${
                    editingRecord.type === 'colaborador'
                      ? editingRecord.exit_source === 'ponto' 
                        ? 'border-sky-300 bg-sky-50 dark:bg-sky-900/20' 
                        : 'border-orange-300 bg-orange-50 dark:bg-orange-900/20'
                      : 'border-slate-300 dark:border-slate-600'
                  }`}
                />
                {editingRecord.type === 'colaborador' && editingRecord.exit_source === 'manual' && (
                  <div className="mt-2">
                    <Label className="text-xs text-orange-600 dark:text-orange-400">Justificativa</Label>
                    <Input
                      value={editingRecord.exit_justification || ''}
                      onChange={(e) => setEditingRecord({ ...editingRecord, exit_justification: e.target.value })}
                      placeholder="Ex: Esqueceu de bater, problemas no app..."
                      className="mt-1 text-sm dark:bg-slate-900 dark:border-slate-600"
                    />
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="dark:text-slate-300">Local</Label>
                  <Select 
                    value={editingRecord.location || 'interno'} 
                    onValueChange={(v) => setEditingRecord({ ...editingRecord, location: v })}
                  >
                    <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                      <SelectItem value="interno" className="dark:text-slate-200">Interno</SelectItem>
                      <SelectItem value="externo" className="dark:text-slate-200">Externo</SelectItem>
                      <SelectItem value="faltou" className="dark:text-slate-200">Faltou</SelectItem>
                      <SelectItem value="folga_abonada" className="dark:text-slate-200">Folga Abonada</SelectItem>
                      <SelectItem value="folga" className="dark:text-slate-200">Folga (legado)</SelectItem>
                      <SelectItem value="banco_horas" className="dark:text-slate-200">Banco Horas</SelectItem>
                      <SelectItem value="atestado" className="dark:text-slate-200">Atestado Dia</SelectItem>
                      <SelectItem value="atestado_horas" className="dark:text-slate-200">Atestado Horas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="dark:text-slate-300">Tipo de Serviço</Label>
                  <Select 
                    value={editingRecord.service_type || 'evento'} 
                    onValueChange={(v) => setEditingRecord({ ...editingRecord, service_type: v })}
                  >
                    <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                      {tiposAtividade.length > 0 ? (
                        tiposAtividade.map(tipo => (
                          <SelectItem key={tipo.id} value={tipo.id} className="dark:text-slate-200">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tipo.cor }}></span>
                              {tipo.nome}
                            </span>
                          </SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="evento" className="dark:text-slate-200">Evento</SelectItem>
                          <SelectItem value="montagem" className="dark:text-slate-200">Montagem</SelectItem>
                          <SelectItem value="desmontagem" className="dark:text-slate-200">Desmontagem</SelectItem>
                          <SelectItem value="manutencao" className="dark:text-slate-200">Manutenção</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label className="dark:text-slate-300">Nº OS</Label>
                {editingRecord.type === 'terceiro' ? (
                  // Para terceiros, usar input de texto para suportar múltiplas OSs
                  <Input
                    value={editingRecord.os_number || ''}
                    onChange={(e) => setEditingRecord({ ...editingRecord, os_number: e.target.value })}
                    placeholder="Ex: 18013 ou 18013, 18026"
                    className="dark:bg-slate-900 dark:border-slate-600"
                  />
                ) : (
                  // Para colaboradores, manter o Select
                  <Select
                    value={editingRecord.os_number || '__none__'}
                    onValueChange={(value) => {
                      const realValue = value === '__none__' ? '' : value;
                      const osSelecionada = todasOss.find(os => os.numero === realValue);
                      const updates = { ...editingRecord, os_number: realValue };
                      if (osSelecionada && osSelecionada.local) {
                        updates.event_location = osSelecionada.local;
                      }
                      setEditingRecord(updates);
                    }}
                  >
                    <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600">
                      <SelectValue placeholder="Selecione a OS" />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-700 max-h-72 overflow-y-auto">
                      <SelectItem value="__none__" className="dark:text-slate-400">Nenhuma</SelectItem>
                      {/* OSs do dia destacadas primeiro */}
                      {ossDoDia.length > 0 && (
                        <>
                          <div className="px-2 py-1 text-[10px] text-emerald-400 font-semibold border-b border-slate-700">📅 OSs do Dia</div>
                          {ossDoDia.map(os => (
                            <SelectItem key={os.numero} value={os.numero} className="text-emerald-300 font-medium">
                              #{os.numero} - {os.cliente?.substring(0, 15) || os.servicos?.substring(0, 15) || 'OS'}
                            </SelectItem>
                          ))}
                        </>
                      )}
                      {/* Todas as outras OSs */}
                      {todasOss.filter(os => !ossDoDia.some(osd => osd.numero === os.numero)).length > 0 && (
                        <>
                          <div className="px-2 py-1 text-[10px] text-slate-400 font-semibold border-b border-slate-700 mt-1">📋 Outras OSs</div>
                          {todasOss
                            .filter(os => !ossDoDia.some(osd => osd.numero === os.numero))
                            .map(os => (
                              <SelectItem key={os.numero} value={os.numero} className="dark:text-slate-200">
                                #{os.numero} - {os.cliente?.substring(0, 15) || os.servicos?.substring(0, 15) || 'OS'}
                              </SelectItem>
                            ))}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                )}
              </div>
              
              {/* Campos específicos para Terceiros */}
              {editingRecord.type === 'terceiro' && (
                <>
                  <div>
                    <Label className="dark:text-slate-300">Descrição do Serviço</Label>
                    <Input
                      value={editingRecord.service_description || ''}
                      onChange={(e) => setEditingRecord({ ...editingRecord, service_description: e.target.value })}
                      placeholder="Ex: Carregamento de equipamentos"
                      className="dark:bg-slate-900 dark:border-slate-600"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="dark:text-slate-300">Valor (R$)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editingRecord.value || ''}
                        onChange={(e) => setEditingRecord({ ...editingRecord, value: parseFloat(e.target.value) || 0 })}
                        placeholder="0.00"
                        className="dark:bg-slate-900 dark:border-slate-600"
                      />
                    </div>
                    <div>
                      <Label className="dark:text-slate-300">Tipo Valor</Label>
                      <Select
                        value={editingRecord.value_type || 'definido'}
                        onValueChange={(v) => setEditingRecord({ ...editingRecord, value_type: v })}
                      >
                        <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                          <SelectItem value="definido">Valor Definido</SelectItem>
                          <SelectItem value="acertado_diretoria">Acertado c/ Diretoria</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="dark:text-slate-300">Data de Vencimento</Label>
                    <Input
                      type="date"
                      value={editingRecord.data_vencimento || ''}
                      onChange={(e) => setEditingRecord({ ...editingRecord, data_vencimento: e.target.value })}
                      className="dark:bg-slate-900 dark:border-slate-600"
                    />
                    <p className="text-xs text-slate-500 mt-1">Data para pagamento do serviço</p>
                  </div>
                </>
              )}
              
              <div>
                <Label className="dark:text-slate-300">Local do Evento</Label>
                <Input
                  value={editingRecord.event_location || ''}
                  onChange={(e) => setEditingRecord({ ...editingRecord, event_location: e.target.value })}
                  placeholder="Ex: SESI, Expo Center, Studio 5..."
                  className="dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={editingRecord.meal_included || false}
                  onCheckedChange={(checked) => setEditingRecord({ ...editingRecord, meal_included: checked })}
                />
                <Label className="dark:text-slate-300">Incluiu refeição</Label>
              </div>
              
              <div>
                <Label className="dark:text-slate-300">Observação do Dia</Label>
                <Textarea
                  value={editingRecord.observations || ''}
                  onChange={(e) => setEditingRecord({ ...editingRecord, observations: e.target.value })}
                  placeholder="Observação sobre o funcionário neste dia..."
                  className="dark:bg-slate-900 dark:border-slate-600"
                  rows={3}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditRecord(false)} className="dark:border-slate-600 dark:text-slate-300">
              Cancelar
            </Button>
            <Button onClick={handleSaveEditRecord} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Registrar Desperdício */}
      <Dialog open={showAddWaste} onOpenChange={setShowAddWaste}>
        <DialogContent className="max-w-md dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Registrar Desperdício
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="dark:text-slate-300">Tipo</Label>
              <Select value={wasteType} onValueChange={setWasteType}>
                <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                  <SelectItem value="refeicao_desperdicada" className="dark:text-slate-200">Refeição Desperdiçada</SelectItem>
                  <SelectItem value="material_danificado" className="dark:text-slate-200">Material Danificado</SelectItem>
                  <SelectItem value="desvio" className="dark:text-slate-200">Desvio</SelectItem>
                  <SelectItem value="outros" className="dark:text-slate-200">Outros</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="dark:text-slate-300">Descrição *</Label>
              <Textarea
                value={wasteDescription}
                onChange={(e) => setWasteDescription(e.target.value)}
                placeholder="Descreva o que aconteceu..."
                rows={3}
                className="dark:bg-slate-900 dark:border-slate-600"
              />
            </div>
            
            <div>
              <Label className="dark:text-slate-300">Valor (R$)</Label>
              <Input
                type="number"
                step="0.01"
                value={wasteValue}
                onChange={(e) => setWasteValue(e.target.value)}
                placeholder="0,00"
                className="dark:bg-slate-900 dark:border-slate-600"
              />
            </div>
            
            <div>
              <Label className="dark:text-slate-300">Responsável (opcional)</Label>
              <Input
                value={wasteResponsible}
                onChange={(e) => setWasteResponsible(e.target.value)}
                placeholder="Nome do responsável..."
                className="dark:bg-slate-900 dark:border-slate-600"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddWaste(false)} className="dark:border-slate-600 dark:text-slate-300">
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleAddWaste} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Batidas Extras (Saída/Retorno Intermediário) */}
      <Dialog open={showExtraEntries} onOpenChange={setShowExtraEntries}>
        <DialogContent className="max-w-lg dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
              <Clock className="w-5 h-5" />
              Batidas Extras - {editingExtraRecord?.human_name}
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              Registre saídas e retornos intermediários (ex: ida ao banco, DETRAN, etc.)
            </DialogDescription>
          </DialogHeader>
          
          {editingExtraRecord && (
            <div className="space-y-4 py-4 max-h-[400px] overflow-y-auto">
              {/* Lista de batidas extras existentes */}
              {(editingExtraRecord.extra_entries || []).map((entry, index) => (
                <div key={index} className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Batida Extra #{index + 1}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-100"
                      onClick={() => {
                        const newExtras = [...(editingExtraRecord.extra_entries || [])];
                        newExtras.splice(index, 1);
                        setEditingExtraRecord({ ...editingExtraRecord, extra_entries: newExtras });
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs dark:text-slate-400">Saída</Label>
                      <Input
                        type="time"
                        value={entry.exit_time || ''}
                        onChange={(e) => {
                          const newExtras = [...(editingExtraRecord.extra_entries || [])];
                          newExtras[index] = { ...newExtras[index], exit_time: e.target.value };
                          setEditingExtraRecord({ ...editingExtraRecord, extra_entries: newExtras });
                        }}
                        className="h-8 text-sm dark:bg-slate-800 dark:border-slate-600"
                      />
                    </div>
                    <div>
                      <Label className="text-xs dark:text-slate-400">Retorno</Label>
                      <Input
                        type="time"
                        value={entry.return_time || ''}
                        onChange={(e) => {
                          const newExtras = [...(editingExtraRecord.extra_entries || [])];
                          newExtras[index] = { ...newExtras[index], return_time: e.target.value };
                          setEditingExtraRecord({ ...editingExtraRecord, extra_entries: newExtras });
                        }}
                        className="h-8 text-sm dark:bg-slate-800 dark:border-slate-600"
                      />
                    </div>
                  </div>
                  <div className="mt-2">
                    <Label className="text-xs dark:text-slate-400">Motivo</Label>
                    <Input
                      placeholder="Ex: Banco, DETRAN, Médico..."
                      value={entry.exit_reason || ''}
                      onChange={(e) => {
                        const newExtras = [...(editingExtraRecord.extra_entries || [])];
                        newExtras[index] = { ...newExtras[index], exit_reason: e.target.value };
                        setEditingExtraRecord({ ...editingExtraRecord, extra_entries: newExtras });
                      }}
                      className="h-8 text-sm dark:bg-slate-800 dark:border-slate-600"
                    />
                  </div>
                </div>
              ))}
              
              {/* Botão para adicionar nova batida */}
              <Button
                variant="outline"
                className="w-full border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/30"
                onClick={() => {
                  const newEntry = { exit_time: '', return_time: '', exit_reason: '' };
                  const currentExtras = editingExtraRecord.extra_entries || [];
                  setEditingExtraRecord({ 
                    ...editingExtraRecord, 
                    extra_entries: [...currentExtras, newEntry] 
                  });
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Batida Extra
              </Button>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExtraEntries(false)} className="dark:border-slate-600 dark:text-slate-300">
              Cancelar
            </Button>
            <Button 
              onClick={async () => {
                if (editingExtraRecord) {
                  await handleUpdateRecord(editingExtraRecord.id, { 
                    extra_entries: editingExtraRecord.extra_entries || [] 
                  });
                  setShowExtraEntries(false);
                }
              }} 
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Componente para adicionar reembolso
// Componente para adicionar Reembolso Exceção
const ReembolsoExcecaoForm = ({ colaboradores, terceiros, onSave, onCancel }) => {
  const [funcionarioId, setFuncionarioId] = useState('');
  const [valor, setValor] = useState('');
  const [descricao, setDescricao] = useState('');
  const [categoria, setCategoria] = useState('excecao');
  const [dataVencimento, setDataVencimento] = useState('');
  const [formaPagamento, setFormaPagamento] = useState('pix');
  const [observacoes, setObservacoes] = useState('');
  
  // Combinar colaboradores + terceiros
  const todasPessoas = [
    ...colaboradores.map(c => ({ id: c.id, name: c.name, tipo: 'colaborador' })),
    ...terceiros.map(t => ({ id: t.human_id, name: t.human_name, tipo: 'terceiro' }))
  ].filter((p, i, arr) => arr.findIndex(x => x.id === p.id) === i);
  
  const handleSubmit = () => {
    if (!funcionarioId || !valor || !descricao) {
      toast.error('Preencha funcionário, valor e descrição');
      return;
    }
    const pessoa = todasPessoas.find(p => p.id === funcionarioId);
    onSave(funcionarioId, pessoa?.name || 'N/A', valor, descricao, categoria, dataVencimento || null, formaPagamento, observacoes);
  };
  
  return (
    <div className="space-y-4 pt-2">
      <div>
        <Label className="dark:text-slate-300">Funcionário / Terceiro</Label>
        <Select value={funcionarioId} onValueChange={setFuncionarioId}>
          <SelectTrigger className="mt-1 dark:bg-slate-900 dark:border-slate-600">
            <SelectValue placeholder="Selecione a pessoa" />
          </SelectTrigger>
          <SelectContent className="dark:bg-slate-800 dark:border-slate-700 max-h-64">
            {todasPessoas.map(p => (
              <SelectItem key={p.id} value={p.id} className="dark:text-slate-200">
                {p.name} {p.tipo === 'terceiro' && '(Terceiro)'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label className="dark:text-slate-300">Descrição do Reembolso *</Label>
        <Input
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          placeholder="Ex: Uber para emergência, material comprado, etc."
          className="mt-1 dark:bg-slate-900 dark:border-slate-600"
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="dark:text-slate-300">Valor (R$) *</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            placeholder="0.00"
            className="mt-1 dark:bg-slate-900 dark:border-slate-600"
          />
        </div>
        
        <div>
          <Label className="dark:text-slate-300">Categoria</Label>
          <Select value={categoria} onValueChange={setCategoria}>
            <SelectTrigger className="mt-1 dark:bg-slate-900 dark:border-slate-600">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
              <SelectItem value="excecao" className="dark:text-slate-200">Exceção Geral</SelectItem>
              <SelectItem value="transporte" className="dark:text-slate-200">Transporte</SelectItem>
              <SelectItem value="material" className="dark:text-slate-200">Material</SelectItem>
              <SelectItem value="outros" className="dark:text-slate-200">Outros</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="dark:text-slate-300">Data Vencimento</Label>
          <Input
            type="date"
            value={dataVencimento}
            onChange={(e) => setDataVencimento(e.target.value)}
            className="mt-1 dark:bg-slate-900 dark:border-slate-600"
          />
        </div>
        
        <div>
          <Label className="dark:text-slate-300">Forma de Pagamento</Label>
          <Select value={formaPagamento} onValueChange={setFormaPagamento}>
            <SelectTrigger className="mt-1 dark:bg-slate-900 dark:border-slate-600">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
              <SelectItem value="pix" className="dark:text-slate-200">PIX</SelectItem>
              <SelectItem value="dinheiro" className="dark:text-slate-200">Dinheiro</SelectItem>
              <SelectItem value="deposito" className="dark:text-slate-200">Depósito</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div>
        <Label className="dark:text-slate-300">Observações</Label>
        <Input
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          placeholder="Observações adicionais"
          className="mt-1 dark:bg-slate-900 dark:border-slate-600"
        />
      </div>
      
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} className="dark:border-slate-600">
          Cancelar
        </Button>
        <Button onClick={handleSubmit} className="bg-amber-600 hover:bg-amber-700">
          Adicionar Reembolso
        </Button>
      </div>
    </div>
  );
};

const ReembolsoForm = ({ colaboradores, onSave, onCancel }) => {
  const [funcionarioId, setFuncionarioId] = useState('');
  const [valor, setValor] = useState('');
  const [formaPagamento, setFormaPagamento] = useState('pix');
  const [observacoes, setObservacoes] = useState('');
  
  const handleSubmit = () => {
    if (!funcionarioId || !valor) {
      toast.error('Selecione o funcionário e informe o valor');
      return;
    }
    const funcionario = colaboradores.find(c => c.id === funcionarioId);
    onSave(funcionarioId, funcionario?.name || 'N/A', valor, formaPagamento, observacoes);
  };
  
  return (
    <div className="space-y-4 pt-2">
      <div>
        <Label className="dark:text-slate-300">Funcionário</Label>
        <Select value={funcionarioId} onValueChange={setFuncionarioId}>
          <SelectTrigger className="mt-1 dark:bg-slate-900 dark:border-slate-600">
            <SelectValue placeholder="Selecione o funcionário" />
          </SelectTrigger>
          <SelectContent className="dark:bg-slate-800 dark:border-slate-700 max-h-64">
            {colaboradores.map(c => (
              <SelectItem key={c.id} value={c.id} className="dark:text-slate-200">
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="dark:text-slate-300">Valor (R$)</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            placeholder="0.00"
            className="mt-1 dark:bg-slate-900 dark:border-slate-600"
          />
        </div>
        
        <div>
          <Label className="dark:text-slate-300">Forma de Pagamento</Label>
          <Select value={formaPagamento} onValueChange={setFormaPagamento}>
            <SelectTrigger className="mt-1 dark:bg-slate-900 dark:border-slate-600">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
              <SelectItem value="pix" className="dark:text-slate-200">PIX</SelectItem>
              <SelectItem value="dinheiro" className="dark:text-slate-200">Dinheiro</SelectItem>
              <SelectItem value="deposito" className="dark:text-slate-200">Depósito</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div>
        <Label className="dark:text-slate-300">Observações</Label>
        <Input
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          placeholder="Motivo do reembolso, etc."
          className="mt-1 dark:bg-slate-900 dark:border-slate-600"
        />
      </div>
      
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} className="dark:border-slate-600">
          Cancelar
        </Button>
        <Button onClick={handleSubmit} className="bg-green-600 hover:bg-green-700">
          Adicionar Reembolso
        </Button>
      </div>
    </div>
  );
};

export default DailyControlPage;
